/**
 * English catalog.
 *
 * Typed as `BanDich` (derived from `vi`), so a missing or misspelled key is a
 * `tsc` error rather than a runtime surprise. That type is the reason this file
 * cannot silently drift from the Vietnamese source.
 *
 * Translation rules for this file:
 *
 * - Business and technical terms the repo keeps in English stay as they are:
 *   `tenant`, `slug`, `model`, `workflow`, `schema`, `OCR`, `RPA`, `eKYC`.
 * - Status words keep the same meaning split as Vietnamese, because the colour
 *   tokens are chosen from that meaning (`success` = serving, `warning` =
 *   waiting on a person, `danger` = needs attention).
 * - Sentence case for labels and buttons, not Title Case: antd's own English
 *   strings are sentence case, and mixing the two reads as two products.
 * - Placeholders (`{{ten}}`) keep their names. Renaming one here silently
 *   breaks only this language.
 */
import type { BanDich } from '../types';
import { enChung } from './tro-ly-ai/chung.en.ts';
import { enHoi } from './tro-ly-ai/hoi.en.ts';
import { enKetNoi } from './tro-ly-ai/ket-noi.en.ts';
import { enKho } from './tro-ly-ai/kho.en.ts';
import { enKhuon } from './tro-ly-ai/khuon.en.ts';
import { enBot } from './tro-ly-ai/bot.en.ts';
import { enVanHanh } from './tro-ly-ai/van-hanh.en.ts';
// TODO(ADR-0095): gộp các khối Nhật ký & Truy vết dưới đây vào thẳng tệp này khi xong.
import { enNktvChung } from './nhat-ky-truy-vet/chung.en.ts';
import { enNktvSuDungAi } from './nhat-ky-truy-vet/su-dung-ai.en.ts';
import { enGiaAi } from './gia-ai.en.ts';
import { enCanhBao } from './canh-bao.en.ts';
import { enBanToken } from './ban-token.en.ts';
import { enManTokenAi } from './token-ai.en.ts';
import { enBanGiao } from './ban-giao.en.ts';
// TODO(ADR-0103): gộp các khối Quản lý RPA dưới đây vào thẳng tệp này khi xong.
import { enRpaChung } from './rpa/chung.en.ts';
import { enRpaLoi } from './rpa/loi.en.ts';
import { enRpaKetNoi } from './rpa/ket-noi.en.ts';
import { enRpaWorkflow } from './rpa/workflow.en.ts';
import { enRpaLichSu } from './rpa/lich-su.en.ts';
import { enRpaTongQuan } from './rpa/tong-quan.en.ts';
import { enRpaLicense } from './rpa/license.en.ts';
import { enRpaDanhBa } from './rpa/danh-ba.en.ts';
import { enRpaThongBao } from './rpa/thong-bao.en.ts';
import { enRpaAgent } from './rpa/agent.en.ts';


export const en: BanDich = {
  chung: {
    lamMoi: 'Refresh',
    thuLai: 'Try again',
    huy: 'Cancel',
    luu: 'Save',
    dangLuu: 'Saving...',
    xoa: 'Delete',
    quayLai: 'Go back',
    xemTatCa: 'View all',
    xoaBoLoc: 'Clear filters',
    thaoTac: 'Actions',
    thaoTacKhac: 'More actions',
    thaoTacKhacVoi: 'More actions for {{ten}}',
    stt: '#',
    trangThai: 'Status',
    khongCo: '—',
    moRong: 'Expand',
    thuGon: 'Collapse',
    dong: 'Close',
    taiThem: 'Load more',
  },

  chonQuyen: {
    timPlaceholder: 'Search permissions or groups',
    timNhan: 'Search permissions',
    khongKhop: 'No permissions match your search',
    chonCaNhom: 'Select every permission in {{nhom}}',
    daChonTrongNhom: '{{chon}}/{{tong}} selected',
    boLoc: 'Filter by selection',
    locTatCa: 'All',
    locDaChon: 'Selected',
    locChuaChon: 'Not selected',
    chuaLuu: 'Unsaved: {{them}} added, {{bo}} removed',
    chuaCoThayDoi: 'No changes yet',
    danhSachNhom: 'Module list',
    nhomDaDoi: 'Has unsaved changes',
  },

  loi: {
    mang: 'Cannot reach the server. Check your connection and try again.',
    khongCoQuyen: 'You do not have permission to do this.',
    khongTimThay: 'No data found.',
    saiTaiKhoan: 'Incorrect username or password.',
    chung: 'Something went wrong. Please try again.',
    hetPhien: 'Your session has expired. Sign in again, then retry.',
    mayChu:
      'The server hit an unexpected error. Try again in a few minutes; if it keeps happening, tell the platform operations team.',
    quaNhieuYeuCau: 'Too many requests in a short time. Wait a few seconds and try again.',
    tamKhongPhucVu: 'The service is temporarily unavailable. Try again in a few minutes.',
    dichVuChuaSanSang:
      "The service isn't ready on this server yet. Try again in a few minutes; if it keeps happening, contact the platform operations team.",
    dangXuLy: 'The previous request is still being processed. Wait a few seconds, then reload.',
    saiDiaChiTruyCap: 'This address doesn’t belong to your organisation. Open the portal at the shared platform address.',
    ipBiChan: 'Access from this IP address is blocked. Switch to an allowed network or contact an administrator.',
    ipNgoaiDanhSach:
      "The network you're using isn't on your organisation's list of allowed IP addresses. Switch to an allowed network or contact your organisation administrator.",
    canXacThucTangCuong:
      "You're connecting from outside your organisation's trusted network, so you need to verify with a one-time code.",
  },

  khung: {
    dieuHuongChinh: 'Main navigation',
    veDashboard: 'FOXAI Native — back to dashboard',
    moThanhDieuHuong: 'Expand navigation',
    gapThanhDieuHuong: 'Collapse navigation',
    timManHinh: 'Search screens',
    timManHinhPhimTat: 'Search screens (/)',
    timTrongDieuHuong: 'Search navigation',
    xoaTuKhoa: 'Clear search',
    xoaTuKhoaTimKiem: 'Clear search keyword',
    soKetQua: '{{so}} screens match your search',
    khongCoManHinhKhop: 'No screens match',
    gapHetCacNhom: 'Collapse all groups',
    moHetCacNhom: 'Expand all groups',
    chuaManDangXem: '(contains the current screen)',
    nguoiDung: 'User',
    chuaXacDinh: 'Not set',
    caiDatTaiKhoan: 'My account',
    dangXuat: 'Sign out',
    ngonNgu: 'Language',
    menuTaiKhoan: 'Account and settings',
    menuTaiKhoanCua: "{{ten}}'s account and settings",
    giaoDien: 'Appearance',
    xemGiaoDienTenant: 'View tenant interface',
    veCongQuanTri: 'Back to admin portal',
  },

  tuyChinh: {
    tieuDe: 'Customise appearance',
    nhomCaNhan: 'Personal appearance',
    chiLuuTrenMayNay: 'Saved on this device only',
    khoiPhucMacDinh: 'Restore default',
    datLaiTatCa: 'Reset all',
    daDatLai: 'Personal appearance reset to default.',
    cheDoNenMo: 'Auto follows your operating system.',
    nenTuDong: 'Auto',
    mucDangChon: 'Selected item',
    mucKhac: 'Other item',
    cheDoNen: 'Background mode',
    nenSang: 'Light',
    nenToi: 'Dark',
    mauNhan: 'Accent colour',
    mauNhanMo:
      'Used for primary buttons, links and the selected item. Status colours do not change.',
    boGoc: 'Corner radius',
    boGocMo: 'Applies to buttons, inputs, cards and dialogs.',
    mauNhanh: 'Preview',
    nutChinh: 'Primary',
    nutPhu: 'Secondary',
    nhanMauNhan: 'Accent',
    nhanXong: 'Done',
    nhanCho: 'Pending',
    nhanLoi: 'Error',
    oNhap: 'Input',
    mauTuyY: 'Custom colour',
    mauTuyYNhan: 'Pick a custom accent colour',
    daLamDam: 'Slightly darkened to {{ma}} so text on buttons stays readable.',
  },


  // Organisation identity (the "Organisation" tab of the customise drawer) — ADR-UI-0016.
  hoSoToChuc: {
    tieuDe: 'Organisation identity',
    moTa: 'The logo and name shown to every member of the organisation.',
    apDungToanTo: 'Applies to every member of the organisation.',
    anhApDungNgay: 'Images apply as soon as they are uploaded. Names apply only after you select Save.',
    tenHienThi: 'Display name',
    tenHienThiGoiY: 'Shown in the navigation bar, emails and reports.',
    tenVietTat: 'Short name',
    tenVietTatGoiY: 'Shown when the navigation bar is collapsed and in the tab title. 12 characters at most.',
    logoSang: 'Logo for light background',
    logoSangMo: 'PNG or JPEG, up to 2 MB, longest side 2048 px. A PNG with a transparent background works best.',
    logoToi: 'Logo for dark background',
    logoToiMo: 'Used in dark mode. Leave empty to use the light-background logo.',
    favicon: 'Favicon',
    faviconMo: 'The small icon on the browser tab. Square image, preferably 64 × 64 px or more.',
    macDinh: 'Using the default Native logo',
    taiAnhLen: 'Upload image',
    thayAnh: 'Replace image',
    goAnh: 'Remove image',
    dangTaiAnh: 'Uploading...',
    luu: 'Save changes',
    dangLuu: 'Saving...',
    boThayDoi: 'Discard changes',
    lichSuNut: 'Change history',
    phienBanHienTai: 'Version {{so}}, updated {{luc}}',
    phienBanHienTaiBoi: 'Version {{so}}, updated {{luc}} by {{nguoi}}',
    daLuu: 'Changes saved.',
    daDoiAnh: 'Image updated.',
    daGoAnh: 'Image removed.',
    loiTai: 'Could not load organisation details',
    loiTaiMo: 'Check your connection and try again.',
    xemTruoc: 'Preview',
    chuaLuu: {
      tieuDe: 'Leave without saving?',
      than: 'The name you edited has not been saved and will be lost if you leave this page.',
      boThayDoi: 'Discard changes',
    },
    truong: {
      tenHienThi: 'Display name',
      tenVietTat: 'Short name',
      logoSang: 'Logo for light background',
      logoToi: 'Logo for dark background',
      favicon: 'Favicon',
    },
    loi: {
      tenRong: 'Enter a display name.',
      tenQuaDai: 'The display name can be 100 characters at most. Shorten it and save again.',
      vietTatRong: 'Enter a short name.',
      vietTatQuaDai: 'The short name can be 12 characters at most. Shorten it and save again.',
      tepDinhDang: 'Only PNG or JPEG images are accepted. SVG and WebP are not supported, so export the image as PNG.',
      tepQuaLon: 'The image is larger than 2 MB. Choose a smaller image or compress it, then upload again.',
      phienBanCu: 'Someone else has just changed this. The latest version has been loaded, so review it and save again.',
    },
    lichSu: {
      tieuDe: 'Change history',
      rong: 'No changes yet',
      loiTai: 'Could not load the history',
      phienBan: 'Version {{so}}',
      hienHanh: 'In use',
      khoiPhucTu: 'Restored from version {{so}}',
      boi: 'by {{nguoi}}',
      chu: '{{truong}}: {{truoc}} → {{sau}}',
      trong: '(empty)',
      anhMoi: '{{truong}}: uploaded',
      anhDoi: '{{truong}}: image replaced',
      anhGo: '{{truong}}: removed',
      khoiPhuc: 'Restore',
      xnTieuDe: 'Restore version {{so}}?',
      xnThan:
        'The names and images go back to exactly what version {{so}} had, for every member. History is kept: this creates a new version and can itself be undone.',
      nutKhoiPhuc: 'Restore version {{so}}',
      dangKhoiPhuc: 'Restoring...',
      daKhoiPhuc: 'Version {{so}} restored.',
    },
  },

  mau: {
    indigo: 'Indigo',
    lam: 'Blue',
    'luc-lam': 'Teal',
    luc: 'Green',
    tim: 'Violet',
    hong: 'Magenta',
    cam: 'Burnt orange',
    than: 'Graphite',
  },

  boGoc: {
    vuong: 'Square',
    nhe: 'Slightly rounded',
    vua: 'Moderately rounded',
    'dac-ta': 'As designed',
    'rat-tron': 'Very round',
  },

  hauTo: {
    macDinh: '{{nhan}} (default)',
  },

  chan: {
    khongCoQuyenTieuDe: 'No access',
    khongCoQuyenMo:
      'Your account cannot view this page. Contact an administrator to request access.',
    chuaDungMo: 'This screen belongs to Phase 6 of the UI rebuild and is not implemented yet.',
    khongTimThayTrang: 'Page not found',
    khongTimThayTrangMo:
      'This path does not exist. Check the link, or pick an item from the navigation on the left.',
    saiCongTieuDe: 'This page belongs to another area',
    saiCongMo:
      'Your account does not use this area of the system. Requesting access will not open it — go back to your own area.',
    veManDauTien: 'Go to my home page',
  },

  bang: {
    phanTrang: 'Pagination: {{caption}}',
  },

  nav: {
    nhom: {
      tongQuan: 'OVERVIEW',
      cauHinhHeThong: 'SYSTEM SETTINGS',
      triThucAi: 'AI KNOWLEDGE',
      xuLyTaiLieu: 'DOCUMENT PROCESSING',
      xuLyVanBan: 'TEXT PROCESSING',
      troLiAi: 'AI ASSISTANT',
      thamDinhAi: 'AI APPRAISAL',
      dinhDanhEkyc: 'EKYC IDENTITY',
      baoCaoDoanhNghiep: 'BUSINESS REPORTING',
      quanLyRpa: 'RPA MANAGEMENT',
      module: 'MODULES',
      quanTriNenTang: 'PLATFORM ADMINISTRATION',
    },
    muc: {
      danhSachModule: 'Module list',
      dashboard: 'Dashboard',
      baoCaoThongKe: 'Reports & statistics',
      tokenAi: 'AI tokens',
      thongBao: 'Notifications',

      cauHinhHeThong: 'System settings',
      cauHinhVaiTro: 'Role settings',
      cauHinhNguoiDung: 'User settings',
      coCauToChuc: 'Organisation structure',

      cauHinhXuLyVanBan: 'Text processing settings',
      cauHinhDauVaoVb: 'Text processing inputs',
      danhSachMauVanBan: 'Document templates',
      vaiTroCacCap: 'Levels and roles',

      cauHinhOcr: 'OCR settings',
      thietLapChungTuOcr: 'OCR document setup',

      quanLyTriThuc: 'Knowledge management',
      kiemDuyetPheDuyet: 'Review & approval',
      cauHinhDauVaoTriThuc: 'Knowledge input settings',
      uploadTaiLieu: 'Upload documents',
      ketNoiDuLieuTuDong: 'Automatic data connections',
      ocrChuanHoa: 'OCR & content normalisation',

      nhanDangOcr: 'OCR recognition',
      quanLyTaiLieuOcr: 'OCR document management',
      quanLyChungTu: 'Document management',

      xuLyVanBan: 'Text processing',
      tomTatVanBan: 'Summarise text',
      soanThaoVanBan: 'Draft text',
      phanCongNhiemVu: 'Assign tasks',
      quanLyNhiemVu: 'Task management',

      tongQuan: 'Overview',
      hoiDapAi: 'AI Q&A',
      troChuyenVoiBot: 'Chat with a bot',
      nguonDuLieu: 'Data sources',
      ketNoiNguon: 'Source connections',
      khoDuLieu: 'Data store',
      congCuApi: 'API tools',
      truyVanMau: 'Query templates',
      phanQuyenDuLieu: 'Data permissions',
      cauHinhChatbot: 'Chatbot settings',
      vanHanh: 'Operations',
      nhatKyTruyVet: 'Audit log',
      thuNghiem: 'Testing',

      hoSoThamDinh: 'Appraisal files',
      pheDuyet: 'Approvals',
      thuVienTieuChi: 'Criteria library',
      tichHopApi: 'API integration',

      dinhDanhNguoiDung: 'User identification',
      hoSoDinhDanh: 'Identity records',
      quyTrinhDinhDanh: 'Identity workflows',
      ungDungKhoaApi: 'Applications & API keys',

      phanHe: 'Modules',
      taiChinh: 'Finance',
      duAn: 'Projects',
      kinhDoanh: 'Sales',
      nhanSu: 'People',
      taiLieuBaoCao: 'Report documents',
      nguonBaoCao: 'Data sources',

      dashboardRpa: 'RPA dashboard',
      troLyAgentRpa: 'RPA agent assistant',
      quanLyProjectWorkflow: 'Project & workflow management',
      cauHinhLoiKetNoi: 'Error handling & notifications',
      danhSachProject: 'Projects',
      danhSachLuong: 'Workflows',
      lichSuLuongChay: 'Run history',
      baoCaoLoiSuCo: 'Errors & incidents',
      quanLyLicenseRpa: 'RPA licences',
      quanLyUserOpenCore: 'OpenCore users',

      moduleDaCai: 'Module',

      caiDatBaoMat: 'Security settings',
      khoaApiToChuc: 'API keys',
      diaChiRieng: 'Custom access addresses',
      licenseCucBo: 'Installation license',
    },
  },

  route: {
    cauHinhPhanHe: 'Module settings',
    phanQuyenTaiNguyen: 'Resource permissions',
    hoaDonVatDauVao: 'Incoming VAT invoices',
    quenMatKhau: 'Forgot password',
    datLaiMatKhau: 'Reset password',
    chiTietChatbot: 'Chatbot details',
  },

  module: {
    tenant: { nhan: 'Tenants', mo: 'Organisations using the platform' },
    goiThanhToan: { nhan: 'Plans & billing', mo: 'Service plans, usage plans, model cost prices and top-up requests' },
    quanLyModule: { nhan: 'Module management', mo: 'Submit, check and approve extension modules (plugins)' },
    license: { nhan: 'Licenses', mo: 'On-premise licenses, approval queue, signing keys' },
    hangDoi: { nhan: 'Job queue', mo: 'Background jobs: running, failed' },
    nhatKy: { nhan: 'System log', mo: 'Who did what, when, on which tenant' },
    chinhSachNenTang: {
      nhan: 'Platform policy',
      mo: 'Plan policies, passwords, blocked IPs, notification recipients',
    },
    nguoiDung: { nhan: 'Users', mo: 'Accounts operating the admin portal' },
    vaiTro: { nhan: 'Roles & permissions', mo: 'Module × action grid' },
  khoaApi: { nhan: 'API keys', mo: 'Integration keys for partners' },
    modelAi: { nhan: 'AI models', mo: 'The AI model catalogue shared by every tenant' },
    mucLog: { nhan: 'Log levels', mo: 'Temporary log levels per service, expiring automatically' },
  },

  sapCo: {
    chuaDung: 'This module is not built yet',
    cotWebhookKetQua: 'Last webhook delivery',
    chuaGuiWebhook: 'Not sent yet',
    thuWebhook: 'Test webhook',
    chuaDungMo:
      'Part of task {{dauViec}}. The screen will replace this without any change to navigation or routes.',
    hangDoiMo: 'Background jobs: running, failed, step by step',
    nguoiDungMo: 'Accounts operating the admin portal',
    vaiTroMo: 'Module × action grid',
    khoaApiMo: 'Integration keys issued to partners',
  },

  vaiTro: {
    quanTriNenTang: 'Platform administrator',
    kiemToan: 'Auditor',
    doiTacPhatTrienModule: 'Module development partner',
    quanTriToChuc: 'Organisation administrator',
    phuTrachLicense: 'License officer',
    nguoiDungToChuc: 'Organisation member',
  },
  taiKhoan: {
    tieuDe: 'My account',
    moTa: 'Your profile, password, two-factor authentication and sign-in sessions.',
    canKeycloak:
      'Sample data mode has no real sign-in page to open. Sign in with Keycloak to use this.',
    hoSo: 'Profile',
    hoTen: 'Full name',
    tenDangNhap: 'Sign-in name',
    emailNhanThu: 'Notification email',
    vaiTro: 'Role',
    hoSoGhiChu: 'To change your name or email, contact the administrator of your account.',
    dangNhapVaBaoMat: 'Sign-in and security',
    dangNhapVaBaoMatMo: 'These actions open the sign-in page, then bring you back here.',
    matKhau: 'Password',
    matKhauDatLuc: 'Last set on {{ngay}}.',
    matKhauChuaCo: 'This account has no password yet.',
    doiMatKhau: 'Change password',
    xacThucHaiLop: 'Two-factor authentication',
    dangBat: 'On',
    chuaBat: 'Off',
    toChucBatBuoc: 'Required by organization',
    otpMoDaBat:
      'Each sign-in asks for a 6-digit code from the authenticator app on your phone after your password.',
    otpMoChuaBat:
      'Turn on to be asked for a 6-digit code from an authenticator app on your phone (Google Authenticator, Microsoft Authenticator...) at each sign-in. A leaked password alone will not let anyone in. Once set up, you can create recovery codes for when you lose your phone.',
    ungDungKhongTen: 'Authenticator app',
    caiNgay: 'Set up {{ngay}}',
    goUngDung: 'Remove app',
    caiUngDung: 'Set up authenticator app',
    motUngDung:
      'Each account uses one authenticator app. To switch phones, remove this app, then set it up again on the new phone.',
    motUngDungBatBuoc:
      'Each account uses one authenticator app. To switch phones, remove this app; your organization requires two-factor authentication, so your next sign-in will ask you to set it up again.',
    batBuocSeHoi:
      'Your organization requires two-factor authentication: your next sign-in will ask you to set up an authenticator app.',
    maKhoiPhuc: 'Recovery codes',
    maKhoiPhucDaTao: 'Created',
    maKhoiPhucChuaTao: 'Not created',
    maKhoiPhucMo:
      'Use one when you lose your phone or cannot open your authenticator app: enter a code instead of the 6-digit one. You get 12 codes each time, and each works only once. Keep them somewhere safe, away from your phone. The sign-in page asks for your password again before creating them.',
    maKhoiPhucTaoLuc: 'Created {{ngay}}.',
    maKhoiPhucTaoLaiMo: 'Creating new codes makes the old ones stop working.',
    taoMaKhoiPhuc: 'Create recovery codes',
    taoLaiMaKhoiPhuc: 'Create new recovery codes',
    xacNhanGoTieuDe: 'Remove authenticator app "{{ten}}"?',
    xacNhanGoThan:
      'Codes from this app will stop working; to use it again you must scan a new QR code. Removing the last app means sign-in asks only for your password, and your recovery codes are deleted too; after setting an app up again, create new codes. The sign-in page asks you to confirm once more before removing it.',
    loiXacThuc: 'Could not load your sign-in settings: {{loi}}',
    phienDangNhap: 'Sign-in sessions',
    phienDangNhapMo:
      'Devices signed in with this account. If you see one you do not recognize, sign it out and change your password.',
    kenhDangKetNoi: 'Connecting...',
    kenhTrucTiep: 'Live updates',
    kenhMatKetNoi: 'Disconnected, retrying...',
    phienCaption: 'Sign-in sessions of this account',
    phienRong: 'No sign-in sessions',
    phienNay: 'This session',
    thietBiKhongRo: 'Unknown device',
    cotThietBi: 'Device',
    cotIp: 'IP address',
    cotBatDau: 'Started',
    cotTruyCapCuoi: 'Last active',
    dangXuatPhien: 'Sign out session',
    dangXuatPhienKhac: 'Sign out other sessions ({{so}})',
    xacNhanDangXuatTieuDe: 'Sign out the session on {{ten}}?',
    xacNhanDangXuatThan:
      'That device will have to sign in again. Unsaved work on that device may be lost.',
    xacNhanDangXuatKhacTieuDe: 'Sign out other sessions ({{so}})?',
    xacNhanDangXuatKhacThan:
      'Every other device will have to sign in again; the session you are using stays. Unsaved work on those devices may be lost.',
    daDangXuatPhien: 'Session signed out.',
    daDangXuatPhienKhac: 'Other sessions signed out.',
    loiPhien: 'Could not load sign-in sessions: {{loi}}',
    ketQua: {
      daCaiOtp: 'Authenticator app set up. Your next sign-in will ask for a code from it.',
      daGoOtp: 'Authenticator app removed.',
      daDoiMatKhau: 'Password changed.',
      huyCaiOtp: 'Authenticator app setup cancelled. Nothing changed.',
      huyGoOtp: 'Removal cancelled. Nothing changed.',
      huyDoiMatKhau: 'Password change cancelled. Your old password still works.',
      daTaoMaKhoiPhuc: 'Recovery codes created. Keep them somewhere safe; each code works only once.',
      huyTaoMaKhoiPhuc: 'Recovery code creation cancelled. Nothing changed; your old codes (if any) still work.',
      loi: 'The sign-in page reported an error, so nothing was done. Try again; if it keeps failing, contact your administrator.',
      anThongBao: 'Dismiss',
    },
  },

  nguCanh: {
    dangTai: 'Loading your account permissions...',
    loiTieuDe: "Couldn't load your account permissions",
    khongThuocCongTieuDe: 'This account cannot use this portal',
    khongThuocCongMoTa:
      'This account is not part of the platform staff, so it cannot open the administration portal. Sign in with another account, or contact a platform administrator if you believe this is a mistake.',
    biKhoaTieuDe: 'Your account is locked',
    tamNgungTieuDe: 'Your organisation is suspended',
    tamNgungMoTa:
      "Your organisation's access is suspended, so this account can't use the portal for now. Contact your organisation administrator to find out when access will be restored.",
    tamNgungMoTaQuanTri: 'Your organisation is suspended. Contact your service provider to restore access.',
    ipTieuDe: 'Access from this IP address is blocked',
    ipBiChanMoTa:
      "Your organisation doesn't allow access from the network you're using. Switch to an allowed network (such as your office network or your organisation's VPN) and reload the page, or contact your organisation administrator.",
    ipNgoaiDanhSachMoTa:
      "The network you're using isn't on your organisation's list of allowed IP addresses. Switch to an allowed network (such as your office network or your organisation's VPN) and reload the page, or ask your organisation administrator to add this network to the list.",
    ipBiChanMoTaNenTang:
      "The platform doesn't allow access from the network you're using. Switch to an allowed network and reload the page, or contact a platform administrator.",
    canOtpTieuDe: 'Verify with a one-time code',
    canOtpMoTa:
      "You're connecting from outside your organisation's trusted network, so your organisation requires a one-time code for this session. If you haven't set up an authenticator app yet, you'll be guided through it.",
    canOtpLaiMoTa:
      "You've just verified, but the one-time code still isn't recognised for this session. Try once more; if it keeps happening, connect to the company network or contact your organisation administrator.",
    saiDiaChiTieuDe: 'This address doesn’t belong to your organisation',
    saiDiaChiMoTa:
      '{{host}} is another organisation’s custom address, or it has stopped serving. Open the organisation portal at the shared platform address to keep working.',
    saiDiaChiMoTaKhongLink:
      '{{host}} is another organisation’s custom address, or it has stopped serving. Ask your organisation administrator for the right address.',
    nutVeDiaChiDung: 'Open {{host}}',
    nutXacThucOtp: 'Verify with a one-time code',
    bangTamNgung: 'Your organisation is suspended. Contact your service provider to restore access.',
    bangLuuTru:
      'Your organisation is no longer active and has been archived. Data is permanently deleted 30 days after archiving; contact your service provider to export it or restore access.',
  },

  dangNhap: {
    tieuDe: 'Sign in',
    moTa: 'Sign in to use the FOXAI Native portal.',
    bangTaiKhoanFoxai: 'Sign in with a FOXAI account',
    chuyenSangTrangChung: 'You will be taken to the shared sign-in page',
    foxaiId: 'FoxAI ID',
    roiQuayLaiDay: ', then brought back here.',
    hoac: 'or',
    tenDangNhap: 'Username / email',
    tenDangNhapGoiY: 'admin, vanhanh, tenant or any name',
    matKhau: 'Password',
    ghiNho: 'Keep me signed in',
    quenMatKhau: 'Forgot your password?',
    dangDangNhap: 'Signing in...',
    dangMoTrangChung: 'Opening the FoxAI ID sign-in page...',
    khongMoDuocTrangDangNhap: 'The sign-in page could not be opened. Please try again in a few minutes.',
    banQuyen: '© {{nam}} FOXAI. All rights reserved.',
    sso: {
      thieu_ma: 'The sign-in service returned an incomplete response. Please try again.',
      state_khong_khop: 'The sign-in session does not match or has expired. Please try again.',
      doi_ma_that_bai: 'Sign-in could not be completed. Please try again.',
      phien_qua_lon: 'The sign-in session exceeded the allowed size. Please tell an administrator.',
      access_denied: 'You cancelled the sign-in.',
      chua_lien_ket:
        'Your account is not linked to single sign-on yet. Contact an administrator, or sign in with a password.',
      khong_tra_duoc_quyen:
        'Your account permissions could not be determined right now. Please try again in a few minutes.',
      cho_duyet:
        'Your account has been created and is waiting for an administrator to grant permissions. You will be able to sign in as soon as it is approved.',
      trung_email:
        'Your account already exists but is not linked to single sign-on. Contact an administrator to link it.',
      trung_ten_dang_nhap:
        'Your username clashes with another account on the system. Please contact an administrator.',
      bi_khoa: 'Your account is disabled or locked. Please contact an administrator.',
      token_khong_hop_le: 'The sign-in session is not valid. Please try again.',
      chua_kiem_duoc: 'The system did not respond in time. Please try again in a few minutes.',
      phien_ket_thuc:
        'Your session has ended, possibly because you signed out on another device. Sign in again to continue.',
      macDinh: 'Single sign-on failed ({{ma}}).',
    },
  },

  dashboard: {
    tieuDe: 'Overview dashboard',
    aiDangHoatDong: 'AI running',
    tongChungTu: 'Total documents',
    soMauOcr: '{{so}} OCR templates',
    boTriThuc: 'Knowledge bases',
    tepTaiLieu: '{{so}} files',
    chatbotHoatDong: 'Active chatbots',
    botDaCauHinh: '{{so}} bots configured',
    nguoiDungHoatDong: 'Active users',
    tongTaiKhoan: '{{so}} accounts in total',
    loiSoLieu: 'Could not load overview figures: {{loi}}',
    hoatDongGanDay: 'Recent activity',
    chuaCoHoatDong: 'No activity yet.',
    hdOcrHoanTat: 'OCR finished {{so}} documents in the system',
    hdKhoTriThuc: 'The knowledge store holds {{so}} files',
    hdChatbot: '{{so}} AI chatbots are running',
    hdNguoiDung: '{{dangHoatDong}}/{{tong}} users are active',
    hdDaXacNhan: '{{so}} documents have been confirmed',
    hdDaChuyenKho: '{{so}} documents moved into the knowledge store',
    vuaXong: 'Just now',
    phutTruoc: '{{so}} minutes ago',
    gioTruoc: '{{so}} hours ago',
    chungTuTheoTrangThai: 'Documents by status',
    chuaCoChungTu: 'No documents yet.',
    ctNhap: 'Imported',
    ctDaXacNhan: 'Confirmed',
    ctDaChuyenKho: 'Moved to knowledge store',
    xuLyTaiLieu: 'Document processing',
    mauOcr: 'OCR templates',
    chungTuNhap: 'Imported documents',
    daXacNhan: 'Confirmed',
    daVaoKho: 'In the store',
    triThucTheoPhongBan: 'Knowledge bases by department',
    chuaCoDuLieu: 'No data yet.',
    phongBanKhac: 'Other',
    trangThaiChatbot: 'AI chatbot status',
    cauHinh: 'Settings',
    chuaCauHinhChatbot: 'No chatbots configured yet.',
    botTat: 'Off',
    botChuaSanSang: 'Not ready',
    botHoatDong: 'Running',
    cauHinhHeThong: 'System settings',
    tongNguoiDung: 'Total users',
    dangHoatDong: 'Active',
    soVaiTro: 'Roles',
    soPhongBan: 'Departments',
    quanLy: 'Manage',
    khoTriThuc: 'AI knowledge store',
    chuaCoBoTriThuc: 'No knowledge bases yet.',
    soTep: '{{so}} files',
    pheDuyetTriThuc: 'Knowledge approval',
    pheDuyetMo: 'The knowledge review and approval system is ready for new submissions.',
    sanSang: 'Ready',
    mo: 'Open',
  },

  tenant: {
    danhSachTieuDe: 'Tenants',
    danhSachMoTa:
      'Organisations using the platform: lifecycle status, usage plan and deployment model',
    taoTenant: 'Create tenant',
    bangCaption: 'Tenants on the platform',
    tkTongSo: 'Total tenants',
    tkTongSoSub: 'excluding deleted tenants',
    tkDangPhucVu: 'Serving users',
    tkDangPhucVuSub: 'trial, active or in grace period',
    tkChoTrienKhai: 'Awaiting deployment',
    tkChoTrienKhaiSub: 'draft or being provisioned',
    tkTamNgung: 'Suspended',
    tkTamNgungSub: 'users currently blocked',
    timTheo: 'Search by name, slug or tax code...',
    tatCaTrangThai: 'All statuses',
    tatCaGoi: 'All usage plans',
    tatCaCachTrienKhai: 'All deployment models',
    nhanGoiDichVu: 'Usage plan',
    nhanCachTrienKhai: 'Deployment model',
    cotTenGoi: 'Name',
    cotGoi: 'Usage plan',
    cotTrienKhai: 'Deployment',
    cotVung: 'Region',
    cotTaoLuc: 'Created',
    tongSoTenant: '{{so}} tenants in total',
    xemChiTiet: 'View details',
    suaHoSo: 'Edit profile',
    luuHoSo: 'Save profile',
    duyet: 'Approve and provision',
    tamNgung: 'Suspend',
    tamNgungMo: "Blocks the tenant's users from signing in",
    khoiPhuc: 'Resume',
    loiDanhSach: 'Could not load the tenant list: {{loi}}',
    rongLoc: 'No tenants match the current filters',
    rongThat: 'No tenants on the platform yet',
    rongThatMo: 'Create the first tenant to start running the platform.',
    loiChiTiet: 'Could not load tenant details: {{loi}}',
    khongTimThay: 'Tenant not found',
    khongTimThayMo:
      'There is no tenant with ID "{{ma}}". Check the address, or open the tenant from the list.',
    veDanhSach: 'Back to tenants',
    bannerTamNgung:
      "Suspended since {{luc}}. All of the tenant's users, including organisation admins, are blocked.",
    bannerDangTrienKhai:
      'The onboarding workflow is running. The profile cannot be edited until deployment finishes; progress updates itself on the Deployment progress tab.',
    bannerTrienKhaiLoi:
      'The last deployment failed, so the tenant went back to draft. See the failed step on the Deployment progress tab.',
    bannerLuuTru:
      'An archived tenant can only export its data, and is permanently deleted 30 days after archiving.',
    bannerDaXoa: "Deleted at {{luc}}. This tenant's slug and domains cannot be reused.",
    tabTongQuan: 'Overview',
    tabDomainVaDiaChi: 'Domains',
    tabDomain: 'Email domains',
    tabTienTrinh: 'Deployment progress',
    tabBaoMat: 'Security',
    tabLienHe: 'Contacts',
    tabNguoiDung: 'Users',
    nguoiDung: {
      tieuDe: 'Tenant users',
      tim: 'Search by name',
      caption: 'Tenant users',
      rong: 'This tenant has no users yet',
      rongLoc: 'No users match this name',
      loi: 'Couldn’t load users: {{loi}}',
    },
    hoSo: 'Profile',
    dongTenHienThi: 'Display name',
    dongTenPhapLy: 'Legal name',
    dongMaSoThue: 'Tax code',
    dongSlug: 'Slug',
    dongTrangThai: 'Status',
    dongGoiDichVu: 'Usage plan',
    dongCachTrienKhai: 'Deployment model',
    dongTang: 'Tier',
    dongVung: 'Region',
    dongTaoLuc: 'Created',
    dongCapNhatLuc: 'Updated',
    haTang: 'Infrastructure',
    haTangOnPremise:
      'An on-premises tenant runs its own infrastructure. The platform only manages its profile, contract and licence, and creates no database, Keycloak group or domain for it.',
    dongCumDb: 'Database cluster',
    dongTenDb: 'Core database',
    dongPhienBanSchema: 'Schema version',
    dongGroupKeycloak: 'Keycloak group',
    nguoiLienHe: 'Admin contacts',
    nguoiLienHeCaption: "The tenant's admin contacts",
    nguoiLienHeRong: 'No admin contacts yet',
    cotHoTen: 'Full name',
    cotEmail: 'Email',
    cotDienThoai: 'Phone',
    nhanChinh: 'Primary',
    hanMuc: 'Resource limits',
    hanMucCaption: "Resource limits of the tenant's base tier",
    hanMucRong: 'This quota plan has no resource limits configured',
    cotTaiNguyen: 'Resource',
    cotGioiHan: 'Limit',
    cotDaDung: 'Used',
    chuaDo: 'Not measured',
    domainCaption: "The tenant's email domains",
    domainRong: 'No email domains yet',
    cotDomain: 'Domain',
    cotXacMinh: 'Verification',
    cotCachXacMinh: 'Verified by',
    cotThemLuc: 'Added',
    daXacMinh: 'Verified',
    chuaXacMinh: 'Not verified',
    // DNS record and manual verification (ADR-0054).
    nguoiDuyet: 'Approved by: {{nguoi}}',
    nguoiDuyetLaBan: 'you',
    lyDoDuyet: 'Reason: {{lyDo}}',
    tdThem: 'Add domain',
    tdDangThem: 'Adding...',
    tdRongMo: "Add the customer's email domain, then send them the DNS record to verify it.",
    tdRongTuyChon: 'The organisation uses personal email, so no domain is needed. Add one if the organisation has work email.',
    tdTieuDe: 'Add email domain to tenant',
    tdThan:
      'Once added, the DNS record to create appears below so you can send it to the customer. Users can sign in with this domain only after it is verified.',
    tdDomain: 'Domain',
    tdDomainGoiY: 'Only the part after @ in the email, without http:// or www.',
    tdDomainViDu: 'branch.company.com',
    tdDomainTrong: 'Enter the domain to add.',
    tdDomainSai: 'This is not a valid domain, for example company.com. Enter accented domains in punycode (xn--...).',
    tdDomainDai: 'The domain is longer than {{so}} characters.',
    tdDaThem: 'Added domain {{domain}}. Send the DNS record below to the customer to verify it.',
    dnsKhoiNhan: 'DNS record for {{domain}}',
    dnsTieuDe: 'DNS record to create for {{domain}}',
    dnsMo: "Send this record to the customer's DNS manager. The system checks every 5 minutes; DNS changes can take a few hours to propagate.",
    dnsLoai: 'Type',
    dnsTen: 'Name',
    dnsGiaTri: 'Value',
    dnsSaoChep: 'Copy',
    dnsDaSaoChep: 'Copied',
    dnsKhongSaoChepDuoc: "The browser doesn't allow copying here. Select the value in the box and copy it with the keyboard.",
    dnsSaoChepNhan: 'Copy the DNS record {{truong}}',
    dnsChuaCoBanGhi: 'The server has not returned a DNS record for this domain. Reload the page to get it.',
    dnsKiemNgay: 'Check now',
    dnsDangKiem: 'Checking...',
    dnsDaKhop: 'Domain {{domain}} is verified by DNS TXT.',
    dnsChuaKhop: 'No matching TXT record found for {{domain}} yet. DNS changes can take a few hours; the system keeps checking every 5 minutes.',
    domainDaXacMinhTruoc: 'Domain {{domain}} was already verified.',
    kiemDnsLoi: "Couldn't check DNS: {{loi}}",
    // Remove an email domain (GG-FX-39): Platform Admin only.
    gdMo: 'Remove domain',
    gdTieuDe: 'Remove domain {{domain}}?',
    gdThan: 'Users with @{{domain}} email are no longer routed by this domain. This cannot be undone; to use it again you must add and verify it again.',
    gdThanChinhMoi: '{{domain}} is the primary domain; {{moi}} will become primary.',
    gdThanChinhHet: '{{domain}} is the last domain; the tenant will have no primary domain.',
    gdDangGo: 'Removing...',
    gdDaGo: 'Removed domain {{domain}}.',
    xmMo: 'Verify manually',
    xmTieuDe: 'Verify domain {{domain}} manually?',
    xmThan: 'Once verified, everyone signing in with an @{{domain}} email goes to this tenant, and a tenant waiting at the domain verification step continues provisioning.',
    xmCanhBao: 'Only verify after confirming through another channel, such as a contract or an email sent from the domain itself, that the customer owns it. This action is logged with the reason and cannot be undone from the interface.',
    xmLyDo: 'Reason for verification',
    xmLyDoGoiY: 'State the evidence; the reason appears in the domain table and the audit log.',
    xmLyDoViDu: 'For example: Confirmed by contract no. 12/2026',
    xmLyDoTrong: 'Enter a reason, for example the contract number you checked.',
    xmLyDoDai: 'The reason can be at most {{so}} characters.',
    xmNut: 'Verify {{domain}}',
    daXacMinhThuCong: 'Domain {{domain}} verified.',
    workflowOnboarding: 'Onboarding workflow',
    tuCapNhat: 'Updates every 5 seconds',
    chuaTrienKhai: 'This tenant has never been deployed.',
    soBuoc: '{{xong}}/{{tong}} steps done',
    capNhatLuc: 'Updated {{luc}}',
    daThuLai: 'retried {{so}} times',
    dialogTao: 'Create tenant',
    dangTao: 'Creating...',
    nhomHoSo: 'Organisation profile',
    nhomGoi: 'Plans',
    nhomTrienKhai: 'Deployment',
    nhomDomain: 'Email domains',
    fEmailCaNhan: 'Organisation uses personal email (no domain of its own)',
    fEmailCaNhanMo: 'Two-factor authentication becomes mandatory for everyone.',
    nhomLienHe: 'Admin contacts',
    fTenHienThi: 'Display name',
    fTenHienThiGoiY: 'For example: FoxAI',
    fTenHienThiTrong: 'Enter a display name.',
    fSlug: 'Slug',
    fSlugMo: 'Locked once the profile is approved.',
    fSlugTrong: 'Enter a slug, or type a display name to get a suggestion.',
    fSlugNgan: 'The slug needs at least {{so}} characters.',
    fSlugDai: 'The slug can be at most {{so}} characters.',
    fSlugDinhDang:
      'Use only unaccented lowercase letters, digits and hyphens; start with a letter, with no double hyphens and no hyphen at the end.',
    fSlugDaKhoa: 'The slug has been locked since the profile was approved.',
    fTenPhapLy: 'Legal name',
    fTenPhapLyGoiY: 'Name on the business registration certificate',
    fMaSoThue: 'Tax code',
    fQuaDai: 'At most {{so}} characters.',
    fKhongXoaDuoc:
      'A field that already has a value cannot be cleared. Enter a new value, or keep the current one.',
    fCachTrienKhai: 'Deployment model',
    fTang: 'Tier',
    fDomainSo: 'Domain {{so}}',
    fDomainGoiY: 'company.com',
    fDomainDinhDang:
      'Not a valid domain, for example company.com. Enter internationalised domains in punycode (xn--…).',
    fDomainDai: 'A domain can be at most {{so}} characters.',
    fDomainLap: 'This domain is already in a row above.',
    themDomain: 'Add domain',
    boDomain: 'Remove domain {{so}}',
    lienHeChinh: 'Primary contact',
    lienHeSo: 'Contact {{so}}',
    fHoTen: 'Full name',
    fHoTenTrong: 'Enter a full name.',
    fEmail: 'Email',
    fEmailTrong: 'Enter an email.',
    fEmailSai: 'That email does not look right, for example an.nguyen@company.com.',
    fEmailNhanThu: 'Notification email (if different)',
    fDienThoai: 'Phone',
    themLienHe: 'Add contact',
    boLienHe: 'Remove contact {{so}}',
    dialogSua: 'Edit {{ten}}',
    xnDuyetTieuDe: 'Approve and provision tenant {{ten}}?',
    xnDuyetThanSaas:
      'Slug {{slug}} is locked for good once approved, because it is part of the database name and the Keycloak group path. The onboarding workflow runs from creating the database, running migrations and creating the group and first Tenant Admin, through to the tenant reaching Trial.',
    xnDuyetThanOnPremise:
      'On-premise tenants run no onboarding: the profile moves to Active and the platform creates no database, Keycloak group or domain. The slug is locked for good once approved.',
    xnDuyetChoXacMinh:
      'Domain {{domain}} is not verified, so the workflow will stop at the Keycloak group step and wait for verification, for up to 14 days. Every step before that still runs right away.',
    xnDuyetNut: 'Approve and provision',
    canTroTieuDe: 'The profile is not ready to provision:',
    canTroDomain: 'No email domain yet, and onboarding needs a primary domain for the Tenant Admin to sign in.',
    canTroLienHe:
      'No admin contact yet, and onboarding creates the first Tenant Admin from the primary contact.',
    canTroEmail:
      'The primary contact email is outside the primary domain {{domain}}, so the first Tenant Admin would not be able to sign in.',
    xnTamNgungTieuDe: 'Suspend tenant {{ten}}?',
    xnTamNgungThan:
      "All of the tenant's users, including organisation admins, will be blocked. Data is kept, and the tenant can be resumed at any time.",
    xnTamNgungNut: 'Suspend tenant',
    xnKhoiPhucTieuDe: 'Resume tenant {{ten}}?',
    xnKhoiPhucThanSaas:
      "The tenant's users can sign in again straight away. The tenant becomes Active if it has a current subscription, otherwise Trial.",
    xnKhoiPhucThanOnPremise: 'The tenant becomes Active.',
    xnKhoiPhucNut: 'Resume tenant',
    daTao: 'Tenant {{ten}} created.',
    daLuu: 'Profile of {{ten}} saved.',
    daDuyetSaas: 'Tenant {{ten}} approved. The onboarding workflow is running; follow it on the Provisioning tab.',
    daDuyetOnPremise: 'Tenant {{ten}} approved and now Active.',
    daTamNgung: 'Tenant {{ten}} suspended.',
    daKhoiPhuc: 'Tenant {{ten}} resumed, now {{trangThai}}.',
    trangThai: {
      DRAFT: 'Draft',
      PROVISIONING: 'Provisioning',
      TRIAL: 'Trial',
      ACTIVE: 'Active',
      GRACE_PERIOD: 'Grace period',
      SUSPENDED: 'Suspended',
      ARCHIVED: 'Archived',
      DELETED: 'Deleted',
    },
    goi: {
      TRIAL: 'Trial',
      STANDARD: 'Standard',
      PROFESSIONAL: 'Professional',
      ENTERPRISE: 'Enterprise',
    },
    cachTrienKhai: {
      SAAS: 'SaaS',
      ON_PREMISE: 'On-premises',
    },
    tang: {
      POOL: 'Pool',
      DEDICATED: 'Dedicated',
      ISOLATED: 'Isolated',
    },
    trangThaiBuoc: {
      PENDING: 'Pending',
      RUNNING: 'Running',
      SUCCEEDED: 'Succeeded',
      FAILED: 'Failed',
    },
    cachXacMinh: {
      DNS_TXT: 'DNS TXT',
      ADMIN_APPROVAL: 'Platform admin approval',
    },
    taiNguyen: {
      MAX_USERS: 'Users',
      DB_BYTES: 'Database storage',
      S3_BYTES: 'File storage',
      NOTIFY_MESSAGES_MONTH: 'Notifications per month',
    },
    taiNguyenLa: 'Other resource',
    fGoiTaiNguyen: 'Usage plan',
    fGoiTaiNguyenMo: '{{so}} AI tokens a month · base tier {{goi}}',
    goiHanMucTieuDe: 'Usage plan',
    doiGoiHanMuc: 'Change usage plan',
    hopDoiGoiHanMuc: 'Change usage plan for {{ten}}',
    dongGoi: 'Plan',
    dongTokenThang: 'AI tokens per month',
    dongGoiNen: 'Base tier',
    doiGoiNenCanhBao:
      'The tenant’s base tier changes from {{cu}} to {{moi}}: security policy and licence by base tier change with it.',
  },

  // ───────────────────────── Branded domains (ADR-0097) ─────────────────────────
  diaChiRieng: {
    tieuDe: 'Custom access addresses',
    caption: 'Custom access addresses of the tenant',
    them: 'Add address',
    dangThem: 'Adding...',
    daDu: 'All {{toiDa}} addresses are in use. Remove one to add a new address.',
    rong: 'No custom access address yet',
    rongMoNenTang:
      "Add one of the customer's domains, for example app.congtyabc.com.vn, so the tenant's users can open the portal on it.",
    rongMoToChuc:
      "Add your organisation's domain, for example app.congtyabc.com.vn, so users open the portal on it instead of the shared platform address.",
    loiTai: 'Couldn’t load custom access addresses: {{loi}}',
    cotHost: 'Address',
    cotTrangThai: 'Status',
    cotDns: 'DNS check',
    cotChungChi: 'TLS certificate',
    nhanGoc: 'Apex domain',
    khoaNenTang: 'Locked: the organisation can’t reactivate or remove it',
    khoaToChuc: 'Locked by the platform administrator. Contact them to reopen or remove it.',
    chiQuanTriToChuc: 'Only organisation administrators can add and manage custom access addresses.',
    trangThai: {
      CHO_DNS: 'Awaiting DNS',
      DA_XAC_MINH: 'Verified',
      DANG_DUNG: 'In use',
      TAM_NGUNG: 'Suspended',
      DA_GO: 'Removed',
    },
    dnsChuaKiem: 'Not checked',
    dnsDatNgan: 'Passed',
    dnsChuaDatNgan: 'Not passed',
    chungChiHan: 'Valid until {{ngay}}',
    chungChiSapHet: 'Expires soon: {{ngay}}',
    chungChiChoCap: 'No information yet',
    loiDns: {
      TXT_MISSING: 'No TXT record found yet. Create the TXT record below; DNS can take a few hours to update.',
      TXT_MISMATCH: 'The TXT record has a different value. Change it to match the verification code below.',
      TARGET_MISMATCH:
        'The address doesn’t point to the platform yet. Fix the CNAME record (or the A record for an apex domain) as shown below.',
      DNS_UNAVAILABLE: 'The DNS server didn’t answer. The system will check again in a few minutes.',
    },
    dnsKhoiNhan: 'DNS records to create for {{host}}',
    dnsTieuDe: 'DNS records for {{host}}',
    dnsMo: 'Create the records below where the domain’s DNS is managed. The system checks periodically; once they pass, activate the address.',
    dnsMoGoc:
      'Create the TXT and A records below where the domain’s DNS is managed. The system checks periodically; once they pass, activate the address.',
    dnsGhiChuAlias:
      'If your DNS provider supports ALIAS, ANAME or CNAME flattening (for example Cloudflare), you can point the domain to {{dich}} instead of the A record; that way nothing needs updating if the platform IP changes.',
    banGhiLoai: '{{loai}} record',
    kiemLai: 'Check DNS again',
    xemBanGhiDns: 'Show DNS records',
    anBanGhiDns: 'Hide DNS records',
    dnsThieuIp:
      'The server has no public IP configured for root domains, so there is no A record yet. Operations must set CUSTOM_HOST_TARGET_IPS in the server .env; meanwhile, use ALIAS/ANAME if the DNS provider supports it.',
    dangKiem: 'Checking...',
    kiemLoi: 'Couldn’t check DNS: {{loi}}',
    dnsDaDat: 'DNS for {{host}} passed. The address can be activated.',
    dnsChuaDat: 'DNS for {{host}} hasn’t passed yet. See the reason in the DNS instructions.',
    dnsVanDat: 'Checked DNS for {{host}} again.',
    kichHoat: 'Activate',
    tamNgung: 'Suspend',
    khoa: 'Lock address',
    go: 'Remove address',
    daThem: 'Added {{host}}. The DNS records to create are at the bottom of the panel.',
    daKichHoat: 'Activated {{host}}.',
    daTamNgung: 'Suspended {{host}}.',
    daKhoa: 'Locked {{host}}.',
    daGo: 'Removed {{host}}.',
    xnKichHoatTieuDe: 'Activate {{host}}?',
    xnKichHoatThan: 'Users can open the organisation portal at https://{{host}}. You can suspend it at any time.',
    xnKichHoatNut: 'Activate address',
    xnTamNgungTieuDe: 'Suspend {{host}}?',
    xnTamNgungThan:
      'Within 60 seconds this address stops serving; current users must use the shared platform address. You can activate it again at any time.',
    xnTamNgungThanNenTang:
      'Within 60 seconds this address stops serving; current users must use the shared platform address. The organisation can’t reactivate or remove it; only a platform administrator can reopen it.',
    xnTamNgungNut: 'Suspend address',
    xnKhoaTieuDe: 'Lock {{host}}?',
    xnKhoaThan:
      'The organisation has already suspended this address. Once locked, the organisation can’t reactivate or remove it; only a platform administrator can reopen it.',
    xnKhoaNut: 'Lock address',
    xnGoTieuDe: 'Remove {{host}}?',
    xnGoThan:
      'The address stops serving immediately. For the next 30 days only this organisation can add it again. This can’t be undone.',
    xnGoNut: 'Remove address',
    hopTieuDe: 'Add custom access address',
    hopThan:
      "Enter the customer's domain. After adding it, the customer creates the DNS records shown; once DNS passes, activate it.",
    hopThanToChuc:
      "Enter your organisation's domain. After adding it, create the DNS records shown where the domain is managed; once DNS passes, activate it.",
    oHostname: 'Address',
    oHostnameGoiY: 'Either a root domain (congtyabc.com.vn) or a subdomain (app.congtyabc.com.vn) works.',
    oHostnameViDu: 'app.congtyabc.com.vn',
    seLuuLa: 'Will be saved as',
    goiYWww:
      'www.{{host}} is a separate address: to let users in through both, add www.{{host}} as well. Each tenant can have up to {{toiDa}} addresses.',
    loiTrong: 'Enter an address, for example app.congtyabc.com.vn.',
    loiDai: 'The address is longer than {{so}} characters.',
    loiIp: 'IP addresses aren’t allowed. Enter a domain name, for example app.congtyabc.com.vn.',
    loiSai: 'Use only letters, digits, hyphens and dots, for example app.congtyabc.com.vn.',
    loiThieuCham: 'Enter a full domain name, for example app.congtyabc.com.vn.',
  },

  hanMucToken: {
    tenTaiNguyen: 'AI tokens per month',
    donVi: 'tokens',
    soToken: '{{so}} tokens',
    trenHanMuc: '/ {{so}} tokens',
    khongGioiHan: 'Unlimited',
    khongGioiHanDong: 'Used this month · unlimited · resets to 0 on {{ngay}}',
    thanhNhan: 'AI tokens used this month',
    thanhGiaTri: '{{daDung}} of {{hanMuc}} tokens',
    conLai: '{{so}} tokens left',
    veKhong: 'Resets to 0 on {{ngay}}',
    trangThai: {
      canhBao: 'Running low',
      daChan: 'Blocked',
    },
    tieuDe: 'AI tokens this month',
    dangTai: 'Loading AI token quota...',
    loi: 'Couldn’t load the AI token quota. {{loi}}',
    chanNenTang:
      'This tenant has used up this month’s quota: new AI calls are rejected until {{ngay}}. Add tokens or set a higher tenant-specific quota to unblock it now.',
    datRieng: 'Set a tenant-specific quota',
    suaRieng: 'Edit tenant-specific quota',
    luuRieng: 'Save tenant-specific quota',
    boRieng: 'Remove tenant-specific quota',
    daDat: 'Saved the tenant-specific AI token quota for {{ten}}.',
    daBo: 'Removed the tenant-specific quota; {{ten}} is back on its usage plan quota.',
    hopDatTieuDe: 'Set a tenant-specific AI token quota for {{ten}}',
    hopSuaTieuDe: 'Edit the tenant-specific AI token quota for {{ten}}',
    hopThan:
      'A tenant-specific quota replaces the usage plan quota for this tenant only and applies to the current month right away. Extra tokens are still added on top.',
    hopHanMucGoi: 'Quota of usage plan {{nguon}}',
    hopDaDung: 'Used this month',
    hopSeChan:
      'This quota is not above what the tenant has used this month ({{so}} tokens): its AI calls are blocked right away, until {{ngay}}.',
    hopBoThanKhongGioiHan:
      'The tenant goes back to the quota of usage plan {{nguon}}: unlimited, effective for this month right away.',
    hopBoTieuDe: 'Remove the tenant-specific quota for {{ten}}?',
    hopBoThan:
      'The tenant goes back to the quota of usage plan {{nguon}} ({{hanMuc}} per month), effective for this month right away. Extra tokens stay.',
    hopBoSeChan:
      'The tenant has used {{so}} tokens this month, reaching the plan quota: its AI calls are blocked right away, until {{ngay}}.',
    oNhan: 'Tokens per month',
    oGoiY: 'Enter 0 to block every AI call from this tenant.',
    oXemTruoc: 'Quota: {{so}} tokens per month',
    oTrong: 'Enter the number of tokens per month.',
    oSai: 'Enter a whole number of 0 or more, for example 30000000.',
    oQuaLon: 'At most {{so}} tokens per month.',
    goiTieuDe: 'AI tokens for the {{goi}} tier',
    goiDangTai: 'Loading AI token quotas by plan...',
    goiLoi: 'Couldn’t load AI token quotas by plan. {{loi}}',
    goiDonViThang: 'tokens per month',
    toChucTieuDe: 'AI tokens this month',
    toChucCanhBao:
      'Your organization has used {{so}}% of this month’s AI token quota. Once it runs out, AI features pause until {{ngay}}; send a request on the Top up tab if you need more.',
    toChucDaChanTieuDe: 'This month’s AI token quota is used up',
    toChucDaChan:
      'AI features are paused until {{ngay}}, or until the platform administrator grants more tokens — send a request on the Top up tab. Everything else keeps working as usual.',
    goiChiTro: 'From the default usage plan {{ten}}.',
    goiMoManGoiTaiNguyen: 'Open Usage plans',
  },

  tokenAi: {
    loai: {
      NAP_THEM: 'Top-up',
      GOI_TAM_THOI: 'Temporary package',
      GIA_HAN: 'Extension',
    },
    loaiMo: {
      NAP_THEM: 'Tokens used gradually over 1, 3, 6 or 12 months; unused tokens carry over to the next month.',
      GOI_TAM_THOI: 'A lump sum of tokens usable at any time during 1–4 weeks or 1–12 months.',
      GIA_HAN: 'Move the end date of a running or scheduled temporary package; adds no tokens.',
    },
    trangThaiCapThem: {
      ACTIVE: 'Active',
      SCHEDULED: 'Scheduled',
      EXPIRED: 'Expired',
      REVOKED: 'Revoked',
    },
    trangThaiYeuCau: {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
    },
    hmSo: 'Allowance: {{so}} tokens/month',
    hmKhongGioiHan: 'Allowance: unlimited',
    hmRiengTenant: 'set for this tenant only',
    hmRiengToChuc: 'set for your organization only',
    hmGoiChuaDat: 'usage plan {{ten}} is unlimited',
    hmCapThemMot: '+ {{so}} extra tokens left (until {{ngay}})',
    hmCapThemNhieu: '+ {{so}} extra tokens left in {{lan}} buckets (the first ends on {{ngay}})',
    ctTieuDe: 'How the quota is worked out',
    ctRiengTenantMo: 'Replaces the usage plan quota, for this tenant only.',
    capThemTieuDe: 'Extra tokens',
    capThemCaption: 'Extra AI tokens for {{ten}}',
    capThemRong: 'No extra tokens yet',
    capThemRongMo: 'When a tenant runs out mid-month, add a top-up or a temporary package to unblock it right away.',
    loiCapThem: 'Couldn’t load extra tokens. {{loi}}',
    napThem: 'Add tokens',
    themGoi: 'Add temporary package',
    giaHanGoi: 'Extend package',
    thuHoi: 'Revoke tokens',
    tenDong: '{{so}} extra tokens',
    cotLoai: 'Type',
    cotToken: 'Tokens',
    cotHieuLuc: 'Valid',
    cotGhiChu: 'Note',
    khoangNgay: '{{tu}} – {{den}}',
    tuYeuCau: 'From the organization’s request',
    capTrucTiep: 'Granted directly',
    dangChoCuaTenant: 'This organization has {{so}} top-up requests waiting for review.',
    moHangCho: 'Open queue',
    daNapThem: 'Added {{so}} tokens for {{ten}}.',
    daThemGoi: 'Added a temporary package of {{so}} tokens per month for {{ten}}.',
    daGiaHan: 'Temporary package extended.',
    daThuHoi: 'Extra tokens revoked.',
    xacNhanThuHoi: 'Revoke {{so}} extra tokens?',
    xacNhanThuHoiMo:
      '{{ten}}’s quota drops by {{so}} tokens right away. If usage reaches the new quota, AI calls are blocked again within seconds. This can’t be undone; to grant again, create a new entry.',
    xacNhanThuHoiChuaToi:
      'This package hasn’t started and will never apply to {{ten}} ({{so}} tokens). This can’t be undone; to grant again, create a new entry.',
    hopNapTieuDe: 'Add tokens for {{ten}}',
    hopGoiTieuDe: 'Add a temporary package for {{ten}}',
    hopNapThan:
      'Tokens are used up gradually until the duration ends; unused tokens carry over to the next month and are lost when it ends.',
    hopGoiThan:
      'Lump sum: the whole token amount can be used at any time during the period, not split by month.',
    hopGiaHanTieuDe: 'Extend {{ten}}’s temporary package',
    hopHieuLucHienTai: 'Currently valid',
    oSoToken: 'Tokens',
    oSoTokenGoiY: 'Total prompt and completion tokens, for example 2000000.',
    oTuNgay: 'From',
    oTuNgayGoiY: 'Leave blank for today.',
    oDenNgayMoi: 'New end date',
    oDenNgayMoiGoiY: 'The package token amount stays the same.',
    oGhiChu: 'Note',
    oGhiChuGoiY: 'Contract number, reason — shown in the list and the audit log.',
    loiSoTrong: 'Enter the number of tokens.',
    loiSoSai: 'Enter a positive whole number, for example 2000000.',
    loiSoKhong: 'Add at least 1 token.',
    loiNgayTrong: 'Choose a date.',
    loiNgayTruocHomNay: 'Choose today or later.',
    loiNgayTruocBatDau: 'The end date must be on or after the start date.',
    loiNgayKhongSau: 'Choose a date after the current end date ({{ngay}}).',
    loiGhiChuDai: 'At most {{so}} characters.',
    hangChoCaption: 'Top-up requests from organizations',
    soDangCho: '{{so}} pending',
    locTrangThai: 'Filter by status',
    tatCaTrangThai: 'All statuses',
    cotToChuc: 'Organization',
    cotNoiDung: 'Request',
    cotLyDo: 'Reason',
    cotGuiLuc: 'Sent',
    guiLuc: 'Sent {{luc}}',
    phanHoi: 'Reply: {{noiDung}}',
    xemTenant: 'View tenant',
    duyetYeuCau: 'Approve request',
    tuChoiYeuCau: 'Reject request',
    hangChoRongCho: 'No pending requests',
    hangChoRongChoMo: 'New requests from organization admins appear here.',
    hangChoRongThat: 'No requests yet',
    hangChoRongThatMo: 'Organization admins send requests from the AI token card on their Overview page.',
    hangChoRongLoc: 'No requests with this status',
    loiHangCho: 'Couldn’t load the queue. {{loi}}',
    dangHien: 'Showing {{so}} requests',
    ndNapThem: '{{so}} tokens · {{thoiHan}}',
    ndGoiTamThoi: '{{so}} tokens lump sum · {{thoiHan}}',
    ndGiaHanGoiTamThoi: 'Temporary package until {{den}}',
    ndDaDuyetNgay: 'Approved: until {{den}}',
    hopDuyetTieuDe: 'Approve {{ten}}’s request',
    lyDoCuaToChuc: 'Reason:',
    goiHienTai: 'Current package: {{conLai}} / {{so}} tokens left, {{tu}} – {{den}}',
    oPhanHoi: 'Note to the organization',
    oPhanHoiGoiY: 'Optional. Worth adding when you approve a different amount.',
    duyetNap: 'Approve and add tokens',
    duyetGoi: 'Approve and add package',
    duyetGiaHan: 'Approve and extend',
    daDuyet: 'Approved {{ten}}’s request.',
    hopTuChoiTieuDe: 'Reject {{ten}}’s request?',
    hopTuChoiThan: 'The organization gets the reason in a notification. This can’t be undone; they can send a new request.',
    oLyDoTuChoi: 'Reason for rejecting',
    loiLyDoTuChoiTrong: 'Enter a reason so the organization knows what to do next.',
    daTuChoi: 'Rejected {{ten}}’s request.',
    yeuCauCuaToChuc: 'Top-up requests',
    yeuCauCaption: 'Your organization’s top-up requests',
    yeuCauRong: 'No requests sent yet',
    yeuCauRongMo: 'Need more tokens this month, a temporary package or an extension? Send a request; the platform administrator reviews it and you get notified.',
    loiYeuCau: 'Couldn’t load requests. {{loi}}',
    guiYeuCau: 'Request a top-up',
    dangGui: 'Sending...',
    daGuiYeuCau: 'Request sent, waiting for the platform administrator.',
    huyYeuCau: 'Cancel request',
    daHuyYeuCau: 'Request cancelled.',
    xacNhanHuy: 'Cancel this request?',
    xacNhanHuyMo: 'The platform administrator will no longer review it. This can’t be undone; send a new request if needed.',
    hopGuiTieuDe: 'Request a token top-up',
    hopGuiThan: 'The platform administrator reviews and approves requests; your organization is notified of the decision.',
    daDuCho: 'Your organization already has {{so}} pending requests. Cancel one or wait for a decision before sending more.',
    oLoai: 'Request type',
    oGoiCanGiaHan: 'Extend',
    goiTamThoiNhan: 'Temporary package {{so}} tokens, until {{den}}',
    oLyDo: 'Reason',
    oLyDoGoiY: 'What it’s for and the expected volume — helps get it approved faster.',
    loiLyDoTrong: 'Enter why you need more tokens.',
    hmTheoGoiTaiNguyen: 'from usage plan {{ten}}',
    chonGoiTamThoi: 'Choose a temporary package',
    khongCoGoiTamThoi: 'Your organization has no running or scheduled temporary package to extend.',
    loiChonGoiTamThoi: 'Choose the temporary package to extend.',
    tomConLaiTren: 'tokens left of {{so}}',
    tomDaDung: 'Used {{so}}',
    tomVeKhong: 'Usage plan tokens reset on {{ngay}}',
    tomThangVaoRa: 'This month: {{vao}} input tokens · {{ra}} output tokens',
    ctThuTuHet: 'Used from the top down: whatever expires first goes first.',
    ctThuTuMua: 'Used from the top down: purchased tokens first, the usage plan last.',
    ctTuiGoi: 'Usage plan {{ten}} · this month',
    ctTuiRiengTenant: 'Tenant-specific quota · this month',
    ctTuiRiengToChuc: 'Organization-specific quota · this month',
    ctTuiNap: 'Top-up',
    ctTuiTamThoi: 'Temporary package',
    ctVeKhong: 'Resets on {{ngay}}',
    ctDungToi: 'Usable until {{ngay}}',
    ctConLai: '{{so}} left',
    ctDaDungTren: 'Used {{daDung}} / {{tong}}',
    sapHetHan: 'Expiring soon',
    ctTongConLai: 'Total left',
    ctTongGiaTri: '{{conLai}} / {{tong}} tokens',
    uuTienTieuDe: 'Token usage order',
    uuTienCanhBao: 'Usage plan tokens not used within the month are lost at the end of the month.',
    uuTienCanhBaoSo:
      '{{so}} usage plan tokens are left this month; whatever is unused by the end of {{ngay}} is lost.',
    xacNhanUuTien: 'Use purchased tokens first?',
    xacNhanUuTienMo:
      'Extra tokens will be used first. Usage plan tokens not used within the month are lost at the end of the month. You can change this back at any time.',
    nutUuTienMua: 'Use purchased tokens first',
    daDatUuTien: 'Token usage order changed.',
    loiUuTien: 'Couldn’t change the token usage order. {{loi}}',
    thTuan: '{{so}} weeks',
    thThang: '{{so}} months',
    thDonVi: 'Duration unit',
    thDonViTuan: 'Weeks',
    thDonViThang: 'Months',
    oThoiHan: 'Duration',
    oThoiHanNapGoiY: 'Unused tokens carry over to the next month until the duration ends.',
    oThoiHanGoiGoiY: 'One lump sum usable at any time during the period, not split by month.',
    oThoiHanNapYeuCauGoiY: 'Counted from the approval date; unused tokens carry over until the duration ends.',
    oThoiHanGoiYeuCauGoiY: 'Counted from the approval date; one lump sum usable at any time during the period.',
    loiThoiHan: 'Choose a duration from the list.',
    oTokenTronGoi: 'Lump-sum tokens',
    oDenNgayTuyChon: 'End date (optional)',
    oDenNgayTuyChonGoiY: 'Leave blank to use the duration: until the end of {{ngay}}.',
    oTuNgayDuyetGoiY: 'Leave blank for the approval date (today).',
    xemTruocKhoang: 'Valid {{tu}} – {{den}}',
    xemTruocTong: 'Total available after granting: {{so}} tokens.',
    hopConLaiHienTai: 'Left / total available',
    hopDaDungThang: 'Used this month',
    hopConLaiGoi: 'Left / package total',
    giaHanKhongCongToken:
      'Extending only moves the end date; it doesn’t add tokens. To add tokens, do a top-up.',
    cotDaDungConLai: 'Used / left',
    daDungConLai: 'Used {{daDung}} · {{conLai}} left',
    daDungChi: 'Used {{daDung}}',
    ndDaDuyet: 'Approved: {{so}} tokens · {{tu}} – {{den}}',
    uuTien: {
      HET_HAN_TRUOC: 'Expiring first',
      MUA_THEM_TRUOC: 'Purchased tokens first',
    },
    thMotTuan: '1 week',
    thMotThang: '1 month',
  },
  // Gói tài nguyên: token AI mỗi tháng trên một gói nền (ADR-0091 mục 1).
  goiThanhToan: {
    tab: {
      goiDichVu: 'Service plans',
      danhMucDichVu: 'Service catalog',
      goiHanMuc: 'Usage plans',
      goiNapToken: 'Top-up packs',
      bangGiaModel: 'Model cost prices',
      yeuCauNapToken: 'Top-up requests',
    },
  },
  goiTaiNguyen: {
    taoGoi: 'Create usage plan',
    luuGoi: 'Save package',
    suaGoi: 'Edit package',
    batGoi: 'Reactivate package',
    ngungGoi: 'Deactivate package',
    daTao: 'Created usage plan {{ten}}.',
    daLuu: 'Saved usage plan {{ten}}.',
    daBat: 'Reactivated package {{ten}}.',
    daNgung: 'Deactivated package {{ten}}.',
    trangThaiBat: 'Active',
    trangThaiNgung: 'Deactivated',
    macDinh: 'System',
    cotTen: 'Package',
    cotGoiNen: 'Base tier',
    cotToken: 'AI tokens / month',
    cotSoTenant: 'Tenants',
    timTheo: 'Search by package name or code',
    timNhan: 'Search usage plans',
    locTrangThai: 'Filter by status',
    tatCaTrangThai: 'All statuses',
    loiDanhSach: 'Couldn’t load usage plans. {{loi}}',
    bangCaption: 'Usage plan catalogue',
    rongLoc: 'No packages match the filters',
    rongThat: 'No usage plans yet',
    rongThatMo:
      'The four default packages come from the migration; create your own when a group of tenants needs different limits.',
    dangHien: 'Showing {{so}} packages',
    dangHienLoc: 'Showing {{so}} of {{tong}} packages',
    xacNhanNgung: 'Deactivate package {{ten}}?',
    xacNhanNgungMo:
      'The package can no longer be chosen for tenants. No tenant uses it right now. You can reactivate it at any time.',
    xacNhanBat: 'Reactivate package {{ten}}?',
    xacNhanBatMo: 'The package can be chosen again in the tenant create and edit forms.',
    hopTaoTieuDe: 'Create usage plan',
    hopSuaTieuDe: 'Edit package {{ten}}',
    suaCanhBao:
      '{{so}} tenants use this package: new limits apply to them right away (except limits set for a specific tenant).',
    oTen: 'Package name',
    oTenGoiY: 'For example: Professional plus.',
    oMa: 'Package code',
    oMaChoTen: 'Enter a name to generate the code',
    oMaKhongDoi: 'Can’t be changed after creation.',
    oMaTuSinh: 'Generated from the name; can’t be changed after creation.',
    oGoiNen: 'Base tier',
    heThongGoiY: 'System package: the name, code and base tier are fixed; the limits can be changed.',
    oGoiNenGoiY:
      'Tenants on this package get this base tier (security policy and licences follow the base tier).',
    oGoiNenChon: 'Choose a base tier',
    oToken: 'AI tokens per month',
    oTokenXemTruoc: '{{so}} tokens per month',
    oTokenGoiY: 'Total prompt and completion tokens; 0 blocks AI.',
    oNguoiDung: 'Maximum users',
    oDungLuong: 'Database storage',
    oTinThongBao: 'Notifications per month',
    donViNguoi: 'users',
    donViGb: 'GB',
    donViTin: 'messages',
    chuaDat: 'Not set',
    oChuaDatGoiY: 'Leave empty to keep it unset (no limit).',
    loiNguoiDungTrong: 'Enter the maximum number of users.',
    loiDungLuongTrong: 'Enter the database storage.',
    loiTinTrong: 'Enter the number of notifications per month.',
    loiSoNguyen: 'Enter a whole number from 1 to {{so}}.',
    keoTenant:
      '{{so}} tenants on this package will move from the {{cu}} tier to {{moi}}: their security policy and licences follow the new tier.',
    loiTenTrong: 'Enter a package name.',
    loiTenDai: 'Package name can be at most {{so}} characters.',
    loiTenKhongSinhMa: 'The name needs letters or digits to generate a code.',
    loiTrungMa: 'Code {{ma}} already exists. Change the name to get a different code.',
    loiGoiNenTrong: 'Choose a base tier.',
    chonGoi: 'Choose a usage plan',
    nhanLuaChon: '{{ten}} · {{so}} tokens/month',
    goiNenLa: 'Base tier: {{goi}}',
  },


  ocr: {
    heThong: 'System',
    ban: 'You',
    nguoiDungMa: 'User {{ma}}',
    modelMacDinh: "Organization's default model",
    tepGoc: 'Original file',
    loiTaiTep: "Couldn't load the original file: {{loi}}",
    khongCoTep: 'No original file to show',
    khongXemTruocDuoc: "This format can't be previewed",
    khongXemTruocDuocMo: "Word and Excel files can't be shown in the browser. Open the file in an app on your computer.",
    moTep: 'Open file',
    loiBanChep: "Couldn't load the OCR transcript: {{loi}}",
    chuaCoBanChep: 'No OCR transcript yet',
    chuaCoBanChepMo: 'The page-by-page transcript is available after the text-reading step.',
    tepSo: 'File {{so}}',
    soTrang: '{{so}} pages',
    trangSo: 'Page {{so}}',
    docBang: 'read with {{ten}}',
    lopVanBanPdf: 'PDF text layer',
    dungLaiTuKho: 'Reused from the OCR library',
    loai: {
      INVOICE: 'Invoice',
      RECEIPT: 'Receipt',
      CONTRACT: 'Contract',
      STATEMENT: 'Statement',
      MINUTES: 'Minutes',
      WAREHOUSE_RECEIPT: 'Goods received note',
      OTHERS: 'Other',
    },
    kieuDuLieu: {
      TEXT: 'Text',
      DATE: 'Date',
      NUMBER: 'Number',
      CURRENCY: 'Amount',
      BOOLEAN: 'Yes / no',
      LIST: 'Choice list',
    },
    viTri: {
      HEADER: 'Document header',
      BODY: 'Document body',
      FOOTER: 'Document footer',
    },
    cotLoc: {
      INVOICE_NUMBER: 'Document number',
      ISSUE_DATE: 'Document date',
      SELLER_TAX_CODE: 'Seller tax code',
      SELLER_NAME: 'Seller name',
      TOTAL_AMOUNT: 'Amount before tax',
      VAT_AMOUNT: 'VAT amount',
      GRAND_TOTAL: 'Grand total',
    },
    cheDo: {
      SINGLE_MODEL: 'Single model',
      OCR_THEN_MAP: 'Two steps: read text, then fill data',
    },
    ngonNgu: {
      vi: 'Vietnamese',
      en: 'English',
      viEn: 'Vietnamese and English',
    },
  },

  cauHinhOcr: {
    daTao: 'Created document type "{{ten}}".',
    daLuu: 'Changes saved.',
    daXoa: 'Deleted document type "{{ten}}".',
    daThemTruong: 'Field added.',
    daXoaTruong: 'Field deleted.',
    daThemBang: 'Table added.',
    daXoaBang: 'Table deleted.',
    daThemCot: 'Column added.',
    daXoaCot: 'Column deleted.',
    dangDung: 'In use',
    daTat: 'Turned off',
    moTa: 'A document type decides what the model reads and which fields and tables it fills during recognition',
    taoLoai: 'Create document type',
    taoLoaiDauTien: 'Create the first document type',
    taoTieuDe: 'Create document type',
    dangTao: 'Creating...',
    loiTheoO: 'Some fields need fixing — see the highlighted fields.',
    tkTong: 'Document types',
    tkTongMo: 'Total declared',
    tkDangDung: 'In use',
    tkDangDungMo: 'Selectable for recognition',
    tkTruong: 'Data fields',
    tkTruongMo: 'Across all document types',
    tkBang: 'Tables',
    tkBangMo: 'Across all document types',
    timPlaceholder: 'Search by code, name, description...',
    timNhan: 'Search document types',
    tatCaLoai: 'All categories',
    locLoai: 'Filter by category',
    tatCaTrangThai: 'All statuses',
    locTrangThai: 'Filter by status',
    bangCaption: 'OCR document types',
    cotLoai: 'Document type',
    cotPhanLoai: 'Category',
    cotModel: 'Extraction',
    buocDoc: 'Read text: {{ten}}',
    buocDien: 'Fill data: {{ten}}',
    cotCauTruc: 'Structure',
    soTruong: '{{so}} fields',
    soBang: '{{so}} tables',
    soCot: '{{so}} columns',
    cotChungTu: 'Documents',
    cotTao: 'Created by',
    dangHien: 'Showing {{so}} document types',
    dangHienLoc: 'Showing {{so}} of {{tong}} document types',
    loiDanhSach: "Couldn't load document types: {{loi}}",
    rongLoc: 'No document types match the filters',
    rongThat: 'No document types yet',
    rongThatMo: 'Declare a document type so it can be selected for OCR recognition.',
    xemCauHinh: 'View configuration',
    batLoai: 'Turn on document type',
    batLoaiMo: 'Selectable for recognition again',
    tatLoai: 'Turn off document type',
    tatLoaiMo: "Can't be selected for recognition; existing documents stay",
    xoaLoai: 'Delete document type',
    xoaChanMo: 'Has {{so}} documents — delete those documents first',
    xoaLoaiHoi: 'Delete document type "{{ten}}"?',
    xoaLoaiThan: "The document type and all its fields, tables and columns are deleted. This can't be undone.",
    fMa: 'Document type code',
    fMaGoiY: "Uppercase letters, digits, underscores, hyphens. Can't be changed after creation.",
    fMaTrong: 'Enter a document type code.',
    fMaSai: 'The code may only contain uppercase letters, digits, _ and -.',
    fTen: 'Document type name',
    fTenGoiY: 'For example: Incoming VAT invoice',
    fTenTrong: 'Enter a document type name.',
    fLoai: 'Category',
    fMoTa: 'Description for the model',
    fMoTaGoiY: 'This goes into the prompt of the data-filling step: say what the document is and how numbers and dates are written.',
    fCheDo: 'Extraction',
    fModelMot: 'Recognition model',
    fModelDien: 'Data-filling model (step 2)',
    fModelDoc: 'Text-reading model (step 1)',
    fModelDocTrong: 'Choose a text-reading model for step 1.',
    fChonModelDoc: 'Choose a text-reading model',
    modelKhongDung: '{{khoa}} (turned off or removed)',
    cheDoMo: {
      SINGLE_MODEL: 'One model reads the text and fills the data in a single pass.',
      OCR_THEN_MAP: 'An OCR model reads each page, then a reasoning model fills the data. The transcript is kept for fast rescans.',
    },
    fDangDung: 'In use',
    fDangDungMo: "When off, it can't be selected for recognition; existing documents stay.",
    coXuLy: 'Processing options',
    co: {
      split_pdf_pages: 'Split PDFs by page',
      split_pdf_pagesMo: 'Send each page to the model. Clear to send the whole document in one call.',
      enable_word_boxes: 'Locate each value',
      enable_word_boxesMo: 'Used to highlight values on the original file during review.',
      transform_by_prompt: 'Normalize by description',
      transform_by_promptMo: 'The model reformats values following the conventions in the description.',
      model_self_map: 'Model matches columns',
      model_self_mapMo: 'The model matches table columns by the labels printed on the document.',
    },
    chiTietTieuDe: 'Document type configuration',
    chiTietMo: 'General information, data fields and tables the model fills',
    khongThay: 'Document type not found',
    khongThayMo: 'This document type was deleted or the link is wrong.',
    veDanhSach: 'Back to document types',
    loiChiTiet: "Couldn't load the document type: {{loi}}",
    khoiChung: 'General information',
    khoiChungMo: "Changes apply to the next recognition run and don't change documents already scanned.",
    boThayDoi: 'Discard changes',
    luuThayDoi: 'Save changes',
    khoiTruong: 'Data fields',
    khoiTruongMo: '{{so}} fields the model fills',
    bangTruongCaption: 'Data fields of {{ten}}',
    cotTruong: 'Field',
    cotKieu: 'Data type',
    cotViTri: 'Position',
    cotRangBuoc: 'Constraints',
    batBuoc: 'Required',
    duyNhat: 'Unique',
    cotCotLoc: 'Quick filter column',
    cotNhanIn: 'Label printed on the document',
    chuaCoTruong: 'No fields yet',
    chuaCoTruongMo: 'Add fields so the model knows which values to extract.',
    themTruong: 'Add field',
    themTruongDauTien: 'Add the first field',
    suaTruong: 'Edit field',
    xoaTruong: 'Delete field',
    xoaTruongHoi: 'Delete field "{{ten}}"?',
    xoaTruongThan: "The model stops filling this field from the next run. A field that already has values on documents can't be deleted. This can't be undone.",
    khoiBang: 'Tables',
    khoiBangMo: '{{so}} tables, each one a list of lines on the document',
    chuaCoBang: 'No tables yet',
    chuaCoBangMo: 'Add a table when the document has a list of lines, such as goods or services.',
    themBang: 'Add table',
    themBangDauTien: 'Add the first table',
    suaBang: 'Edit table',
    xoaBang: 'Delete table',
    xoaBangHoi: 'Delete table "{{ten}}"?',
    xoaBangThan: "The table and its {{so}} columns are deleted. A table that already has lines on documents can't be deleted. This can't be undone.",
    coHangTieuDe: 'has a header row',
    khongHangTieuDe: 'no header row',
    bangCotCaption: 'Columns of table {{ten}}',
    cotCot: 'Column',
    chuaCoCot: 'This table has no columns yet',
    chuaCoCotMo: 'Add columns so the model knows which values each line holds.',
    themCot: 'Add column',
    suaCot: 'Edit column',
    xoaCot: 'Delete column',
    xoaCotHoi: 'Delete column "{{ten}}"?',
    xoaCotThan: "The model stops filling this column from the next run. This can't be undone.",
    truongThemTieuDe: 'Add data field',
    truongSuaTieuDe: 'Edit data field',
    luuTruong: 'Save field',
    fKhoa: 'Key',
    fKhoaGoiY: 'Letters, digits, underscores; starts with a letter. Used in the JSON result.',
    fKhoaBangGoiY: "The key can't change once documents have lines in this table.",
    fKhoaTrong: 'Enter a key.',
    fKhoaSai: 'The key may only contain letters, digits and underscores, and must start with a letter.',
    fNhan: 'Label',
    fNhanTrong: 'Enter a label.',
    fKieu: 'Data type',
    fViTri: 'Position on the document',
    fBatBuoc: 'Value required',
    fDuyNhat: 'No duplicates across documents',
    fCotLoc: 'Quick filter column',
    fCotLocGoiY: 'The value is copied to a filter column of the document list. Each filter column takes one field.',
    khongGanCotLoc: 'None',
    cotLocDaDung: '{{cot}} — assigned to "{{ten}}"',
    fNhanIn: 'Label printed on the document',
    fNhanInGoiY: 'Separate alternative printings with |, for example: No.|Invoice no.',
    fRegex: 'Validation pattern',
    fRegexGoiY: 'A regular expression the value must match. Leave empty to skip.',
    fRegexSai: 'The regular expression is invalid.',
    fMoTaTruong: 'Note for the model',
    fMoTaTruongGoiY: 'For example how to read it, or the allowed values of a choice list.',
    bangThemTieuDe: 'Add table',
    bangSuaTieuDe: 'Edit table',
    luuBang: 'Save table',
    fTenBang: 'Table name',
    fTenBangTrong: 'Enter a table name.',
    fMoTaBang: 'Note for the model',
    fCoTieuDe: 'Table has a header row',
    fCoTieuDeMo: 'Clear when the printed table has no header row: columns are matched in the declared order.',
    cotThemTieuDe: 'Add column to table "{{bang}}"',
    cotSuaTieuDe: 'Edit column of table "{{bang}}"',
    luuCot: 'Save column',
    // NEO-CAU-HINH-OCR
  },

  khoOcr: {
    moTa: 'Documents already read by OCR, kept so you can create documents without reading them again',
    taiLen: 'Upload documents',
    timNhan: 'Search documents',
    timPlaceholder: 'Search by file name...',
    locTrangThai: 'Filter by status',
    tatCaTrangThai: 'All statuses',
    trangThai: {
      READY: 'Has a usable version',
      RUNNING: 'Reading',
      FAILED: 'Reading failed',
      EMPTY: 'No OCR version',
    },
    trangThaiBan: {
      QUEUED: 'Waiting',
      READING: 'Reading',
      DONE: 'Read',
      FAILED: 'Reading failed',
    },
    nguon: {
      PRE_OCR: 'Read ahead in the library',
      DOCUMENT: 'Read while recognizing a document',
      BACKFILL: 'Added from old data',
    },
    cot: {
      tep: 'Document',
      trangThai: 'Status',
      banDung: 'Version in use',
      soBan: 'Versions',
      soChungTu: 'Used by',
      nguoiTai: 'Uploaded by',
      ngayTai: 'Uploaded on',
    },
    bangCaption: 'Documents in the OCR library',
    tienDo: 'Page {{xong}}/{{tong}}',
    tienDoChuaRo: 'Preparing...',
    daGhim: 'Pinned',
    moiNhat: 'Latest version',
    chuaCoBanDung: 'None',
    xemChiTiet: 'View details',
    docLai: 'Read again with another model',
    xoaTaiLieu: 'Delete document',
    loiDanhSach: "Couldn't load the OCR library: {{loi}}",
    rongLoc: 'No documents match the filters',
    rongThat: 'The OCR library has no documents yet',
    rongThatMo: 'Upload PDFs or images to read them ahead, then create documents from the OCR version without reading again.',
    taiLenDauTien: 'Upload the first document',
    dangHien: 'Showing {{so}} documents',
    dangHienCon: 'Showing {{so}} documents, older ones available',
    hopTaiTieuDe: 'Upload documents to read',
    keoTha: 'Drag files here, or click to choose',
    keoThaMo: 'PDF or images, up to 20 files, 25 MB each',
    tepDaChon: 'Selected files',
    boTep: 'Remove {{ten}}',
    loiSaiDinhDang: '{{ten}}: only PDF or images are accepted. Remove this file and send again.',
    loiQuaLon: '{{ten}}: larger than 25 MB. Split the file or lower the resolution, then choose it again.',
    loiQuaNhieu: 'Up to 20 files at a time. Remove {{so}} files and send again.',
    loiChuaChon: 'Choose at least one file to read.',
    modelDoc: 'Text-reading model',
    modelDocMo: 'Only models that can read page by page are listed.',
    chonModelDoc: 'Choose a text-reading model',
    chuaCoModelDoc: 'No text-reading model is turned on. Turn one on in AI model settings, then come back.',
    loiChuaChonModel: 'Choose a text-reading model.',
    boQuaDaCo: 'Skip files that already have an OCR version',
    boQuaDaCoMo: "Files with the same content as an already read document aren't read again.",
    guiSo: 'Read {{so}} files',
    dangGui: 'Uploading...',
    xong: 'Done',
    ketQuaTieuDe: 'Upload results',
    ketQuaTomTat: '{{xep}} files queued for reading, {{bo}} skipped, {{loi}} not accepted.',
    ketQua: {
      QUEUED: 'Queued for reading',
      SKIPPED_DONE: 'Skipped: already has an OCR version',
      SKIPPED_RUNNING: 'Skipped: being read',
      REJECTED: 'Not accepted',
      FAILED: 'Failed',
    },
    hopDocLaiTieuDe: 'Read "{{ten}}" again?',
    hopDocLaiMo: 'A new OCR version is created with the chosen model. Older versions are kept, and documents using them don\'t change.',
    docLaiNut: 'Read document again',
    daDocLai: 'Queued "{{ten}}" for reading again.',
    xoaTepTieuDe: 'Delete "{{ten}}" from the library?',
    xoaTepMo: 'The document and all its OCR versions are removed from the library. This can\'t be undone.',
    daXoaTep: 'Deleted "{{ten}}".',
    xoaBanTieuDe: 'Delete this OCR version?',
    xoaBanMo: 'The OCR version read with {{ten}} is deleted. This can\'t be undone.',
    xoaBanNut: 'Delete OCR version',
    daXoaBan: 'OCR version deleted.',
    chiTietTieuDe: 'Document details',
    thongTin: 'File information',
    tenTep: 'File name',
    kichThuoc: 'Size',
    kieuTep: 'File type',
    xemTruoc: 'Preview',
    cacBan: 'OCR versions ({{so}})',
    chuaCoBan: 'No OCR versions yet',
    chuaCoBanMo: 'Read the document again to create its first OCR version.',
    dangDung: 'In use',
    ghim: 'Pin this version',
    boGhim: 'Unpin',
    huyDoc: 'Cancel reading',
    xemBanChep: 'View transcript',
    daGhimToast: 'OCR version pinned. Documents created from now on use this version.',
    daBoGhim: 'OCR version unpinned.',
    daHuyDoc: 'Reading cancelled.',
    hopHuyDocTieuDe: 'Cancel the reading in progress?',
    hopHuyDocMo: 'This OCR version stops and is marked as failed. You can read the document again at any time.',
    banTao: '{{ngay}} · {{nguoi}}',
    soChungTuDung: 'Used by {{so}} documents',
    banChepCua: 'Transcript: {{ten}}',
    dongBanChep: 'Close transcript',
    thaoTacBan: 'Actions for OCR version {{ten}}',
    loiChiTiet: "Couldn't load the document: {{loi}}",
    // NEO-KHO-OCR
  },

  aiModel: {
    themModel: 'Add AI model',
    bangCaption: 'Platform AI model catalogue',
    timTheo: 'Search by name, key, model id...',
    timNhan: 'Search AI models',
    tatCaEngine: 'All engines',
    tatCaTrangThai: 'On and off',
    locTrangThai: 'Filter by status',
    cotTenHienThi: 'Display name',
    cotEngine: 'Engine',
    cotModelId: 'Model id',
    cotLamDuoc: 'Can do',
    cotSuyLuan: 'Reasoning',
    cotGhiChu: 'Notes',
    macDinh: 'Default',
    modelCuaNhaCungCap: "Engine's default",
    anh: 'image: {{id}}',
    vaiDocChu: 'Read text',
    vaiDienDuLieu: 'Fill data',
    vaiGiong: 'Voice',
    mucDich: { VAN_BAN: 'Text', GIONG: 'Voice' },
    dangBat: 'On',
    daTatNhan: 'Off',
    dangHien: 'Showing {{so}} models',
    dangHienLoc: 'Showing {{so}} of {{tong}} models',
    datLamMacDinh: 'Set as default model',
    dangLaMacDinh: 'Already the default model',
    batTruocKhiDatMacDinh: 'Turn the model on before setting it as default',
    khongDienDuocDeMacDinh: "A text-reading model can't be the default",
    batModel: 'Turn model on',
    tatModel: 'Turn model off',
    doiMacDinhTruocKhiTat: 'Default model — set another model as default before turning it off',
    chinhSua: 'Edit',
    xacNhanTat: 'Turn off "{{ten}}" for every tenant?',
    xacNhanTatMo:
      "Users in every tenant can no longer pick this model when setting up or recognising documents; document types that use it will show the model as unavailable. The model isn't deleted and can be turned back on at any time.",
    xacNhanTatGiongMo: 'The AI assistant in every tenant loses the voice that uses this model. The model is not deleted and can be turned back on at any time.',
    loiDanhSach: "Couldn't load the AI model catalogue: {{loi}}",
    rongLoc: 'No models match the filters',
    rongThat: 'The catalogue has no AI models yet',
    rongThatMo: 'Add the first model so tenants can recognise documents.',
    dialogThem: 'Add AI model',
    dialogSua: 'Edit AI model',
    themVaLuu: 'Add model',
    fTenHienThi: 'Display name',
    fTenHienThiGoiY: 'For example: Gemini 2.5 Pro',
    fTenHienThiTrong: 'Enter a display name for the model.',
    fQuaDai: 'Up to {{so}} characters.',
    fKhongSinhDuocKhoa: 'The name needs at least one letter or digit to build a key, or fill in the model id.',
    fKhoaTrung: "Couldn't build a unique key from this name. Change the name or use a different model id.",
    fKhoa: 'Model key',
    fKhoaChoTen: 'Fill in a name or model id to build the key',
    fKhoaTuSinh: "Built from the model id, or from the name when there's none; -2, -3 is added if taken. Can't be changed after creation.",
    fEngine: 'Engine',
    fEngineMo: "Can't be changed after creation.",
    fKhongDoiSauTao: "The key and engine can't be changed after creation: tenants' document settings refer to them.",
    fModelId: 'Model id',
    fModelIdMo: "Leave empty to use the engine's default model configured by the platform.",
    fVisionModelId: 'Image model id',
    fVisionModelIdMo: 'Only needed when the engine reads images with a separate model.',
    fMucDich: 'Purpose',
    fMucDichMo: "Voice models are only for speech: they don't appear in model pickers and can't be the default.",
    fMucDichMacDinh: 'The default model is always a text model. To change this, set another model as default first.',
    fSuyLuan: 'Reasoning',
    fSuyLuanMo: 'Whether the model runs a reasoning step before answering.',
    fGhiChu: 'Notes',
    fGhiChuGoiY: 'Which document types this model suits',
    fApDung: 'Turn this model on',
    fMacDinh: "Set as the platform's default model",
    fMacDinhPhaiBat: 'The default model must be on. Turn this model on or untick default.',
    fMacDinhLuonBat: 'The default model is always on. To turn it off, set another model as default first.',
    daThem: 'Added model "{{ten}}" with key {{khoa}}.',
    daCapNhat: 'Saved model "{{ten}}".',
    daDatMacDinh: '"{{ten}}" is now the default model.',
    daBat: 'Turned on model "{{ten}}".',
    daTat: 'Turned off model "{{ten}}".',
    tab: 'AI models',
    tenantTieuDe: 'AI models this tenant can use',
    tenantDem: '{{so}}/{{tong}} models available',
    tenantCaption: 'AI models of tenant {{ten}}',
    tenantDangTai: "Loading the tenant's AI models",
    tenantLoi: "Couldn't load the tenant's AI models: {{loi}}",
    tenantLoiLuu: "Couldn't save: {{loi}}",
    tenantRong: 'No models are on in the catalogue',
    tenantRongMo: 'Turn on or add models on the AI models screen, then come back to limit them for this tenant.',
    moDanhMuc: 'Open the AI model catalogue',
    cotDungDuoc: 'Available to tenant',
    dungDuoc: 'Available',
    khongDungDuoc: 'Off',
    congTacNhan: 'Let the tenant use model {{model}}',
    macDinhCuaTenant: "Tenant's default",
    tenantTatHet: "The tenant won't be able to recognise documents until at least one model is turned back on.",
    tenantChuaLuu: 'Unsaved changes: {{so}}',
    tenantDaKhop: 'No unsaved changes',
    huyThayDoi: 'Discard changes',
    luuThayDoi: 'Save changes',
    tenantDaLuu: 'Saved AI models for tenant {{ten}}.',
    engine: {
      GEMINI: 'Google Gemini',
      CLAUDE: 'Anthropic Claude',
      FOXAI: 'FoxAI (self-hosted)',
      GLM: 'Zhipu GLM',
      CHANDRA: 'Chandra OCR (self-hosted)',
      DEEPSEEK: 'DeepSeek OCR (self-hosted)',
      CHANDRA2: 'Chandra 2 (self-hosted)',
      PADDLE: 'PaddleOCR-VL (self-hosted)',
      CHANDRA2_L40: 'Chandra 2 on the L40 cluster',
      QWEN38: 'Qwen 3.8 (self-hosted)',
      CHANDRA2_H200: 'Chandra 2 on the H200 cluster',
      QWEN38_H200: 'Qwen 3.8 on the H200 cluster',
      GEMMA4: 'Gemma 4 (self-hosted)',
    },
    suyLuan: {
      OPTIONAL: 'Optional',
      FORCED: 'Always on',
      DISABLED: 'Not used',
    },
  },

  chungTu: {
    tieuDe: 'Document management',
    moTa: 'Documents recognised by OCR: review results, edit, confirm, then send to the knowledge base',
    bangCaption: 'Uploaded documents',
    nhanDangMoi: 'Recognise a document',
    thTongChungTu: 'Total documents',
    thDangXuLy: 'Processing',
    thChoXacNhan: 'Awaiting confirmation',
    thDaXacNhan: 'Confirmed',
    thDaChuyenKho: 'Sent to knowledge base',
    thLoiOcr: 'OCR errors',
    goiYGiaiDoan: '{{cho}} queued · {{doc}} reading · {{suyLuan}} reasoning',
    timTheo: 'Search by file name, type code, document number, seller...',
    timNhan: 'Search documents',
    tatCaTrangThai: 'All statuses',
    tatCaLoai: 'All types',
    nhanLoaiChungTu: 'Document type',
    locTatCa: 'Processing (all)',
    cotChungTu: 'Document',
    cotMa: 'Type code',
    cotLoai: 'Type',
    cotSoChungTu: 'Number, seller',
    cotTongTien: 'Total',
    cotNgayTao: 'Uploaded',
    cotNguoiThucHien: 'Uploaded by',
    khongTen: 'Untitled file',
    phuLuc: '+{{so}} attached files',
    dangHien: 'Showing {{so}} documents',
    dangHienConNua: 'Showing {{so}} documents, older ones not loaded yet',
    daChon: '{{so}} documents selected',
    xacNhanSo: 'Confirm {{so}} documents',
    chuyenSo: 'Send {{so}} documents',
    xoaSo: 'Delete {{so}} documents',
    boChon: 'Clear selection',
    xacNhanXoaSo: 'Delete {{so}} documents?',
    xacNhanXoaSoMo:
      "The selected documents and their OCR results are deleted. This can't be undone. Confirmed or sent documents are kept. Files in the OCR library are not deleted.",
    xacNhanChuyenSo: 'Send {{so}} documents to the knowledge base?',
    xacNhanChuyenSoMo:
      "Only confirmed documents are sent; after that they can't be edited. An older sign-in may need to sign in again for this sensitive action.",
    ketQuaHangLoat: '{{viec}} {{xong}} documents.',
    ketQuaHangLoatBoQua: '{{viec}} {{xong}} documents; skipped {{boQua}} that were in the wrong status.',
    viecXacNhan: 'Confirmed',
    viecChuyen: 'Sent',
    viecXoa: 'Deleted',
    xemChiTiet: 'View details',
    xacNhanChungTu: 'Confirm document',
    chuyenVaoKho: 'Send to knowledge base',
    thuLaiSuyLuan: 'Retry the reasoning step',
    thuLaiSuyLuanMo: 'Reuses the existing OCR transcript',
    quetLaiTuDau: 'Rescan from scratch',
    quetLaiMo: 'Reads the original file again',
    huyLuotQuet: 'Cancel the scan',
    xoaChungTu: 'Delete document',
    xacNhanXoaMot: 'Delete document "{{ten}}"?',
    xacNhanXoaMotMo: "The document and its OCR results are deleted. This can't be undone. Files in the OCR library are not deleted.",
    xacNhanQuetLai: 'Rescan "{{ten}}" from scratch?',
    xacNhanQuetLaiMo:
      'The original file is read again and every field is refilled; values you edited are overwritten, only rows you added are kept. The scan runs in the background for a few minutes.',
    xacNhanHuyQuet: 'Cancel the scan of "{{ten}}"?',
    xacNhanHuyQuetMo: 'The document moves to Error at the current step. You can rescan it at any time.',
    xacNhanChuyenMot: 'Send "{{ten}}" to the knowledge base?',
    xacNhanChuyenMotMo:
      "After sending, the document can't be edited. An older sign-in may need to sign in again for this sensitive action.",
    daXoaMot: 'Document deleted.',
    daXacNhanMot: 'Document confirmed.',
    daChuyenMot: 'Document sent to the knowledge base.',
    daBatDauQuetLai: 'Document queued for rescanning.',
    daHuyQuet: 'Scan cancelled.',
    daLuuGiaTri: 'Edited values saved.',
    loiDanhSach: "Couldn't load documents: {{loi}}",
    rongLoc: 'No documents match the filters',
    rongThat: 'No documents yet',
    rongThatMo: 'Documents appear here after you recognise a file with OCR.',
    keoDeDoiDoRong: 'Drag to resize the two panes',
    chiTietTieuDe: 'Document details',
    loiChiTiet: "Couldn't load the document: {{loi}}",
    khongTimThay: 'Document not found',
    khongTimThayMo: 'The document was deleted or belongs to another organization.',
    veDanhSach: 'Back to documents',
    theTepGoc: 'Original file',
    theBanChep: 'OCR transcript',
    tepChinh: 'Main file',
    dangQuet: 'Recognising — this page updates when done',
    luotThu: 'attempt {{so}}',
    loiQuet: 'The last scan failed at the {{buoc}} step',
    buocDoc: 'text-reading',
    buocSuyLuan: 'reasoning',
    thongTinChung: 'Document information',
    ttLoaiChungTu: 'Document type',
    ttSoChungTu: 'Document number',
    ttNgayChungTu: 'Document date',
    ttNguoiBan: 'Seller',
    ttMstNguoiBan: 'Seller tax code',
    ttTongThanhToan: 'Grand total',
    ttDoTinCay: 'Confidence',
    ttTaiLenBoi: 'Uploaded',
    ttXacNhanBoi: 'Confirmed',
    ttChuyenBoi: 'Sent',
    nguoiLuc: '{{ten}}, {{luc}}',
    truongDuLieu: 'Data fields',
    soTruong: '{{so}} fields',
    chuaCoTruong: 'This document type has no fields yet',
    chuaNhanDang: 'Not recognised',
    daSuaTay: 'Edited',
    co: 'Yes',
    khong: 'No',
    chiDoc: 'Only Draft or Awaiting confirmation documents can be edited.',
    luuGiaTri: 'Save edited values',
    boThayDoi: 'Discard changes',
    soDong: '{{so}} rows',
    bangCaptionDong: 'Rows of table {{ten}} recognised from the document',
    bangRong: 'This table has no rows',
    trangThai: {
      DRAFT: 'Draft',
      PROCESSING: 'Processing',
      PROCESSED: 'Awaiting confirmation',
      CONFIRMED: 'Confirmed',
      TRANSFERRED: 'Sent to knowledge base',
      ERROR: 'Error',
    },
    giaiDoan: {
      QUEUED: 'Queued',
      READING: 'Reading text (OCR)',
      READ_DONE: 'OCR done, awaiting reasoning',
      MAPPING: 'Reasoning',
      RETRY_WAIT: 'Failed, retrying soon',
    },
    tooltipGiaiDoan: 'At this stage: {{nhan}}',
    tooltipChoThuLai: 'The previous attempt failed and is waiting to retry.',
    loiOBuoc: 'Failed at the {{buoc}} step',
    loai: {
      HDVAT: 'VAT invoice',
      HDBL: 'Sales contract',
      PNK: 'Goods received note',
      BBBG: 'Handover minutes',
    },
  },

  nhanDang: {
    chonKhuonTieuDe: 'OCR recognition',
    chonKhuonMoTa: 'Choose a document type to start recognising',
    timKhuon: 'Search document types by name or code...',
    timKhuonNhan: 'Search document types',
    khongCoKhuonKhop: 'No document types match',
    chuaCoKhuon: 'No document types in use',
    chuaCoKhuonMo: 'Set up a document type in OCR document setup before recognising.',
    moThietLap: 'Open OCR document setup',
    loiKhuon: "Couldn't load document types: {{loi}}",
    soTruong: '{{so}} fields',
    soBang: '{{so}} tables',
    khongTimThayKhuon: 'Document type not found',
    khongTimThayKhuonMo: 'The document type with code "{{ma}}" does not exist or was removed.',
    khuonDaTat: 'This document type is turned off — turn it on in OCR document setup before recognising.',
    chonKhuonKhac: 'Choose another document type',
    doiKhuon: 'Change document type',
    keoThaTep: 'Drag files here, or click to choose',
    gioiHanTep: 'PDF, image, Word, Excel, CSV or TXT · up to 20 files, 25 MB each, 60 MB in total',
    tepDaChon: 'Selected files',
    tepChinhMo: 'The first file is the main file; the rest are attached to the same document.',
    boTep: 'Remove file {{ten}}',
    loiDinhDang: "This format can't be recognised.",
    loiQuaLon: 'The file is larger than 25 MB.',
    loiQuaNhieu: 'Up to 20 files at a time.',
    loiTongQuaLon: 'The files exceed 60 MB in total — remove some and send again.',
    caiDat: 'How to recognise',
    cheDo: 'Mode',
    modelDien: 'Data-filling model',
    modelDienMo: "Leave empty to use the document type's model, or the organization's default.",
    theoLoaiChungTu: 'As set on the document type',
    modelDoc: 'Text-reading model',
    modelDocMo: 'Step 1 of the two-step mode: transcribes each page verbatim.',
    ngonNgu: 'Document language',
    batDauNhanDang: 'Start recognition',
    batDauNhanDangSo: 'Recognise {{so}} files',
    dangGui: 'Uploading...',
    chiXem: 'You can only view — uploading needs the recognition permission.',
    daTaiLen: 'Files uploaded; the document is being recognised.',
  },
  // ───────────────────────── Service packages ─────────────────────────
  goiDichVu: {
    tkTongGoi: 'Total plans',
    tkTongGoiSub: 'including those no longer on sale',
    tkDangBan: 'On sale',
    tkDangBanSub: 'new subscription periods can be created',
    tkTenantDangDung: 'Tenants using',
    tkTenantDangDungSub: 'includes tenants within their grace period',
    tkDichVu: 'Services',
    tkDichVuSub: 'adding a service requires a deploy',

    bangCaption: 'Service plans sold to tenants',
    cotTenGoi: 'Plan',
    cotDichVu: 'Services included',
    cotAnHan: 'Grace',
    cotTenantDangDung: 'Tenants using',
    cotSoKy: 'Periods',
    anHanNgay: '{{so}} days',
    anHanKhong: 'No grace period',
    soDichVu: '{{so}} services',
    themNDichVu: '+{{so}}',
    khongCoDichVu: 'No services selected',
    nhanHeThong: 'System',
    nhanDangBan: 'On sale',
    nhanNgungBan: 'Off sale',
    tongSoGoi: '{{so}} plans',

    themGoi: 'Add plan',
    suaGoi: 'Edit plan',
    xoaGoi: 'Delete plan',
    khongXoaHeThong: 'System plan, cannot be deleted',
    khongXoaDaDung: 'Used by {{so}} subscription periods',

    dialogThem: 'Add service plan',
    dialogSua: 'Edit plan {{ma}}',
    dialogSuaCanhBao: 'Changes take effect immediately for every tenant on this plan.',
    fTenGoi: 'Plan name',
    fTenGoiGoiY: 'Basic',
    fTenGoiTrong: 'Please enter a plan name.',
    fMaGoi: 'Plan code',
    fMaGoiGoiY: 'Leave blank to derive it from the name',
    fMaGoiMo:
      'Cannot be changed after creation, because subscription periods and the audit log reference the plan by code.',
    fMaGoiSai:
      'Use unaccented capital letters, digits and underscores; start with a letter; 2–40 characters.',
    fMoTa: 'Description',
    fMoTaGoiY: 'Who this plan is for and what it includes',
    fAnHan: 'Default grace period (days)',
    fAnHanMo:
      'After the end date, a tenant keeps access for this many more days. New periods copy the value at creation time, so changing it here does not alter periods already signed.',
    fAnHanSai: 'The grace period is a whole number of days from 0 to 365.',
    fDangBan: 'On sale',
    fDangBanMo:
      'Clear this and no new period can be created, while periods already running continue to the end.',
    fDichVu: 'Services in this plan',
    fDichVuLuonBat: '— always on',
    nutTaoGoi: 'Create plan',

    xacNhanXoaTieuDe: 'Delete plan {{ten}}?',
    xacNhanXoaThan:
      'This plan has never been used for a subscription period, so no tenant is affected. This cannot be undone.',
    nhanXacNhanXoa: 'Delete {{ten}}',

    rongThat: 'No service plans yet',
    rongThatMo: 'Create the first plan to sell to tenants.',
    rongLoc: 'No plan matches the filters',
    loiDanhSach: 'Could not load the plan list. {{loi}}',
    daThem: 'Service plan added.',
    daCapNhat: 'Changes saved.',
    daXoa: 'Service plan deleted.',

    danhMucCaption: 'Platform service catalogue and the matching module codes',
    danhMucMo:
      'The catalogue is created by migration and this screen is read-only. Adding a service requires a deploy: a navigation group in the Portal, a module code at the Gateway, and a migration.',
    cotDichVuTen: 'Service',
    cotMaModule: 'Module code at the Gateway',
    cotLoai: 'Type',
    loaiBatBuoc: 'Mandatory, always on',
    loaiTheoGoi: 'By plan',
    khongBaoGioChan: 'Never blocked',
    chuaGoiApi: 'Calls no API — only hidden in navigation',
    ghiChuVungDungChung:
      'When two services need the same module code (for example Trợ lý AI and Tri thức AI both use {{ma}}), that module is blocked only once both are off.',
    ghiChuAnMenu:
      'Turning a service off does more than hide a menu: the Gateway blocks the matching module code before a request reaches the application.',

    tinhTrang: {
      chuaCo: 'No period',
      chuaBatDau: 'Not started',
      hieuLuc: 'Active',
      sapHetHan: 'Expiring soon',
      anHan: 'In grace period',
      hetHan: 'Expired',
    },
  },

  thueBao: {
    tab: 'Plans & subscription',
    tieuDeThueBao: 'Current subscription',
    tieuDeDichVu: 'Tenant services',
    tieuDeLichSu: 'Subscription history',
    moDichVu:
      'Turning a service off also blocks its module code at the gateway, not just hides the navigation group.',

    dongGoi: 'Plan',
    dongKhoangNgay: 'Term',
    dongConLai: 'Remaining',
    dongAnHan: 'Grace period',
    dongHetAnHan: 'Grace ends',
    dongSoHopDong: 'Contract number',
    dongGhiChu: 'Note',
    khongThoiHan: 'No end date',
    conLaiNgay: '{{so}} days',
    anHanNgay: '{{so}} days',
    khongAnHan: 'No grace period',
    tuDen: '{{tu}} – {{den}}',
    tinhChoNgay: 'Calculated for {{ngay}}',
    chuaCoKy: 'This tenant has no subscription period yet',
    chuaCoKyMo: 'Create the first period so the tenant can use services beyond the mandatory ones.',

    bangDichVuCaption: 'Tenant services, with the reason each one is on or off',
    cotDichVu: 'Service',
    cotTrangThai: 'State',
    cotLyDo: 'Reason',
    cotGhiDe: 'Override',
    dangBat: 'On',
    dangTat: 'Off',
    khongGhiDe: 'Follows plan',
    ghiDeBatDen: 'On until {{den}}',
    ghiDeBatTu: 'On from {{tu}}',
    ghiDeTatDen: 'Off until {{den}}',
    ghiDeTatTu: 'Off from {{tu}}',
    ghiDeBatVoHan: 'On, no time limit',
    ghiDeTatVoHan: 'Off, no time limit',
    ghiDeChuaHieuLuc: 'Not in effect',
    vungBiChan: 'Modules blocked at the gateway',
    vungBiChanRong: 'No module is blocked',

    lyDo: {
      batBuoc: 'Mandatory',
      ghiDeBat: 'Override: on',
      ghiDeTat: 'Override: off',
      theoGoi: 'Follows plan',
      anHan: 'In grace period',
      ngoaiGoi: 'Not in plan',
      hetHan: 'Subscription expired',
      chuaBatDau: 'Subscription not started',
      chuaCo: 'No subscription',
    },

    bangLichSuCaption: 'Every subscription period of this tenant, including cancelled ones',
    cotKy: 'Period',
    cotTacNhan: 'Changed by',
    nhanDaHuy: 'Cancelled',
    nhanHieuLuc: 'Active',
    nhanHienHanh: 'In effect',
    huyLuc: 'Cancelled at {{luc}}',
    lichSuRong: 'No period yet',

    taoKy: 'Create subscription period',
    giaHan: 'Renew',
    doiGoi: 'Change plan',
    suaKy: 'Edit terms',
    huyKy: 'Cancel period',
    datGhiDe: 'Override service',
    suaGhiDe: 'Edit override',
    boGhiDe: 'Remove override',
    khongSuaKyDaHuy: 'Period is cancelled and cannot be edited',
    khongHuyKyDaHuy: 'This period was already cancelled',
    khongGhiDeBatBuoc: 'Mandatory service, always on — cannot be overridden',
    khongThaoTacLuuTru: 'Tenant is archived; its subscription can no longer be changed',

    dialogTaoKy: 'Create subscription period',
    dialogDoiGoi: 'Change plan mid-period',
    dialogSuaKy: 'Edit terms of period #{{id}}',
    fGoi: 'Service plan',
    fGoiChon: 'Select a plan',
    fGoiMo: 'Only plans on sale can start a new period.',
    fGoiTrong: 'Please select a plan.',
    fBatDau: 'Start date',
    fBatDauTrong: 'Please enter a start date.',
    fKetThuc: 'End date',
    fKetThucMo: 'Leave empty for no end date.',
    fKetThucSai: 'The end date must be on or after the start date.',
    fAnHan: 'Grace period (days)',
    fAnHanMo:
      "Leave empty to copy the plan's default. The value is copied into the period, so changing the plan later does not change this period's terms.",
    fAnHanSai: 'The grace period is a whole number of days from 0 to 365.',
    fSoHopDong: 'Contract number',
    fSoHopDongGoiY: 'HD-2026-017',
    fSoHopDongMo: 'A reference string, not an amount.',
    fGhiChu: 'Note',
    loiHuyKyChong: 'Cancel the overlapping period',
    loiDoiNgayBatDau: 'Start from the next day',
    fThayThe: 'Cut the running period to change plan',
    fThayTheMo:
      'The running period will end the day before the new one starts. Without this, overlapping dates are rejected.',
    canhBaoDoiGoi:
      'The new period takes effect on its start date. Services missing from the new plan are cut off.',
    nutTaoKy: 'Create period',
    nutLuuKy: 'Save changes',

    xacNhanHuyTieuDe: 'Cancel subscription period #{{id}}?',
    xacNhanHuyThan:
      'The tenant loses every service that came with this plan as soon as it is cancelled. The period stays in the history. This cannot be undone.',
    fLyDoHuy: 'Reason for cancelling',
    fLyDoHuyGoiY: 'Customer terminated the contract from 01/01/2027',
    fLyDoHuyTrong: 'Please give a reason for cancelling.',
    fLyDoHuyDai: 'The reason can be at most 500 characters.',
    nhanXacNhanHuy: 'Cancel period #{{id}}',

    dialogGhiDe: 'Override service {{ten}}',
    fCheDo: 'Mode',
    fCheDoBat: 'Turn the service on, regardless of plan',
    fCheDoTat: 'Turn the service off, regardless of plan',
    fTu: 'In effect from',
    fTuMo: 'Leave empty for no start limit.',
    fDen: 'In effect until',
    fDenMo: 'Leave empty for no end limit.',
    fDenSai: 'The end date must be on or after the start date.',
    fGhiChuGhiDe: 'Note',
    fGhiChuGhiDeGoiY: 'Three-month trial as agreed',
    moGhiDeBat:
      'An "on" override keeps the service running even after the subscription expires, because it is checked before the plan.',
    nutLuuGhiDe: 'Save override',

    xacNhanBoTieuDe: 'Remove the override for {{ten}}?',
    xacNhanBoThan:
      'The service goes back to following the current subscription plan, so it may end up on or off differently from now.',
    nhanXacNhanBo: 'Remove override',

    daTaoKy: 'Subscription period created.',
    daDoiGoi: 'Plan changed.',
    daCapNhatKy: 'Changes saved.',
    daHuyKy: 'Subscription period cancelled.',
    daGhiDeBat: 'Override saved: service on.',
    daGhiDeTat: 'Override saved: service off.',
    daBoGhiDe: 'Override removed.',

    loiTai: 'Could not load the services and subscription of this tenant. {{loi}}',

    fGoiBanDau: 'Service plan',
    fGoiBanDauKhong: 'None — mandatory services only',
    loiTaiGoi: 'Could not load the plan list. {{loi}}',
    daGanGoiBanDau: 'Plan {{goi}} assigned from {{ngay}}.',
    ganGoiBanDauLoi:
      'Tenant {{ten}} was created, but plan {{goi}} could not be assigned: {{loi}} Assign it again in the "Plans & subscription" tab.',
  },

  license: {
    tieuDe: 'On-premise licenses',
    moTa: 'Signed licenses for on-premise installations, requests awaiting approval, the keyring and the revocation list.',
    tab: 'License',
    tabDanhSach: 'Licenses',
    tabChoDuyet: 'Awaiting approval ({{so}})',
    tabKhoaKy: 'Signing keys and revocation',

    tkDangChay: 'Running',
    tkDangChaySub: 'Valid or in grace period',
    tkSapHetHan: 'Expiring soon',
    tkSapHetHanSub: 'Within the next {{so}} days',
    tkAnHan: 'In grace period',
    tkAnHanSub: 'Past expiry; read-only once grace ends',
    tkChoDuyet: 'Awaiting approval',
    tkChoDuyetSub: 'Needs a second approver',

    trangThai: {
      NOT_YET_VALID: 'Not yet valid',
      ACTIVE: 'Active',
      EXPIRING: 'Expiring soon',
      GRACE: 'In grace period',
      EXPIRED: 'Expired',
      REVOKED: 'Revoked',
      SUPERSEDED: 'Superseded',
    },
    trangThaiYeuCau: {
      DRAFT: 'Draft',
      PENDING_APPROVAL: 'Awaiting approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      ISSUED: 'Issued',
      CANCELLED: 'Cancelled',
    },
    trangThaiCaiDat: {
      PENDING: 'Awaiting license',
      ACTIVE: 'In use',
      REPLACED: 'Replaced',
      DECOMMISSIONED: 'Decommissioned',
    },
    trangThaiKhoa: {
      PREPUBLISHED: 'Prepublished',
      ACTIVE: 'Signing',
      RETIRED: 'Retired',
      COMPROMISED: 'Compromised',
    },
    kieu: {
      ISSUE: 'New',
      RENEW: 'Renewal',
      REPLACE: 'Installation change',
    },
    lyDoDuyet: {
      TERM_OVER_MAX: 'Term longer than 12 months',
      SPECIAL_FEATURES: 'Turns on special features',
      OVER_PLAN_QUOTA: 'Exceeds plan limits or adds modules outside the package',
      RENEW_BEFORE_WINDOW: 'Renewed before the renewal window opens',
    },
    loi: {
      trungCaiDat:
        'This installation_id is already registered with different content or to another tenant. Check the activation-request.json file the customer sent.',
      canDuyet: 'This license needs a second approver. Choose Send for approval instead of Issue license.',
      tuDuyet: 'The requester cannot approve or reject their own request. Ask another approver.',
      khongKyDuoc: 'The signing service is unavailable. Nothing was saved; try again in a few minutes.',
    },

    bangCaption: 'All on-premise licenses on the platform',
    locTatCa: 'All',
    locSapHetHan: 'Expiring within {{so}} days',
    locAnHan: 'In grace period',
    timTenant: 'Search by tenant name',
    cotTenant: 'Tenant',
    cotHetHan: 'Expires',
    cotAnHanToi: 'Grace until',
    cotPlan: 'Plan',
    cotCaiDat: 'Installation',
    cotLicense: 'License',
    cotHieuLuc: 'Valid',
    cotProductCode: 'Product',
    cotDangKyLuc: 'Registered',
    rongThat: 'No licenses yet',
    rongThatMo: 'Licenses are issued from the License tab of an on-premise tenant.',
    rongLoc: 'No licenses match the filter',
    xemTenant: 'View tenants',
    moTenant: 'Open tenant',
    loiTai: 'Unable to load license data: {{loi}}',
    conNgay: '{{so}} days left',
    quaHanNgay: '{{so}} days overdue',
    khoangNgay: '{{tu}} – {{den}}',

    xemNoiDung: 'View content',
    taiTep: 'Download file',
    thuHoi: 'Revoke',
    dong: 'Close',
    noiDungTieuDe: 'License content',
    noiDungYeuCauTieuDe: 'Request content',
    noiDungKy: 'Signed content',
    dongPlan: 'Plan (plan_code)',
    dongHieuLuc: 'Valid',
    dongAnHanToi: 'Grace until',
    dongCaiDat: 'installation_id',
    dongJti: 'jti',
    dongKid: 'Signing key (kid)',
    dongDigest: 'license_digest',
    dongCapLuc: 'Issued at',
    dongThuHoiLuc: 'Revoked at',
    dongTenant: 'Tenant',
    dongLoai: 'Type',
    dongLyDoCap: 'Reason for issuing',
    oModules: 'Modules',
    oMaxUsers: 'Maximum users',
    oMaxNodes: 'Maximum nodes',
    oGioiHan: 'Limit {{ten}}',
    oFeatures: 'Special features',
    khongCoFeature: 'No features turned on',
    thuHoiTieuDe: 'Revoke license {{ma}}?',
    thuHoiThan:
      'The Control Plane marks this license as revoked and adds it to the next revocation list. Installations stop using it only after loading that list. This cannot be undone: to use it again, issue a new license.',
    lyDoThuHoi: 'Reason for revoking',
    lyDoThuHoiViDu: 'For example: wrong number of users issued',
    nhanXacNhanThuHoi: 'Revoke license',
    loiThieuLyDoThuHoi: 'Enter a reason for revoking; it is recorded in the audit log.',
    daThuHoi: 'License revoked.',
    veTaiTieuDe: 'Download ticket ready',
    veTaiThan: 'The link works once and expires at {{luc}}. Every download is recorded in the audit log.',
    taiLicense: 'Download license',

    hieuLucTieuDe: 'Active license',
    hieuLucMo: 'The one the installation uses: the running license with the latest expiry',
    chuaCoHieuLuc: 'This tenant has no active license',
    chuaCoHieuLucMo: 'Issue a license once you receive activation-request.json from the customer’s installer.',
    giaHan: 'Renew',
    capLicense: 'Issue license',
    dangChoDuyet: '{{so}} request(s) for this tenant are awaiting approval.',
    thieuDauMoi:
      'This tenant has no contract owner contact (CONTRACT_OWNER): license expiry notices will reach no one. Add this contact for the tenant.',
    caiDatTieuDe: 'Installations',
    caiDatMo: 'On-premise installations registered with activation-request.json',
    caiDatCaption: 'On-premise installations of {{ten}}',
    caiDatRong: 'No installations yet',
    caiDatRongMo: 'An installation is recorded when you import activation-request.json in the license wizard.',
    dongThoiGianTieuDe: 'License timeline',
    dongThoiGianMo: 'Each renewal chain runs from the newest license back to the original',
    dongThoiGianCaption: 'License history of {{ten}}',
    dongThoiGianRong: 'No licenses issued yet',
    thayCho: 'replaces {{ma}}',

    trinhCapTieuDe: 'Issue a license for {{ten}}',
    trinhGiaHanTieuDe: 'Renew the license for {{ten}}',
    buocCaiDat: 'Installation',
    buocNoiDung: 'Content',
    buocThoiHan: 'Term',
    buocXemLai: 'Review',
    tiepTuc: 'Continue',
    nguonTep: 'New installation',
    nguonCoSan: 'Existing installation',
    keoThaTep: 'Drop activation-request.json here, or click to choose a file',
    keoThaTepMo: 'The file is read in your browser; nothing is sent to the server until you select Continue.',
    dongTep: 'File',
    dongProductCode: 'product_code',
    dongPhienBan: 'Version',
    dongDigestKichHoat: 'SHA-256 (canonical JSON)',
    tenCaiDat: 'Installation name',
    tenCaiDatGoiY: 'A name to recognise the installation, such as the server location.',
    chonCaiDat: 'Choose an installation',
    seThayThe: 'This installation differs from the one on the active license, so the new license replaces the old one (REPLACE).',
    tepJsonHong: 'The file is not valid JSON. Choose the activation-request.json file the installer generated.',
    tepKhongPhaiDoiTuong: 'The file must be a JSON object. Choose the activation-request.json file the installer generated.',
    tepThieuTruong: 'The file is missing the {{truong}} field. Ask the customer to rerun the installer to generate a new file.',
    tepSaiUuid: 'The {{truong}} field is not a UUID. Ask the customer to rerun the installer to generate a new file.',
    tepKhacTenant: 'This file belongs to another tenant ({{ma}}). Check the file the customer sent.',
    tepKhongDocDuoc: 'Unable to read the file. Choose the file again and retry.',
    loiChuaCoTep: 'Choose an activation-request.json file to continue.',
    cachNhapNhan: 'How the activation request arrives',
    cachNhapTep: 'Upload file',
    cachNhapMa: 'Paste activation code',
    maKichHoat: 'Activation code',
    maKichHoatGoiY:
      'The installer prints this code next to activation-request.json; it starts with FXA1. The code is read in your browser; nothing is sent to the server until you select Continue.',
    maKichHoatViDu: 'FXA1.eyJjcmVhdGVkX2F0Ijoi...',
    loiChuaCoMa: 'Paste the activation code the customer sent to continue.',
    maSaiTienTo: 'The activation code must start with FXA1. Copy the whole line the installer printed.',
    maBase64Hong: 'The activation code is incomplete or contains stray characters. Copy the whole line without leaving anything out.',
    maKhongPhaiYeuCau: 'This code does not contain an activation request. Ask the customer to rerun the installer to get a new code.',
    maThieuTruong: 'The activation code is missing the {{truong}} field. Ask the customer to rerun the installer to get a new code.',
    maSaiUuid: 'The {{truong}} field in the activation code is not a UUID. Ask the customer to rerun the installer to get a new code.',
    maKhacTenant: 'This activation code belongs to another tenant ({{ma}}). Check the code the customer sent, or open the wizard from the right tenant.',
    maKhongDocDuoc: 'Unable to read the activation code. Paste it again and retry.',
    loiChuaChonCaiDat: 'Choose an installation to continue.',
    loiChuaChonPlan: 'Choose a plan_code to build the license content.',
    goiXuatPhat: 'Service package',
    goiXuatPhatGoiY: 'Modules are prefilled from the services in the package.',
    khongTheoGoi: 'No package',
    planCode: 'plan_code',
    chonPlan: 'Choose a plan_code',
    loiMau: 'Unable to build content from the package: {{loi}}',
    noiDungMo: 'Content is prefilled from the package. Change only what the contract differs on; changed fields are marked for the approver.',
    khacGoi: 'Differs from package',
    giaTriGoi: 'Package sets {{gt}}',
    loiOTrong: 'Enter the contract value: this plan has no default.',
    featuresGoiY: 'Type a feature name and press Enter.',
    thoiHan: 'Term',
    soThang: '{{so}} months',
    ngayCuThe: 'Specific date',
    ngayHetHan: 'Expiry date',
    tomTatThoiHan: 'Valid {{tu}} – {{den}}, grace until {{anHan}}.',
    giaHanMo: 'Content is copied from the current license; the new license starts exactly when the old one expires. Choose a term only.',
    canDuyetTieuDe: 'This license needs a second approver before signing:',
    khongCanDuyet: 'No approval needed: the license is signed as soon as you select Issue license.',
    lyDoCap: 'Reason for issuing',
    lyDoCapGoiY: 'Include the contract or appendix number so the approver can check it.',
    lyDoCapViDu: 'For example: Contract no. 12/2026/HĐ-FOXAI',
    loiThieuLyDoCap: 'Enter a reason for issuing; it is recorded in the audit log.',
    guiDuyet: 'Send for approval',
    daCap: 'License issued.',
    daGuiDuyet: 'Request sent for approval.',

    hangChoCaption: 'License requests awaiting approval',
    hangChoRong: 'No requests awaiting approval',
    hangChoRongMo: 'Requests land here when the term exceeds 12 months, a special feature is on, or plan limits are exceeded.',
    cotThoiHan: 'Term',
    cotVaoHangCho: 'Needs approval because',
    cotLyDoCap: 'Reason for issuing',
    cotNguoiGui: 'Requested by',
    ban: 'You',
    duyetVaKy: 'Approve and sign',
    tuChoi: 'Reject',
    huyYeuCau: 'Cancel request',
    duyetTieuDe: 'Approve and sign the license for {{ten}}?',
    duyetThan: 'The license is signed right after approval and the issuer can download it. To withdraw a signed license, revoke it.',
    tuChoiTieuDe: 'Reject the request for {{ten}}?',
    tuChoiThan: 'The requester sees the reason. A rejected request cannot be reopened; issuing requires a new request.',
    lyDoTuChoi: 'Reason for rejecting',
    nhanXacNhanTuChoi: 'Reject request',
    loiThieuLyDoTuChoi: 'Enter a reason so the requester knows what to fix.',
    daDuyet: 'License approved and signed.',
    daTuChoi: 'Request rejected.',
    daHuyYeuCau: 'Request cancelled.',
    huyYeuCauTieuDe: 'Cancel the license request for {{ten}}?',
    huyYeuCauThan:
      'The request moves to Cancelled and leaves the approval queue; no license can be signed from it. This cannot be undone: issuing again needs a new request.',
    nhanXacNhanHuy: 'Cancel request',

    keyringTieuDe: 'Current keyring',
    keyringMo: 'Public keys installations use to verify license signatures',
    keyringCaption: 'Signing keys in the current keyring',
    keyringRong: 'The keyring has no keys',
    thuHoiDsTieuDe: 'Latest revocation list',
    thuHoiDsMo: 'Installations reject any license whose jti is on this list',
    thuHoiDsRong: 'No licenses revoked yet.',
    taiKeyring: 'Download keyring',
    taiDsThuHoi: 'Download revocation list',
    taiKeyringLen: 'Upload keyring',
    taiKeyringLenTieuDe: 'Upload a new keyring?',
    taiKeyringLenMo:
      'Choose the keyring file signed with the root key on the offline machine (root key ceremony). license-service checks the root key signature and each kid\'s public key before accepting it.',
    tepKeyring: 'Keyring file (.jws)',
    keyringKeoTha: 'Drag the keyring file here or click to choose',
    keyringKeoThaMo: 'The file is only read in the browser for preview; nothing is sent until you upload.',
    dongKyBoi: 'Signed by root key',
    dongCacKhoa: 'Keys',
    sequenceDoi: '{{cu}} → {{moi}}',
    sequenceDauTien: '{{moi}} (first keyring)',
    taiKeyringLenHauQua:
      'Once accepted, issuing key statuses follow this keyring (only ACTIVE keys can sign licenses) and keyrings with sequence {{sequence}} or lower can no longer be uploaded. This cannot be undone.',
    nhanTaiKeyringLen: 'Upload keyring sequence {{sequence}}',
    dangTaiKeyringLen: 'Uploading...',
    daTaiKeyringLen: 'Uploaded keyring sequence {{sequence}}.',
    loiKeyringChuaChon: 'Choose a keyring file before uploading.',
    loiKeyringTepLon: 'The file is larger than 64 KB, so it is not a keyring. Choose the .jws file signed at the root key ceremony.',
    loiKeyringKhongPhai: 'This file is not a keyring (JWS typ=KEYRING). Choose the keyring-<sequence>.jws file signed at the root key ceremony.',
    loiKeyringKhongCoKhoa: 'The keyring has no keys. Rewrite keyring.json with every key still needed for verification and sign it again.',
    loiKeyringSequence:
      'The file\'s sequence is not greater than the current one ({{hienHanh}}). Sign the keyring again with a sequence greater than {{hienHanh}}.',
    keyringChuaCo: 'No keyring yet',
    thuHoiDsChuaCo: 'No revocation list yet',
    thuHoiDsChuaCoMo: 'The list is signed the first time a license is revoked.',
    keyringChuaCoMo:
      'No license can be signed until the first keyring is in place. A license approver signs a keyring with the root key at the ceremony, then uploads it with Upload keyring.',
    dongSequence: 'sequence',
    dongPhatHanhLuc: 'Issued',
    dongSoThuHoi: 'Revoked licenses',
    cotKid: 'kid',
    cotDungTu: 'Valid from',
    cotNghiSau: 'Retire after',
    khongDocDuocTep: 'Unable to read the signed file',
    khongDocDuocTepMo: 'The returned file is not a valid JWS. Report it to the license-service operators.',
    khongRoLicense: 'Not in the licenses currently shown',
  },

  // ─────────── Module management (plugin platform) — ADR-0045, 0046, 0086 ───────────
  quanLyModule: {
    danhSachTieuDe: 'Module management',
    danhSachMoTa: 'Extension modules submitted by FoxAI or partners, checked and approved before a tenant can install them',
    bangCaption: 'List of modules submitted to the platform',
    cotTen: 'Module name',
    cotNhaPhatTrien: 'Developer',
    cotPhienBan: 'Version',
    cotCapNhat: 'Updated',
    cotNopLuc: 'Submitted at',
    cotDuyetLuc: 'Reviewed at',
    cotGhiChu: 'Note',
    timTheo: 'Search by name, code or developer',
    xemChiTiet: 'View details',
    xemChiTietCua: 'View details of {{ten}}',
    nopGoi: 'Submit module',
    duyetNut: 'Approve',
    loiDanhSach: 'Could not load the module list: {{loi}}',
    loiChiTiet: 'Could not load this module',
    rongThat: 'No module submitted yet',
    rongThatMo: 'Submit the first module to get started',
    rongLoc: 'No module matches the filter',
    tabDanhSach: 'Modules',
    moTaiLieu: 'Guide',
    moTaiLieuMo: 'Open the module packaging and submission guide in a new tab',

    trangThai: {
      DRAFT: 'Draft',
      IN_REVIEW: 'Pending review',
      PUBLISHED: 'Approved',
      REJECTED: 'Rejected',
      DEPRECATED: 'Deprecated',
    },

    khoiMetadata: 'Module information',
    khoiPhienBanMoiNhat: 'Latest version',
    khoiLichSu: 'Version history',
    lichSuCaption: 'History of versions submitted for this module',
    phienBanDaDuyet: 'Approved version',
    giaoDien: {
      diemVao: 'Bundled interface',
      khongCo: 'None, the module only runs on the server',
      manHinh: 'Screens (menus)',
    },
    xemTruoc: {
      tieuDe: 'Interface preview · version {{phienBan}}',
      mo: "Runs the exact code of the approved module, as tenants will see it after installing.",
      chonMan: "Choose a module screen",
      canhBao:
        "The screen runs with your session. Platform accounts don't belong to a tenant, so tenant-data APIs the module calls may fail; you can still try the module's actions.",
    },
    khongCoManifestChiTiet:
      'No API yet returns the manifest content (required permissions, SDK version) of a version that has not been published — check the submitted module directly.',

    maLoiKiem: {
      MODULE_PACKAGE_INVALID: 'Invalid module layout: missing module.yaml, or a corrupted zip',
      MODULE_MANIFEST_INVALID: 'Invalid manifest',
      MODULE_VERSION_CONFLICT:
        'The version must be higher than the released one (for example 0.1.0 to 0.2.0), or the module code already belongs to someone else. Change the version or code in module.yaml and submit again',
      MODULE_SCAN_FAILED: 'Image has critical-severity vulnerabilities',
      MODULE_MIGRATION_INVALID: "Module's migration failed a dry run",
    },

    daDuyet: 'Module approved (version {{phienBan}})',
    daTuChoi: 'Module rejected (version {{phienBan}})',
    xoaMuc: 'Delete module',
    xoaTieuDe: 'Delete module {{ten}}?',
    xoaThan:
      'Every submitted version is deleted and the module disappears from the tenants’ Approved modules list. This cannot be undone. If any tenant still has it installed, ask them to uninstall it first.',
    xoaNhan: 'Delete module {{ten}}',
    daXoa: 'Deleted module {{ten}}.',

    upload: {
      tieuDe: 'Submit module',
      mo: 'Choose the .zip that platform-module pack created, with module.yaml and the frontend/ folder. All module details come from the manifest; nothing to re-enter here.',
      canhBaoBaoMat:
        "The platform checks the manifest before approval, but the module still runs the submitter's code. Read the code in frontend/ before approving.",
      chonTep: 'Choose a .zip file or drag and drop here',
      dieuKien: '.zip file, up to {{toiDa}}',
      saiDinhDang: 'Only .zip files are accepted',
      quaLon: 'File exceeds {{toiDa}}',
      nopGoi: 'Submit module',
      ketQua: 'Module {{code}} received, version {{phienBan}}',
      daVaoHangDoi: 'The module is now in the approval queue. The Platform Team will review the code and approve or reject it.',
      moTenant: "Choose the module's .zip file. Name, version and permissions are read from the file; nothing to re-enter.",
      canhBaoTenant: "The module runs with the signed-in user's account. Only submit modules from sources you trust.",
      daDuyetNgay: 'The module is approved and can be installed from the Approved modules tab.',
      daVaoHangDoiTenant:
        'Sent to your organization admin for approval. The running version (if any) keeps working until the new one is approved. Track it in the My submissions tab.',
    },

    tuChoi: {
      tieuDe: 'Reject version {{phienBan}}',
      mo: 'The reason will be sent to the submitter so they can fix it and resubmit.',
      oNhap: 'Rejection reason',
      nut: 'Reject',
    },

    duyetXacNhan: {
      tieuDe: 'Approve version {{phienBan}}?',
      than:
        'The module goes live on the Marketplace immediately, visible and installable by every tenant. The previous PUBLISHED version (if any) becomes DEPRECATED.',
      nut: 'Approve version {{phienBan}}',
    },
  },

  // ─────────── Tenant portal Modules screen — ADR-0096 §8 ───────────
  moduleToChuc: {
    tieuDe: 'Module list',
    moTa: 'Submit, view and install modules for your organization.',
    nopGoi: 'Submit module',
    tabDaDuyet: 'Approved modules',
    tabGoiCuaToi: 'My submissions',
    boKhoiTao: {
      tab: 'Developer kits',
      bang: 'Module starter kits',
      gioiThieu:
        'Download the starter kit of a system module to build your own extension module: a sample UI project plus the permissions and API of that module. The kit does not contain the module source code.',
      cacBuoc:
        'Unzip it, follow the README inside (install dependencies, set your module code, pack it), then select "Submit module" to send yours.',
      phienBanSdk: 'SDK {{phienBan}}',
      cotQuyenApi: 'Permissions and APIs',
      soQuyenApi: '{{quyen}} permissions · {{api}} APIs',
      cotDungLuong: 'Size',
      tai: 'Download starter kit',
      daTai: 'Downloaded the {{ten}} starter kit.',
      rong: 'No starter kits yet',
      rongMo: 'This version has not published any starter kits. Contact the platform administrators.',
      loiTai: "Couldn't load the starter kits: {{loi}}",
    },
    bangDaDuyet: 'Approved modules',
    bangGoiCuaToi: 'Modules you submitted',
    cotModule: 'Module',
    cotNhaPhatTrien: 'Vendor',
    cotPhienBan: 'Version',
    cotTrangThai: 'Status',
    cotNopLuc: 'Submitted',
    cotGhiChu: 'Note',
    trangThaiCai: {
      daCai: 'Installed',
      dangCai: 'Installing...',
      dangGo: 'Uninstalling...',
      loi: 'Install failed',
      chuaCai: 'Not installed',
    },
    thaoTac: {
      mo: 'Open module',
      cai: 'Install module',
      caiLai: 'Reinstall module',
      go: 'Uninstall module',
      xoa: 'Delete module',
    },
    daGuiCai: 'Installing {{ten}}. It will appear in the menu when done.',
    loiCai: "Couldn't install {{ten}}: {{loi}}",
    xnGoTieuDe: 'Uninstall {{ten}}?',
    xnGoThan: "The module disappears from everyone's menu in your organization. Its data is kept, not deleted.",
    daGuiGo: 'Uninstalling {{ten}}. It will leave the menu when done.',
    loiGo: "Couldn't uninstall {{ten}}: {{loi}}",
    xnXoaTieuDe: 'Delete {{ten}} from the Marketplace?',
    xnXoaThan: 'Every version you submitted of this module is deleted, and no organization can install it. This can’t be undone.',
    daXoa: 'Deleted {{ten}} from the Marketplace.',
    loiXoa: "Couldn't delete {{ten}}: {{loi}}",
    loiTai: "Couldn't load the list: {{loi}}",
    rongDaDuyet: 'No approved modules yet',
    rongDaDuyetMo: 'Approved modules show up here, ready to install.',
    rongGoiCuaToi: "You haven't submitted any modules",
    rongGoiCuaToiMo: 'Zip your module following the guide, then select Submit module.',
    choDuyet: {
      tab: 'Pending approval',
      tabSoLuong: 'Pending approval ({{so}})',
      bang: 'Organization module versions and update history',
      moTa:
        'Modules submitted by people in your organization can only be used after you approve them. Approving a new version marks the old one as "Deprecated", and it stays in the history; organizations that installed the old version upgrade from the Approved modules tab.',
      cotNguoiNop: 'Submitted by',
      cotNguoiDuyet: 'Reviewed by',
      cotDuyetLuc: 'Reviewed at',
      thaoTacDuyet: 'Approve version',
      thaoTacTuChoi: 'Reject version',
      xnDuyetTieuDe: 'Approve {{ten}} version {{phienBan}}?',
      xnDuyetThan:
        'This becomes the organization\'s released version. The previously released version is marked "Deprecated" and stays in the history. Anyone allowed to install modules can install or upgrade to it.',
      nutDuyet: 'Approve version {{phienBan}}',
      daDuyet: 'Approved {{ten}} version {{phienBan}}.',
      daTuChoi: 'Rejected {{ten}} version {{phienBan}}.',
      rong: 'No versions yet',
      rongMo: 'When people in your organization submit modules, versions awaiting approval show up here.',
      loiTai: 'Could not load the approval list: {{loi}}',
    },
  },

  // ─────────── Installed module screens, loaded via Module Federation — ADR-0096 ───────────
  moduleDaCai: {
    dangTai: 'Loading module interface...',
    thuLai: 'Try again',
    loiDanhSach: {
      tieuDe: "Couldn't load installed modules",
    },
    chuaCai: {
      tieuDe: "This module isn't installed for your organization",
      mo: 'Install module {{code}} from the Module list, then open this screen again.',
    },
    khongGiaoDien: {
      tieuDe: '{{ten}} has no interface',
      mo: 'This module has no screen to show.',
    },
    loiNap: {
      tieuDe: "Couldn't load the {{ten}} interface",
      mo: 'Try again in a few minutes. If it still fails, contact the module vendor.',
    },
    loiChay: {
      tieuDe: '{{ten}} ran into an error',
      mo: 'Other screens still work. Try again; if it still fails, contact the module vendor.',
    },
  },

  // ─────────── On-premise installation license — ADR-0059 §6, §8 "Customer side" ───────────
  licenseCucBo: {
    tieuDe: 'Installation license',
    moTa: 'License status, enabled modules and usage of each limit for this on-premise installation.',
    chiOnPremise: 'This screen is only available on on-premise installations',
    chiOnPremiseMo: 'On SaaS the platform manages licenses; there is nothing to import here.',
    taiYeuCau: 'Download activation-request.json',
    dangTaiYeuCau: 'Downloading...',
    loiTai: 'Unable to load the license status: {{loi}}',
    trangThai: {
      ACTIVE: 'Active',
      EXPIRING: 'Expiring',
      GRACE: 'In grace period',
      READ_ONLY: 'Read-only',
      NOT_YET_VALID: 'Not yet valid',
      INVALID: 'Invalid',
    },
    conLai: {
      ACTIVE: '{{so}} days until expiry.',
      EXPIRING: '{{so}} days until expiry. Send a renewal request to the vendor now.',
      GRACE: 'Expired; {{so}} days of grace left before the system turns read-only.',
      READ_ONLY: 'Grace period over: only reading, signing in, backup and data export work.',
      NOT_YET_VALID: '{{so}} days until the license takes effect.',
      INVALID: 'No valid license: only the license screen and data restore are available.',
    },
    thanhBen: {
      goi: '{{goi}} plan',
      conNgay: '{{so}} days left',
      conMotNgay: '1 day left',
      anHanNgay: '{{so}} days of grace left',
      anHanMotNgay: '1 day of grace left',
      hieuLucSau: 'Takes effect in {{so}} days',
      hieuLucSauMotNgay: 'Takes effect in 1 day',
      chiDoc: 'Expired, read-only',
      khongHopLe: 'License is invalid',
      chuaKichHoat: 'License not activated',
      loiTai: 'Couldn’t load the license',
      tomTat: '{{goi}} plan license: {{trangThai}}, {{demNguoc}}',
    },
    chuaCoLicense: 'No license imported yet',
    lyDo: 'Reason code: {{ma}}',
    dongHoTieuDe: 'The server clock went back more than 5 minutes',
    dongHoThan:
      'The license still counts from the latest time ever seen, so turning the clock back does not extend it. Check the server time sync (NTP).',

    thongTinTieuDe: 'License details',
    dongGoi: 'Plan',
    dongHieuLuc: 'Valid',
    dongAnHanToi: 'Grace until',
    dongCaiDat: 'installation_id',
    dongTenant: 'tenant_id',
    dongMa: 'License ID (jti)',
    dongKeyring: 'Keyring',
    dongThuHoi: 'Revocation list',
    soThuTu: 'sequence {{so}}',
    chuaNhap: 'Not imported',

    moduleTieuDe: 'Enabled modules',
    moduleMo: 'Modules missing from the license are not loaded.',
    moduleRong: 'No modules yet',
    tinhNang: 'Features',

    mucDungTieuDe: 'Usage',
    mucDungMo: 'Limits in the license and what this installation uses.',
    mucDungCaption: 'Usage of each limit in the license',
    mucDungRong: 'No limits yet',
    mucDungRongMo: 'Limits appear here after a license is imported.',
    cotGioiHan: 'Limit',
    cotMucDung: 'In use',
    cotTiLe: 'Share',
    mucDungNguoiDung: '{{dung}}/{{toiDa}} users',
    mucDungChung: '{{dung}}/{{toiDa}}',
    chuaDo: 'Not measured (max {{toiDa}})',
    gioiHan: {
      max_users: 'Users',
      max_nodes: 'Nodes',
      storage_gb: 'Storage (GB)',
    },

    nhapTieuDe: 'Import a file',
    nhapMo: 'A license, keyring or revocation list from the vendor. The signature is checked before the file is saved.',
    keoTha: 'Drop a file here or click to choose',
    keoThaMo: 'A vendor-signed file, up to 64 KB',
    dongTep: 'File',
    loaiTepNhan: 'File type',
    loaiTepGoiY: 'Detected from the file content; change it only if the vendor says so.',
    loaiTep: {
      license: 'License',
      keyring: 'Keyring',
      revocation: 'Revocation list',
    },
    nhanNhap: 'Import file',
    dangNhap: 'Importing...',
    loiChuaChonTep: 'Choose the file from the vendor before importing.',
    loiTepLon: 'The file is larger than 64 KB, so it is not a license file. Use the file the vendor sent.',
    loiKhongNhanRa: 'The file type was not recognised. Choose the type and import; if it still fails, ask the vendor to resend the file.',
    cachNhapNhan: 'Import method',
    cachNhapTep: 'Upload file',
    cachNhapDan: 'Paste content',
    noiDungDan: 'License content',
    noiDungDanGoiY:
      'Paste the whole string the vendor sent: a license, keyring or revocation list. Spaces and line breaks are ignored.',
    noiDungDanViDu: 'eyJhbGciOiJFZERTQSIsInR5cCI6IkxJQyJ9...',
    nhanNhapDan: 'Import content',
    loiChuaDan: 'Paste the content from the vendor before importing.',
    loiDanLon: 'The content is larger than 64 KB, so it is not a license. Paste the string the vendor sent.',
    loiDanSaiDang:
      'This is not a signed license (it needs three parts joined by dots). Copy the whole string the vendor sent without leaving anything out.',
    daNhap: {
      license: 'License imported.',
      keyring: 'Keyring imported.',
      revocation: 'Revocation list imported.',
    },

    giaHanTieuDe: 'Renew or change the license',
    giaHanMo: 'Renewals, extra modules and higher limits all come as a new license from the vendor.',
    giaHanBuoc1: 'Download activation-request.json for this installation.',
    giaHanBuoc2:
      'Send it to the vendor with your contract number. It only holds the installation ID, tenant, product, version and creation date — no business data.',
    giaHanBuoc3: 'Import the license file you receive in the Import a file panel. The new license takes effect on import.',
    giaHanNhac: 'Send it at least 45 days before expiry so the vendor has time to issue.',

    bang: {
      EXPIRING: 'The installation license expires in {{so}} days. Send activation-request.json to the vendor to renew.',
      GRACE: 'The license has expired; {{so}} days of grace remain. After that the system becomes read-only.',
      READ_ONLY: 'The license grace period is over, so the system is read-only. Import a renewed license to write data again.',
      NOT_YET_VALID: 'The license is not valid yet ({{so}} days to go), so data cannot be written.',
      INVALID: 'This installation has no valid license, so only the license screen and data restore are available.',
    },
    moManLicense: 'Open license screen',
  },

  // ─────────── Settings form built from the settings registry — ADR-0059 §1, §8 ───────────
  caiDat: {
    'auth.chi_email_domain_xac_minh': {
      nhan: 'Only accept email on verified domains',
    },
    'auth.mfa_required': {
      nhan: 'Require two-factor authentication (MFA)',
      moTa: 'Users without an authenticator app must set one up before they can sign in.',
    },
    'auth.password_min_length': {
      nhan: 'Minimum password length',
    },
    'auth.password_max_length': {
      nhan: 'Maximum password length',
    },
    'auth.password_history': {
      nhan: 'Block reuse of recent passwords',
    },
    'auth.bruteforce_max_failures': {
      nhan: 'Failed attempts before lockout',
    },
    'auth.bruteforce_lock_minutes': {
      nhan: 'Account lockout duration',
    },
    'auth.password_blacklist_enabled': {
      nhan: 'Block breached passwords',
    },
    'network.ip_allowlist': {
      nhan: 'Trusted range',
    },
    'network.mfa_ngoai_dai': {
      nhan: 'Require a one-time code outside the trusted range',
      moTa: 'Applies even when two-factor authentication is not required.',
    },
    'network.auto_block_minutes': {
      nhan: 'Temporary block for repeated failed sign-ins',
    },
    'audit.allow_sample_rate': {
      nhan: 'Logging rate for allowed requests',
    },
    'audit.read_sample_rate': {
      nhan: 'Logging rate for read actions',
    },
    'audit.login_error_burst_per_ip': {
      nhan: 'Failed sign-in threshold per IP',
      moTa: 'Above this, a security alert is sent and the IP is blocked temporarily.',
    },
    'audit.alert_rules': {
      nhan: 'Alert rules',
    },
    'log.debug_until': {
      nhan: 'Detailed (DEBUG) logging until',
    },
    'notify.security_critical': {
      nhan: 'Critical security alerts',
    },
    'notify.ops_warning': {
      nhan: 'Operations warnings',
    },
    'notify.tenant_security': {
      nhan: 'Tenant security alerts',
    },
    'notify.audit_alert': {
      nhan: 'Rule-based audit alerts',
    },
    'notify.license_expiry': {
      nhan: 'License expiring soon',
    },
    'ai_quota.uu_tien': {
      nhan: 'AI token usage order',
      moTa: 'Resource-plan tokens not used within the month expire at the end of the month.',
      HET_HAN_TRUOC: 'Expiring first',
      MUA_THEM_TRUOC: 'Purchased tokens first',
    },
    'notify.ai_quota': {
      nhan: 'AI tokens running low or used up',
    },
    'notify.ai_token_request': {
      nhan: 'Top-up requests',
    },
    'notify.ai_token_decision': {
      nhan: 'Top-up request decisions',
    },
    'contact.types': {
      nhan: 'Contact types',
      moTa: 'Uppercase letters and underscores, e.g. ADMIN, SECURITY.',
    },
    'license.default_term_months': {
      nhan: 'Default license term',
    },
    'license.grace_days': {
      nhan: 'Grace period',
    },
    'license.renewal_window_days': {
      nhan: 'Renewal opens',
    },
    'license.notify_days': {
      nhan: 'License expiry reminders',
    },
    'license.approval.max_term_months': {
      nhan: 'Longest term without approval',
    },
    'license.approval.over_plan_quota': {
      nhan: 'Approve licenses above plan quota',
    },
    'license.approval.special_features': {
      nhan: 'Features that need approval',
      moTa: '* means every feature.',
    },

    nguon: {
      macDinh: 'Default',
      nenTang: 'Set by the platform',
      theoNenTang: 'From the platform',
      goi: 'From the plan',
      theoGoi: 'From the {{goi}} plan',
      tenant: 'Set by the organisation',
      dangSua: 'Editing',
      veKeThua: 'Reset to default',
      seLay: 'After saving: {{nguon}}',
    },
    bien: {
      tuDen: '{{min}} to {{max}}',
      tuSanDen: '{{min}} (platform minimum) to {{max}}',
      tuDenTheoGoi: '{{min}} to {{max}} on the current plan',
      tu: 'At least {{min}}',
      den: 'At most {{max}}',
      denTheoGoi: 'At most {{max}} on the current plan',
      tuDenToiDaPhanTu: 'Each value {{min}} to {{max}}, up to {{so}} values',
      toiDaDong: 'Up to {{so}} rows',
      toiDaPhut: 'Up to {{so}} minutes from now',
      toiDaNguoiNhan: 'Up to {{so}} recipients',
      toiDaQuyTac: 'Up to {{so}} rules',
    },
    donVi: {
      phut: 'minutes',
      lan: 'attempts',
      kyTu: 'characters',
      ngay: 'days',
      thang: 'months',
      suKienS: 'events/s',
    },
    loi: {
      kieu_sai: 'Enter a value of the right type for this field.',
      khong_phai_so_nguyen: 'Enter a whole number.',
      nho_hon_min: 'Enter {{min}} or more.',
      lon_hon_max: 'Enter no more than {{max}}.',
      ngoai_danh_sach: 'Choose a value from the list.',
      qua_so_dong: 'Remove some rows: the list holds up to {{max}}.',
      qua_so_nguoi_nhan: 'Remove some recipients: up to {{max}} addresses. Use a group mailbox to reach more people.',
      cidr_sai: 'Row {{dong}}: change "{{cidr}}" to an IP address or CIDR range, such as 10.0.0.0/24.',
      cidr_trung: 'Row {{dong}}: {{cidr}} is already on another row. Delete one of them.',
      cidr_tat_ca: 'Row {{dong}}: you cannot block every address (/0). Enter a narrower range.',
      dong_ip_sai: '{{so}} lines are not valid yet. Fix the lines listed right below the box before saving.',
      thoi_diem_qua_khu: 'Choose a time in the future.',
      thoi_diem_qua_xa: 'Choose a time within the next {{phut}} minutes.',
      email_sai: 'Change "{{email}}" to a full email address, such as secops@company.com.',
      sai_dinh_dang: 'Change "{{giaTri}}" to match this field’s format.',
      thieu_kenh: 'Choose at least one channel.',
      chuoi_rong: 'Remove the empty item from the list.',
      chi_duoc_siet: 'A tenant can only lower or keep the inherited value ({{gioiHan}}).',
      chi_duoc_siet_bat: 'The plan or platform has turned this on; a tenant cannot turn it off.',
      qua_so_quy_tac: 'Delete some rules: up to {{max}} rules.',
      quy_tac_sai: 'Rule {{dong}} ("{{ten}}"): choose Edit and check the {{truong}} field.',
    },
    bat: 'On',
    tat: 'Off',
    khongDat: 'Not set',
    danhSachRong: 'Empty list',
    soDong: '{{so}} rows',
    soQuyTac: '{{so}} rules',
    tomTatTuyen: '{{kenh}} · {{so}} recipients · from {{muc}}',
    danhSachSoViDu: 'For example: 45, 30, 7, 1',
    danhSachChuoiViDu: 'Type, then press Enter',
    chuaLuu: 'Unsaved',
    chiXem: 'Read-only',
    hoanTac: 'Undo',
    veMacDinh: 'Reset to default',
    veMacDinhMo: 'Remove the value set here and use {{nguon}}: {{giaTri}}',
    veKeThuaGhiChu: 'Removes the value set at this level and uses the inherited value.',
    khoaLyDo: {
      khongQuyen: 'Your account can view security settings but not change them.',
      tangKhac: 'Your role can’t change this setting at this level.',
      nenTangQuanLy: 'The platform manages this setting for every organisation; organisations can only view it.',
      biEpEmailNgoaiDomain: 'Required because the organisation accepts email outside its verified domains.',
    },
    nhom: {
      xacThuc: 'Authentication',
      ghiNhatKy: 'Audit logging',
      phatHienDo: 'Password-spraying detection',
      yeuCauMatKhau: 'Password requirements',
      khoaKhiSai: 'Lockout after failed sign-ins',
      goLoi: 'Debugging',
    },
    kenh: { EMAIL: 'Email', CHAT: 'Chat', PORTAL: 'In the portal' },
    mucDo: { INFO: 'Info', WARNING: 'Warning', CRITICAL: 'Critical' },
    tuyen: {
      nguoiNhan: 'Recipients',
      nguoiNhanViDu: 'secops@company.com — type, then press Enter',
      mucToiThieu: 'Send from level',
      kenh: 'Channels',
      chuaCoNguoiNhan: 'No recipients: alerts only appear in the admin portal.',
    },
    cidr: {
      phamViMot: '1 address',
      phamViKhoang: '{{dau}} – {{cuoi}}',
      phamViSo: '{{so}} addresses',
      phamViDaiLon: 'IPv6 range /{{tienTo}}',
      cotCidr: 'CIDR',
      daHetHan: 'Expired',
      chuaIpCuaBan: 'Contains your IP',
    },
    thayDoi: {
      them: 'Added:',
      bo: 'Removed:',
      chiDoiGhiChu: 'Only notes or expiry dates changed.',
      sua: 'Changed:',
      chiDoiThuTu: 'Only the rule order changed.',
      thanh: 'to',
    },
    chuaCoThayDoi: 'No changes yet.',
    coThayDoi: '{{so}} unsaved changes.',
    suaLoiTruoc: 'Fix {{so}} fields with errors before saving.',
    boThayDoi: 'Discard changes',
    luuSoThayDoi: 'Save changes ({{so}})',
    xacNhanTieuDe: 'Save {{so}} changes?',
    lyDo: 'Reason',
    lyDoGoiY: 'Recorded in the change history so others know why. Optional.',
    daLuu: 'Saved {{so}} changes.',
    xungDot: 'Someone else just saved a change to these settings. Load the new version to see it; your edits stay on the form.',
    taiLaiBanMoi: 'Load new version',
    loiTai: 'Unable to load settings: {{loi}}',
    ruiRo: {
      mfaNgoaiDaiBat: "Users outside the trusted range will have to enter a one-time code on their next action; anyone without an authenticator app will have to set one up.",
      mfaBat: 'Users who have not set up MFA will have to set it up before they can continue.',
      emailNgoaiDomain:
        'People can use personal email and two-factor authentication becomes required for the whole organisation: anyone who has not set it up must do so before continuing.',
    },
    mfa: {
      apDungKhi: 'When should MFA apply?',
      lanDangNhapSau: 'From the next sign-in',
      lanDangNhapSauMo: 'Open sessions continue; each person sets up MFA the next time they sign in.',
      ngay: 'Immediately',
      ngayMo: 'Every open session must re-authenticate with MFA. People without MFA are signed out to set it up.',
    },
    lichSu: {
      tieuDe: 'Change history',
      caption: 'Security settings change history',
      cotPhienBan: 'Version',
      cotThoiDiem: 'Time',
      cotNguoiSua: 'Changed by',
      cotThayDoi: 'Changes',
      cotLyDo: 'Reason',
      hienTai: 'Current',
      heThong: 'System',
      themN: '+{{so}} more',
      tenPhienBan: 'version {{so}}',
      xemThayDoi: 'View changes',
      khoiPhuc: 'Restore this version',
      xemTieuDe: 'Changes in version {{so}}',
      xemMo: 'Saved by {{nguoi}} at {{luc}}.',
      khoiPhucTieuDe: 'Restore version {{so}}?',
      khoiPhucThan: 'Settings return to exactly how they were after this version. The change goes through every check a save does; older history is kept.',
      nhanKhoiPhuc: 'Restore version {{so}}',
      rong: 'No changes yet',
      rongMo: 'These settings use inherited values throughout. The first save will appear here.',
      loi: 'Unable to load history: {{loi}}',
    },
  },

  // ─────────── Tenant security settings — ADR-0059 §8 ───────────
  baoMat: {
    khongCoTenant: 'This screen shows one tenant’s data',
    khongCoTenantMo: 'Platform accounts do not belong to a tenant. Open Tenants in the admin portal to view each tenant’s settings and logs.',
    goi: {
      TRIAL: 'Trial',
      STANDARD: 'Standard',
      PROFESSIONAL: 'Professional',
      ENTERPRISE: 'Enterprise',
    },
    the: {
      truyCapIp: 'IP access',
      dangNhap: 'Sign-in',
      nhatKy: 'Activity log',
      lichSu: 'Change history',
    },
    theIp: {
      daiTinCay: 'Trusted range',
      ngoaiDai: 'IPs outside the trusted range',
      biChan: 'Blocked IPs',
      tuDong: 'Automatic temporary block',
    },
    ip: {
      allowlistTieuDe: 'Access by trusted range',
      ipCuaBan: 'Your IP:',
      tuDongTieuDe: 'Automatic temporary block',
      baoCaoTieuDe: 'IPs outside the trusted range in the last {{so}} hours',
      chuaCoDaiGhiChu: 'No trusted range yet, so no IP counts as outside.',
      cotIp: 'IP address',
      cotSoLan: 'Requests',
      cotNguoiDung: 'Users',
      cotLanCuoi: 'Last seen',
      soNguoiDung: '{{so}} users',
      themVaoAllowlist: 'Add to trusted range',
      daCoTrongAllowlist: 'Already in the trusted range',
      ghiChuTuBaoCao: 'Added from the outside-access report',
      baoCaoRong: 'No access from outside the trusted range',
      baoCaoRongMo: 'Every request in this period came from the trusted range.',
      loi: 'Unable to load the report: {{loi}}',
    },
    daiTinCay: {
      viDu: '113.185.44.0/24 # Hanoi office\n2001:db8:10::/48 # VPN\n203.0.113.7 # Integration server',
      huongDanThayCa: 'The box holds the whole list; saving replaces all of it, with the Save button at the bottom of the page.',
      huongDanDan: 'You can paste a list you exported earlier.',
      saoChep: 'Copy list',
      taiVe: 'Download list (.txt)',
      daSaoChep: 'Copied {{so}} lines.',
      khongSaoChepDuoc: 'Your browser blocked copying. Use Download list (.txt) instead.',
      ipTrongDai: 'In the trusted range',
      ipNgoaiDai: 'Outside the trusted range',
    },
    dsIp: {
      huongDanTieuDe: 'How to enter',
      huongDanMotDong: 'One IP address or CIDR range per line, IPv4 or IPv6.',
      huongDanGhiChu: 'Put a note after # at the end of the line. Lines starting with # are ignored.',
      huongDanMotDiaChi: 'An IP without a /prefix is exactly one address.',
      viDuTieuDe: 'Example',
      soDong: '{{so}} valid lines · up to {{max}}',
      loiDinhDang: 'Line {{dong}}: “{{giaTri}}” is not an IP address or CIDR range. Write it like 203.0.113.7 or 10.0.0.0/24.',
      loiTienTo: 'Line {{dong}}: the part after / in “{{giaTri}}” is wrong. Use 0 to 32 for IPv4, 0 to 128 for IPv6.',
      loiTrung: 'Line {{dong}}: {{cidr}} repeats line {{dongTruoc}}. Delete one of the two lines.',
      loiTatCa: 'Line {{dong}}: {{cidr}} covers every address. Enter a narrower range.',
      loiQuaSo: 'Line {{dong}}: over the limit of {{max}} lines. Merge nearby addresses into one range.',
      loiGhiChuDai: 'Line {{dong}}: the note is longer than {{max}} characters. Shorten it.',
      loiChuaIpCuaBan: 'Line {{dong}}: this range contains the IP you are using ({{ip}}) — blocking it would lock you out. Remove the line or enter a narrower range.',
      loiDaCo: 'Line {{dong}}: {{cidr}} is already on the blocklist.',
      loiMayChu: 'Line {{dong}}: {{loi}}',
      goiYDai: 'Line {{dong}}: {{ip}} is exactly one address. For the whole range, write {{cidr}}.',
    },
    chan: {
      tieuDe: 'Blocked IPs',
      canhBao: 'Blocking takes effect immediately, without Save, even for IPs in the trusted range.',
      oNhap: 'Block more addresses',
      viDu: '198.51.100.23 # Password guessing\n45.67.0.0/16 # Port scanning',
      huongDanLyDo: 'The note after # is the reason; if empty, “Blocked manually” is recorded.',
      huongDanTuKhoa: 'You cannot block a range that contains the IP you are using.',
      lyDoMacDinh: 'Blocked manually',
      nhanChan: 'Block addresses',
      nhanChanSo: 'Block {{so}} addresses',
      daChanSo: 'Blocked {{so}} addresses.',
      cotNguon: 'Source',
      thuCong: 'Manual',
      rong: 'No IPs blocked',
      rongMo: 'Block an IP or a range when you see suspicious access in the logs. IPs that keep failing to sign in are blocked temporarily on their own.',
      xacNhanGoThan: 'Access from this range is checked against the trusted range as usual again. To block it again you have to add a new row.',
      goSomTuDong: 'The system blocked this row because the IP kept failing to sign in. Unblock it early if it was someone in your organisation mistyping a password.',
      cotThoiHan: 'Duration',
      vinhVien: 'Permanent',
      conPhut: '{{so}} min left',
      conGio: '{{so}} h left',
      conNgay: '{{so}} days left',
      hetLuc: 'Expires {{luc}}',
      tuDong: 'Automatic',
      tuDongMo: 'Blocked by the system because this IP kept failing to sign in',
      oThoiHan: 'Block for',
      thoiHanVinhVienGoiY: 'Stays blocked until someone removes this row.',
    },
    chanThoiHan: {
      '1h': '1 hour',
      '24h': '24 hours',
      '7d': '7 days',
      'vinh-vien': 'Permanent',
    },
    dangNhap: {
      mfaTieuDe: 'Two-factor authentication',
      matKhauTieuDe: 'Password policy',
      emailTieuDe: 'Sign-in email',
      emailNgoaiDomainCanhBao: 'People can use personal email such as gmail; everyone will be required to use two-factor authentication.',
    },
    khoaTam: {
      tieuDe: 'Temporarily locked accounts',
      bangCaption: 'Accounts temporarily locked after failed sign-ins',
      cotNguoiDung: 'User',
      cotSoLanSai: 'Failed attempts',
      cotLanSaiCuoi: 'Last failed attempt',
      cotIp: 'IP of last failed attempt',
      chuaDocDuocEmail: 'Email unavailable',
      moKhoa: 'Unlock account',
      xnTieuDe: 'Unlock the account of {{ten}}?',
      xnThan: '{{ten}} can sign in again right away, and the count of {{so}} failed attempts resets to 0. This can’t be undone: if someone is guessing the password, they also get to start over.',
      xnGhiChu: 'An account disabled in User settings stays disabled.',
      daMoKhoa: 'Unlocked the account of {{ten}}.',
      rong: 'No accounts are temporarily locked',
      rongMo: 'Accounts that exceed the allowed failed sign-ins appear here.',
      chuaThay: 'No locked accounts found among the users checked so far',
      chuaThayMo: 'Users are checked in batches. Continue checking to see the rest.',
      traTiep: 'Continue checking',
      dangHien: 'Temporarily locked accounts: {{so}}',
      dangHienCoThem: 'Locked accounts so far: {{so}}, more users to check',
      loi: 'Couldn’t load temporarily locked accounts: {{loi}}',
      loiTraTiep: 'Couldn’t continue checking: {{loi}}',
    },
    nhatKy: {
      canhBaoTieuDe: 'Security alert recipients',
      vanHanhTieuDe: 'Logging and debugging',
    },
    // Three-level two-factor authentication (ADR-0047), Sign-in tab of the tenant portal.
    mfa: {
      tieuDe: 'Two-factor authentication',
      mucNhan: 'Who must enter an OTP code at sign-in',
      tomTatKhong: 'Not required',
      tomTatNguoi: 'People who must enter an OTP code: {{so}}',
      tomTatCaToChuc: 'The whole organisation must enter an OTP code',
      chuaCaiUngDung: 'Anyone without an authenticator app will have to set one up at their next sign-in.',
      tatCaNhan: 'Everyone in the organisation',
      tatCaMo: 'Including people added later',
      goiYChonTatCa: 'People added later will not be required. Choose “Everyone” to apply it to the whole organisation.',
      caToChucKhongSua:
        'A platform administrator has not allowed the organisation to require it for everyone; you can still select people one by one.',
      caToChucBiEp:
        'A platform administrator requires two-factor authentication for the whole organisation: everyone must enter an OTP code at sign-in. Contact a platform administrator to change this.',
      caToChucBiEpEmail: 'Required because the organisation accepts email outside its verified domains. To stop requiring it, turn Only accept email on verified domains back on above.',
      dangXuatNgay: 'Sign everyone out now to apply',
      dangXuatNgayMo:
        'Everyone, including you, must sign in again with an OTP code right after saving.',
      dangXuatNguoiChon: 'Sign the newly selected people out now to apply',
      dangXuatNguoiChonMo: 'The newly selected people must sign in again with an OTP code right after saving.',
      apDungDanMo:
        'Open sessions keep working until they expire, within 8 hours; the OTP code is required from the next sign-in.',
      timNhan: 'Search users',
      timPlaceholder: 'Search by name or email',
      daChon: 'Selected: {{so}}',
      daChonLoc: 'Selected: {{so}}, showing {{hien}}',
      loiNguoi: '{{ten}}: {{loi}}',
      loiTaiDanhSach: 'Could not load the people who must use two-factor authentication: {{loi}}',
      loiTaiNguoi: 'Could not load the user list: {{loi}}',
      dangTaiNguoi: 'Loading the user list',
      rongNguoi: 'The organisation has no users yet',
      rongNguoiMo: 'Invite users in User settings, then come back here to select them.',
      denCauHinhNguoiDung: 'Go to User settings',
      rongLoc: 'No one matches “{{tuKhoa}}”',
      luuNuaChung:
        'The list of selected people is saved, but the organisation-wide level is not. Your choices are kept; fix the error below, then save again.',
      xungDot:
        'Someone has just saved the organisation’s security settings. Load the new version, then save again; your choices are kept.',
      nutLuu: 'Save two-factor authentication',
      nutLuuVaDangXuat: 'Save and sign everyone out',
      nutLuuVaDangXuatNguoiChon: 'Save and sign out the newly selected people',
      boThayDoi: 'Discard changes',
      daLuu: 'Two-factor authentication settings saved.',
      daLuuVaDangXuat: 'Two-factor authentication settings saved and everyone signed out.',
      daLuuVaDangXuatNguoiChon: 'Two-factor authentication settings saved and the newly selected people signed out.',
    },
    hauQua: {
      tieuDe: 'After saving',
      moRongDan:
        'Extend to the whole organisation: people who were not selected must also enter an OTP code, from each person’s next sign-in, within 8 hours.',
      moRongNgay:
        'Extend to the whole organisation: everyone, including you, is signed out now and must sign in again with an OTP code.',
      moRongSoDan:
        'Extend to the whole organisation: people not selected before ({{so}}) must also enter an OTP code, from each person’s next sign-in, within 8 hours.',
      moRongSoNgay:
        'Extend to the whole organisation: everyone, including you, is signed out now and must sign in again with an OTP code; {{so}} of them did not need one before.',
      thuHep:
        'Narrow to the selected people ({{giu}}): they still enter an OTP code, everyone else signs in with a password only from their next sign-in. No one is signed out.',
      thuHepSo:
        'Narrow to the selected people ({{giu}}): they still enter an OTP code; the others ({{thoi}}) sign in with a password only from their next sign-in. No one is signed out.',
      themNguoiDan:
        'Newly selected people ({{so}}) must enter an OTP code from each person’s next sign-in, within 8 hours. Anyone without an authenticator app must set one up.',
      themNguoiNgay: 'Newly selected people ({{so}}) are signed out now and must sign in again with an OTP code.',
      boNguoi: 'Unselected people ({{so}}) sign in with a password only from their next sign-in.',
      banBiDangXuat: 'You are among the newly selected people, so you are signed out too.',
    },
    xacNhan: {
      tieuDe: 'Save the two-factor authentication settings?',
      mfaBatNgay:
        'Require two-factor authentication: everyone, including you, is signed out now and must sign in again with an OTP code.',
      mfaBatDan:
        'Require two-factor authentication: applies at each person’s next sign-in, within 8 hours.',
      mfaTat: 'Stop requiring two-factor authentication: people sign in with a password only.',
      hieuLucNgay: 'Changes take effect as soon as you save.',
      khongHoanTac: 'Changes take effect as soon as you save. Signing people out cannot be undone.',
    },
  },

  quyTacCanhBao: {
    tieuDe: 'Alert rules',
    them: 'Add rule',
    sua: 'Edit rule',
    xoa: 'Delete rule',
    capNhat: 'Update rule',
    themTieuDe: 'Add alert rule',
    suaTieuDe: 'Edit rule "{{ten}}"',
    nganMo: 'The rule goes into your unsaved changes; select Save in the bar at the bottom to apply it.',
    caption: 'Rules of {{ten}}',
    cotTen: 'Rule',
    cotDieuKien: 'Condition',
    cotNguong: 'Threshold',
    cotMuc: 'Severity',
    cotBat: 'On',
    batTatVoi: 'Turn on rule {{ten}}',
    demToiDa: '{{so}}/{{toiDa}} rules',
    rong: 'No alert rules yet',
    rongMo: 'Add a rule to be alerted about unusual event bursts, for example a user being denied many times within a few minutes.',
    xoaTieuDe: 'Delete rule "{{ten}}"?',
    xoaThan: 'Once the settings are saved, the rule stops being evaluated and raises no new alerts; alerts already raised are kept. Before saving, select Undo on the Alert rules row to bring it back.',
    loiTruong: '{{truong}}: {{cau}}',
    nhomChung: 'General',
    nhomDieuKien: 'Events to count',
    nhomNguong: 'Trigger threshold',
    tenViDu: 'Repeated denials',
    idTuSinh: 'Generated from the name; cannot be changed after the rule is added.',
    idKhongDoi: 'The ID cannot be changed: raised alerts refer to the rule by this ID.',
    moiQuyetDinh: 'Any decision',
    actionsGoiY: '* matches any text, for example user.*. Leave empty for any action.',
    actionsViDu: 'GET, user.* — type, then press Enter',
    resourceGoiY: 'Only count resources starting with this text. Leave empty for any resource.',
    resourceViDu: '/api/v1/',
    sourcesGoiY: 'Select no source to count all sources.',
    errorCodesGoiY: 'Upper-case error codes. Leave empty to not filter by error code.',
    errorCodesViDu: 'PERMISSION_DENIED',
    toiDaMuc: 'Up to {{so}} items.',
    donViGiay: 'seconds',
    donViLan: 'events',
    bang: '= {{giaTri}}',
    xemTruoc: 'Triggers at {{tomTat}}. Counts: {{dieuKien}}.',
    nguongTomTat: '{{so}} events / {{cuaSo}} {{nhom}}',
    truong: {
      id: 'Rule ID',
      name: 'Rule name',
      enabled: 'Rule on',
      decision: 'Decision',
      actions: 'Action',
      resource_prefix: 'Resource prefix',
      sources: 'Source',
      error_codes: 'Error code',
      group_by: 'Count separately by',
      threshold: 'Threshold',
      window_seconds: 'Counting window',
      cooldown_seconds: 'Wait before alerting again',
      severity: 'Alert severity',
    },
    quyetDinh: { ALLOW: 'Allowed', DENY: 'Denied' },
    nguon: { gateway: 'Gateway', service: 'Service', keycloak: 'Keycloak' },
    nhom: { user: 'User', ip: 'IP', tenant: 'Whole tenant' },
    nhomTheo: { user: 'per user', ip: 'per IP', tenant: 'across the tenant' },
    thoiLuong: { giay: '{{so}} s', phut: '{{so}} min', gio: '{{so}} h', ngay: '{{so}} d' },
    tomTat: {
      action: 'action {{ds}}',
      resource: 'resource starting with {{tienTo}}',
      nguon: 'source {{ds}}',
      maLoi: 'error code {{ds}}',
      moiSuKien: 'Any audit event',
    },
    loi: {
      bat_buoc: 'Enter a value for this field.',
      qua_dai: 'Shorten to at most {{max}} characters.',
      id_sai: 'Use at least one letter or digit in the name to generate an ID.',
      id_trung: 'ID {{giaTri}} is already used by another rule. Rename the rule.',
      khong_phai_so_nguyen: 'Enter a whole number, without decimals.',
      nho_hon_min: 'Enter {{min}} or more.',
      lon_hon_max: 'Enter no more than {{max}}.',
      qua_so_muc: 'Remove some items: up to {{max}} items.',
      muc_sai: 'Fix "{{giaTri}}" to match the format of this field.',
      ngoai_danh_sach: 'Choose a value from the list.',
    },
  },
  canhBaoAudit: {
    tieuDe: 'Raised alerts',
    caption: 'Raised audit alerts',
    cotThoiDiem: 'Time',
    cotQuyTac: 'Rule',
    cotMuc: 'Severity',
    cotNhom: 'Subject',
    cotSoLan: 'Events',
    cotCuaSo: 'Window',
    soLanTrenNguong: '{{so}} / {{nguong}}',
    tuDen: '{{tu}} – {{den}}',
    trangThai: { OPEN: 'Not seen', ACKNOWLEDGED: 'Seen' },
    danhDauDaXem: 'Mark as seen',
    daXacNhan: 'Marked alert "{{ten}}" as seen.',
    loiXacNhan: 'Could not mark alert "{{ten}}" as seen: {{loi}}',
    taiLai: 'Reload list',
    loi: 'Could not load alerts: {{loi}}',
    loiTaiThem: 'Could not load more: {{loi}}',
    locMuc: 'Filter by severity',
    locTrangThai: 'Filter by status',
    moiMuc: 'All severities',
    tatCa: 'All',
    rong: 'No alerts yet',
    rongMo: 'When a rule reaches its threshold, the alert shows here and is sent to the audit alert recipients.',
    rongLoc: 'No alerts match the filters',
  },

  // ─────────── Activity log — spec §8.2, audit-service ───────────
  nhatKy: {
    moTaMan: 'Who did what, to which object, when, and with what result. Shows the last 24 hours by default.',
    moTaManNenTang: 'Activity and AI usage logs of tenants. Every view is itself logged.',
    chonTenant: 'Choose a tenant',
    chuaChonTenant: 'Choose a tenant to view its log',
    chuaChonTenantMo: 'The log is searched one tenant at a time, up to 90 days per search.',
    loiTenant: 'Unable to load tenants: {{loi}}',
    caption: 'Activity log',
    cot: {
      thoiDiem: 'Time',
      nguoi: 'Performed by',
      thaoTac: 'Action',
      ketQua: 'Result',
    },
    ketQua: { ALLOW: 'Allowed', DENY: 'Denied' },
    moc: {
      hom_nay: 'Today',
      '24_gio': 'Last 24 hours',
      '7_ngay': 'Last 7 days',
      '30_ngay': 'Last 30 days',
      tuy_chon: 'Custom',
    },
    loc: {
      khoang: 'Time range',
      nguoi: 'User',
      nguoiGoiY: 'Search by name or email',
      nhomNguoiDung: 'Tenant users',
      nhomNhanSu: 'Platform staff',
      dangTaiNguoi: 'Loading users...',
      khongCoNguoi: 'No users match',
      loiNguoi: 'Unable to load users: {{loi}}',
      thaoTac: 'Action',
      nhomThaoTac: 'Action groups',
      chiTiet: 'Specific actions',
      chiTietMo: 'Choose individual actions',
      daChonChiTiet: '{{so}} actions selected',
      timThaoTac: 'Search actions',
      boChonChiTiet: 'Clear selection',
      khongCoThaoTac: 'No actions match',
      ketQua: 'Result',
      chonKhoang: 'Choose both a start and an end time.',
      nguoc: 'Choose an end time after the start time.',
      quaDai: 'Narrow the range to {{so}} days or fewer.',
    },
    hanhDong: {
      'auth.login_error': 'Failed sign-in',
      'auth.user_temporarily_locked': 'Account temporarily locked after failed sign-ins',
      USER_INVITED: 'Invite user',
      USER_INVITE_RESENT: 'Resend invitation',
      USER_INVITE_REJECTED: 'Email cannot be invited',
      USER_PROFILE_UPDATED: 'Edit user profile',
      USER_DEPARTMENT_CHANGED: 'Change user’s department',
      USER_ROLES_CHANGED: 'Assign roles to user',
      USER_DISABLED: 'Lock user',
      USER_ENABLED: 'Unlock user',
      USER_MFA_REQUIRED_ENABLED: 'Require two-factor authentication for user',
      USER_MFA_REQUIRED_DISABLED: 'Stop requiring two-factor authentication for user',
      USER_PASSWORD_RESET_SENT: 'Send password reset email',
      USER_LOGIN_UNLOCKED: 'Unlock sign-in',
      USER_MFA_RESET: 'Reset two-factor authentication',
      USER_BRUTE_FORCE_UNLOCKED: 'Clear failed sign-in lockout',
      ROLE_CREATED: 'Create role',
      ROLE_UPDATED: 'Edit role',
      ROLE_PERMISSIONS_CHANGED: 'Change role permissions',
      ROLE_DELETED: 'Delete role',
      DEPARTMENT_CREATED: 'Create department',
      DEPARTMENT_UPDATED: 'Edit department',
      DEPARTMENT_DELETED: 'Delete department',
      PLATFORM_STAFF_REQUESTED: 'Request platform staff account',
      PLATFORM_STAFF_APPROVED: 'Approve platform staff',
      PLATFORM_STAFF_UPDATED: 'Edit platform staff',
      PLATFORM_STAFF_ROLE_CHANGED: 'Change platform staff account group',
      PLATFORM_STAFF_ROLES_CHANGED: 'Assign platform roles to staff',
      PLATFORM_STAFF_DISABLED: 'Lock platform staff',
      PLATFORM_STAFF_ENABLED: 'Unlock platform staff',
      PLATFORM_STAFF_INVITE_SENT: 'Send platform staff invitation',
      PLATFORM_STAFF_INVITE_RESENT: 'Resend platform staff invitation',
      PLATFORM_ROLE_CREATED: 'Create platform role',
      PLATFORM_ROLE_UPDATED: 'Edit platform role',
      PLATFORM_ROLE_PERMISSIONS_CHANGED: 'Change platform role permissions',
      PLATFORM_ROLE_DELETED: 'Delete platform role',
      KEYCLOAK_USER_CREATED: 'Create sign-in account',
      KEYCLOAK_USER_DELETED: 'Delete sign-in account',
      KEYCLOAK_USER_DISABLED: 'Lock sign-in account',
      KEYCLOAK_USER_ENABLED: 'Unlock sign-in account',
      KEYCLOAK_USER_BRUTE_FORCE_CLEARED: 'Clear sign-in account lockout',
      KEYCLOAK_USER_MFA_REQUIRED: 'Require OTP for account',
      KEYCLOAK_USER_MFA_NOT_REQUIRED: 'Stop requiring OTP for account',
      KEYCLOAK_INVITE_SENT: 'Send set-password invitation',
      KEYCLOAK_SESSIONS_REVOKED: 'Sign user out of all sessions',
      KEYCLOAK_OTP_RESET: 'Remove account OTP',
      KEYCLOAK_LOGIN_UNLOCKED: 'Unlock account sign-in',
      KEYCLOAK_PASSWORD_RESET_SENT: 'Send account password reset email',
      PERMISSION_CHECK: 'Internal permission check',
      TENANT_CREATED: 'Create tenant',
      TENANT_VIEWED: 'View tenant details',
      TENANT_UPDATED: 'Edit tenant',
      TENANT_APPROVED: 'Approve tenant',
      TENANT_SUSPENDED: 'Suspend tenant',
      TENANT_RESUMED: 'Resume tenant',
      TENANT_ONBOARDING_FINISHED: 'Finish onboarding',
      TENANT_ONBOARDING_REVERTED: 'Revert onboarding',
      KEYCLOAK_TENANT_GROUP_CREATED: 'Create tenant group in Keycloak',
      TENANT_DOMAIN_ADDED: 'Add domain',
      TENANT_DOMAIN_CHECKED: 'Check domain verification',
      TENANT_DOMAIN_APPROVED: 'Approve domain',
      'tenant.contact.created': 'Add contact',
      'tenant.contact.updated': 'Edit contact',
      'tenant.contact.deleted': 'Delete contact',
      'security.policy.updated': 'Change security settings',
      'security.ip_blocklist.added': 'Add IP to blocklist',
      'security.ip_blocklist.removed': 'Remove IP from blocklist',
      'ip.monitor': 'Access from outside the allowlist (Trial mode)',
      'api_key.created': 'Create API key',
      'api_key.revoked': 'Revoke API key',
      KEYCLOAK_TENANT_MFA_ENABLED: 'Require OTP for the whole organization',
      KEYCLOAK_TENANT_MFA_DISABLED: 'Stop requiring OTP for the whole organization',
      KEYCLOAK_TENANT_SESSIONS_REVOKED: 'Sign the organization out of all sessions',
      KEYCLOAK_TENANT_PASSWORD_POLICY_SYNCED: 'Sync password policy',
      TENANT_SESSIONS_REVOKED_MFA_APPLY_NOW: 'End sessions to apply two-factor authentication now',
      'license.issue': 'Issue license',
      'license.revoke': 'Revoke license',
      'license.request.create': 'Create license request',
      'license.request.approve': 'Approve license request',
      'license.request.reject': 'Reject license request',
      'license.request.cancel': 'Cancel license request',
      'license.installation.register': 'Register on-premise installation',
      'license.download_ticket': 'Issue license download ticket',
      'license.download': 'Download license',
      'license.keyring.upload': 'Upload license signing keyring',
      'license.local.license_imported': 'Import license',
      'license.local.license_rejected': 'Reject invalid license',
      'license.local.keyring_imported': 'Import signing keyring',
      'license.local.keyring_rejected': 'Reject invalid signing keyring',
      'license.local.revocation_imported': 'Import revocation list',
      'license.local.revocation_rejected': 'Reject invalid revocation list',
      'license.local.clock_rollback': 'Server clock rollback detected',
      'license.local.license_status': 'License status changed',
      MODULE_INSTALLED: 'Install module',
      MODULE_UNINSTALLED: 'Uninstall module',
      MODULE_UPGRADED: 'Upgrade module',
      MODULE_ROLLED_BACK: 'Roll back module',
      MODULE_DELETED: 'Delete module from Marketplace',
      'audit.query': 'Search the log',
      'audit.export': 'Export the log',
      'audit.archive.list': 'View cold storage archives',
      'audit.archive.download': 'Download cold storage archive',
      'audit.archive.completed': 'Month archived to cold storage',
      'audit.archive.partition_dropped': 'Drop archived hot table',
      'audit.archive.failed': 'Cold storage archiving failed',
      'audit.alert.triggered': 'Log alert triggered',
      'audit.alert.acknowledged': 'Mark alert as seen',
      'audit.trash.deleted': 'Move log to trash',
      'audit.trash.restored': 'Restore log from trash',
      'audit.trash.purged': 'Permanently delete expired log',
      'ops.log_level.changed': 'Change log level',
      'notification.sent': 'Send notification',
      'llm.goi': 'Call AI model',
      AI_MODEL_CREATED: 'Add AI model',
      AI_MODEL_UPDATED: 'Edit AI model',
      TENANT_AI_MODELS_UPDATED: 'Change tenant’s AI models',
    },
    goiApi: 'API call ({{phuongThuc}})',
    nhom: {
      dang_nhap: 'Sign-in',
      nguoi_dung: 'Users & roles',
      tenant: 'Tenant',
      bao_mat: 'Security',
      license_module: 'License & modules',
      truy_cap_api: 'API access',
      van_hanh: 'Logs & operations',
      ai: 'AI',
    },
    hanhDongLa: 'Action not in this version’s catalog ({{ma}})',
    nguon: { GATEWAY: 'Gateway', SERVICE: 'Business service', SDK: 'Business service (SDK)', KEYCLOAK: 'Sign-in (Keycloak)' },
    heThong: 'System',
    chiNguoiNay: 'Show only this person’s actions',
    moChiTiet: 'Open action details',
    co: 'Yes',
    khong: 'No',
    rongThat: 'No actions in this period',
    rongThatMo: 'Try a longer time range.',
    xem30Ngay: 'Show last 30 days',
    rongLoc: 'No actions match the filters',
    hetBanGhi: 'Showing all {{so}} records for these filters.',
    loi: 'Unable to load the log: {{loi}}',
    chiTiet: {
      tieuDe: 'Action details',
      tomTat: 'Summary',
      doiTuong: 'Object',
      lyDo: 'Reason / error code',
      ip: 'IP address',
      nguonGhi: 'Recorded by',
      thayDoi: 'Changes',
      khongDoi: 'This action did not change any data.',
      duLieuKyThuat: 'Technical data',
      maSuKien: 'Event ID',
      maBanGhi: 'Record ID',
      maNguoi: 'User ID',
    },
  },

  // Cold storage (ADR-0059 §4.2)
  luuTruLanh: {
    theThaoTac: 'Activity log',
    the: 'Cold storage',
    moTa: 'Logs older than 12 months are moved month by month to object storage as compressed JSON Lines, with a manifest for hash-chain checks, and kept for 5 years. Every view and download is recorded.',
    caption: 'Cold storage archives of the activity log, newest first',
    cot: {
      thang: 'Month',
      trangThai: 'Status',
      soDong: 'Rows',
      dungLuong: 'Size',
      giuToi: 'Retained until',
      loi: 'Last error',
    },
    trangThai: {
      PENDING: 'Waiting',
      EXPORTING: 'Exporting',
      UPLOADED: 'Stored, awaiting detach',
      DETACHED: 'Detached from table',
      DROPPED: 'Hot table dropped',
      FAILED: 'Failed, will retry',
    },
    soLanThu: 'Tried {{so}} times',
    soTenant: '{{so}} tenants',
    taiDuLieu: 'Download data file',
    taiManifest: 'Download manifest',
    rong: 'No cold storage archives yet',
    rongMo: 'The archive job runs daily and only moves logs older than 12 months, so a new system has none yet.',
    hetBanGhi: 'Showing all {{so}} archives.',
    loi: 'Unable to load cold storage archives: {{loi}}',
  },

  // Log trash (ADR-0089)
  thungRac: {
    the: 'Trash',
    moTa: 'Deleted logs stay here for 30 days and can be restored; after that they are permanently deleted. This ledger is append-only: restores and permanent deletions are recorded too, and entries cannot be removed.',
    caption: 'Log deletions, newest first',
    cot: {
      khoang: 'Deleted period',
      tenant: 'Tenant',
      soBanGhi: 'Records',
      lyDo: 'Reason',
      thoiDiem: 'Deleted at / by',
      conLai: 'Restorable for',
      trangThai: 'Status',
    },
    donVi: { DAY: 'Day', WEEK: 'Week', MONTH: 'Month' },
    trangThai: { ACTIVE: 'In trash', RESTORED: 'Restored', PURGED: 'Permanently deleted' },
    khoangNgay: '{{tu}} – {{den}}',
    conNgay: '{{so}} days left',
    conMotNgay: '1 day left',
    choXoa: 'Awaiting permanent deletion',
    daXoaVinhVien: '{{so}} records deleted',
    khoiPhuc: 'Restore',
    khoiPhucTieuDe: 'Restore deleted logs?',
    khoiPhucThan: '{{so}} records for {{tenant}} from {{tu}} through {{den}} will reappear in searches and exports. The restore is recorded in the trash ledger.',
    khoiPhucThanNgay: '{{so}} records for {{tenant}} on {{ngay}} will reappear in searches and exports. The restore is recorded in the trash ledger.',
    khoiPhucNut: 'Restore {{so}} records',
    dangKhoiPhuc: 'Restoring...',
    daKhoiPhuc: 'Restored {{so}} log records.',
    rong: 'The trash is empty',
    rongMo: 'Logs deleted by day, week or month from the Activity log tab stay here for 30 days.',
    rongLoc: '{{tenant}} has no log deletions',
    dangLoc: 'Showing only deletions for {{tenant}}, the tenant selected on the Activity log tab.',
    loi: 'Unable to load the trash: {{loi}}',
    hetBanGhi: 'Showing all {{so}} deletions.',
    nutXoa: 'Delete logs',
    tieuDe: 'Delete logs for {{tenant}}?',
    donViNhan: 'Delete by',
    chon: { DAY: 'Day to delete', WEEK: 'Week to delete', MONTH: 'Month to delete' },
    khoang: 'From {{tu}} through {{den}}, Vietnam time.',
    dinhDangTuan: 'Week {{so}}, {{nam}}',
    lyDo: 'Reason for deletion',
    lyDoGoiY: 'For example: test data from the October UAT round',
    lyDoThieu: 'Enter a reason to record in the trash ledger.',
    lyDoNgan: 'Write a reason of at least {{so}} characters.',
    lyDoDai: 'Shorten the reason to {{so}} characters or fewer.',
    chonThoiDiem: 'Choose a period to see how many records will be deleted.',
    dangDem: 'Counting records...',
    soSeXoa: '{{so}} records will be moved to the trash.',
    khongCoBanGhi: 'No records left to delete in this period. Choose another period.',
    loiDem: 'This period cannot be deleted: {{loi}}',
    hauQua: 'Records in the chosen period move to the trash and disappear from searches and exports right away. You can restore them from the Trash tab within 30 days; after that they are permanently deleted and cannot be recovered. The audit records of this deletion and any restore are always kept.',
    nutXacNhan: 'Delete {{so}} records',
    dangXoa: 'Deleting...',
    daXoa: 'Moved {{so}} records to the trash.',
  },

  // ─────────── Tenant contacts — ADR-0059 §6a.3 ───────────
  lienHe: {
    tieuDe: 'Contacts',
    mo: 'Who receives the tenant’s notifications, by type. For on-premise tenants this is the only way to warn of license expiry.',
    caption: 'Tenant contacts',
    cotTen: 'Name',
    cotLoai: 'Type',
    cotThongBao: 'Receives',
    cotCapNhat: 'Updated',
    chinh: 'Primary contact',
    khongNhan: 'No notifications',
    loai: {
      ADMIN: 'Administrator',
      CONTRACT_OWNER: 'Contract owner',
      TECHNICAL: 'Technical',
      SECURITY: 'Security',
      BILLING: 'Billing',
    },
    themDauMoi: 'Add contact',
    luuDauMoi: 'Save contact',
    suaDauMoi: 'Edit contact',
    xoaDauMoi: 'Delete contact',
    dialogThem: 'Add contact',
    dialogSua: 'Edit contact {{ten}}',
    fLoai: 'Contact type',
    fTen: 'Name or team',
    fTenTrong: 'Enter a name or team.',
    fEmail: 'Email',
    fEmailGoiY: 'Use a group mailbox so notifications are not lost when someone leaves.',
    fEmailViDu: 'security@company.com',
    fEmailTrong: 'Enter an email address.',
    fEmailSai: 'Enter a full email address, such as security@company.com.',
    fEmailNhanThu: 'Notification email (if different)',
    fDienThoai: 'Phone',
    fNhanThongBao: 'Receives notifications',
    fNhanThongBaoGoiY: 'Leave empty if this contact is for reference only.',
    fChinh: 'Primary contact for this type',
    fChinhMo: 'Each type has one primary contact; choosing this clears the previous one.',
    rong: 'No contacts yet',
    rongMo: 'Add at least one contact so security and license notifications reach the tenant.',
    loi: 'Unable to load contacts: {{loi}}',
    xacNhanXoaTieuDe: 'Delete contact {{ten}}?',
    xacNhanXoaThan: 'This contact stops receiving: {{thongBao}}. This cannot be undone.',
    daThem: 'Contact added.',
    daCapNhat: 'Contact saved.',
    daXoa: 'Contact deleted.',
  },

  // ─────────── Notifications and recipients — ADR-0059 §6a ───────────
  thongBao: {
    nguoiNhanTieuDe: 'Recipients by notification type',
    guiThu: 'Send test',
    guiThuDungBanDaLuu: 'The test uses saved recipients, not your unsaved edits.',
    daGuiThu: 'Test notification sent to {{so}} recipients.',
    guiThuChuaCoNguoiNhan: 'This type has no recipients: the test only appears in the admin portal.',
    guiThuLoi: 'The test was not sent. Check the channels, then try again.',
    ganDayTieuDe: 'Recent notifications',
    cotThoiDiem: 'Time',
    cotMucDo: 'Severity',
    cotNoiDung: 'Content',
    cotNguoiNhan: 'Recipients',
    soNguoiNhan: '{{so}} recipients',
    trangThai: {
      SENT: 'Sent',
      NO_RECIPIENT: 'No recipient',
      FAILED: 'Failed',
    },
    rong: 'No notifications yet',
    rongMo: 'Notifications from infrastructure alerts, audit rules and license jobs appear here.',
    loi: 'Unable to load notifications: {{loi}}',
  },

  // ─────────── Platform policy — ADR-0059 §8 ───────────
  chinhSach: {
    the: {
      goi: 'By plan',
      nenTang: 'Platform',
      matKhau: 'Passwords',
      blocklist: 'Blocked IPs',
      thongBao: 'Notifications',
    },
    goi: {
      chon: 'Plan',
      coNhap: '{{goi}} (unsaved)',
      tieuDe: '{{goi}} plan policy',
    },
    nenTang: {
      truyCapTieuDe: 'Authentication and limits',
      auditTieuDe: 'Logging and password-spraying detection',
      lienHeTieuDe: 'Contacts',
    },
    matKhau: {
      tieuDe: 'Password policy minimums',
    },
    dongBo: {
      SYNCED: 'Synced to Keycloak',
      PENDING: 'Waiting to sync',
      DISABLED: 'Not synced to Keycloak',
      PENDING_MO: 'Saved, but Keycloak has not received it yet. This usually takes a few minutes; if not, check the tenant-service logs.',
      DISABLED_MO: 'Realm sync is off: values are stored on the platform only. Operations must apply them through the realm file until the platform-realm-config client is enabled.',
    },
    blocklist: {
      tieuDe: 'IPs blocked platform-wide',
      canhBao: 'Blocking takes effect immediately for every tenant and the admin portal.',
      cotLyDo: 'Reason',
      cotTao: 'Blocked by',
      go: 'Unblock',
      nhanGo: 'Unblock {{cidr}}',
      xacNhanGoTieuDe: 'Unblock {{cidr}}?',
      xacNhanGoThan: 'Requests from this range go through tenant checks as usual again. This cannot be undone; to block it again, add a new row.',
      daGo: 'Unblocked {{cidr}}.',
      rong: 'No IP ranges blocked',
      rongMo: 'Ranges blocked here apply to every tenant. To block for one tenant, use that tenant’s Security tab.',
      loi: 'Unable to load the blocklist: {{loi}}',
    },
  },

  nguoiDung: {
    trangThai: {
      ACTIVE: 'Active',
      INVITED: 'Invited',
      DISABLED: 'Locked',
    },
    tatCaTrangThai: 'All statuses',
    bangCaption: 'Users of the organisation',
    cotNguoiDung: 'User',
    cotVaiTro: 'Roles',
    cotCapNhat: 'Updated',
    ban: '(you)',
    chuaDocDuocEmail: 'Email unavailable',
    loiDanhSach: 'Could not load users: {{loi}}',
    loiTaiThem: 'Could not load more: {{loi}}',
    rongThat: 'The organisation has no users yet',
    rongThatMo: 'Invite colleagues by their work email. They get a link to set a password, valid for 72 hours.',
    rongLoc: 'No users with this status',
    moiNguoiDauTien: 'Invite the first user',
    dangHien: '{{so}} users',
    dangHienCoThem: 'Showing {{so}} users, more available',
    taiThem: 'Load more',
    suaThongTin: 'Edit details',
    guiLaiLoiMoi: 'Resend invitation',
    ganVaiTro: 'Assign roles',
    khoaTaiKhoan: 'Lock account',
    moKhoaTaiKhoan: 'Unlock account',
    moiNguoiDung: 'Invite user',
    moiTieuDe: 'Invite user',
    nhacNgoaiDomain: 'This email is on {{domain}}, outside your verified domains. Double-check it: the recipient will get access to the organisation.',
    fEmail: 'Sign-in email',
    fEmailGoiY: 'Must belong to a verified domain of the organisation.',
    fEmailTrong: 'Enter the email of the person to invite.',
    fEmailSai: 'This is not a valid email. Use the form name@company.com.',
    fEmailKhongSua: 'The email is the sign-in name, so it cannot be changed.',
    fEmailNhanThu: 'Notification email (if different)',
    nhanThu: 'Notifications to: {{email}}',
    doiEmailNhanThu: 'Change notification email',
    xnDoiEmailTieuDe: "Change {{ten}}'s notification email?",
    xnDoiEmailThan:
      'Password reset emails and all notifications will go to the new address. This person is signed out of every session and must verify the new address.',
    fEmailNhanThuMoi: 'New notification email',
    fEmailNhanThuTrong: 'Enter the new notification email.',
    fEmailNhanThuTrung: 'This is already the current notification email.',
    fEmailNhanThuKhongDung: 'This email cannot be used. Enter a different address.',
    daDoiEmailNhanThu: "Changed {{ten}}'s notification email.",
    daDoiChuaGuiXacMinh:
      "Changed {{ten}}'s notification email, but the verification email could not be sent. They must verify it at their next sign-in.",
    fPhongBan: 'Department',
    fPhongBanTrong: 'No department',
    fPhongBanLoi: 'Could not load departments: {{loi}}',
    fPhongBanKhongCo: 'No matching departments',
    cotPhongBan: 'Department',
    chuaCoPhongBan: 'None',
    phongBanKhongRo: 'Unknown',
    fTenHienThi: 'Display name',
    fTenTrong: 'Enter a display name.',
    fQuaDai: 'Longer than {{so}} characters. Shorten it.',
    fQuanTriMo: 'Full access across the organisation, no roles needed',
    fQuanTriLuuY: 'Must set up an authenticator app at first sign-in. Cannot be changed after inviting.',
    fVaiTro: 'Roles',
    fVaiTroChon: 'Select roles',
    fVaiTroKhongKhop: 'No matching roles',
    fVaiTroRong: 'The organisation has no roles yet. You can invite now, then create and assign roles later.',
    fVaiTroLoiTai: 'Could not load roles. Reopen the list to try again, or invite now and assign roles later.',
    chuaGanVaiTro: 'No roles',
    vaiTroKhongRo: 'Unknown',
    guiLoiMoi: 'Send invitation',
    dangGuiLoiMoi: 'Sending...',
    suaTieuDe: 'Edit details of {{ten}}',
    luuThongTin: 'Save details',
    xnKhoaTieuDe: 'Lock the account of {{ten}}?',
    xnKhoaThan:
      'This person is signed out of every session right away and cannot sign in until an administrator unlocks the account. Their profile and data are kept.',
    xnKhoaThanDaMoi:
      'The link in the invitation email stops working, and this person cannot sign in until an administrator unlocks the account. Their profile is kept.',
    daGuiLoiMoi: 'Invitation sent to {{email}}.',
    daTaoChuaGuiEmail:
      '{{email}} was added but the invitation email could not be sent. Use "Resend invitation" to try again.',
    chuaGanDuocVaiTro: 'Invited {{email}}, but the roles were not assigned: {{loi}}',
    ganLaiVaiTro: 'Assign roles again',
    daGuiLaiLoiMoi: 'Invitation resent to {{email}}. {{so}} resends left today.',
    daGuiLaiLoiMoiHetLuot: 'Invitation resent to {{email}}. That was the last resend for today.',
    hetLuotGuiLai: 'The invitation has been resent 5 times today for this person. Try again from midnight tomorrow (Vietnam time).',
    daLuu: 'Saved the details of {{ten}}.',
    daKhoa: 'Locked the account of {{ten}}.',
    daMoKhoa: 'Unlocked the account of {{ten}}.',
    mfa: {
      cot: 'Two-factor authentication',
      batBuoc: 'Required',
      daCai: 'Set up',
      chuaCai: 'Not set up',
      khongRo: 'Unknown',
      batBuocMuc: 'Require two-factor authentication',
      boBatBuocMuc: 'Stop requiring two-factor authentication',
      xnTieuDe: 'Require two-factor authentication for {{ten}}?',
      xnThan:
        'This person is signed out of every session now. At their next sign-in they must enter an OTP code from an authenticator app, setting one up first if they have not.',
      xnThanChuaCai:
        'This person has not set up an authenticator app. They are signed out of every session now, and at their next sign-in must set one up and enter an OTP code to get in.',
      xnThanBan:
        'You are signed out of every session now and must enter an OTP code at your next sign-in. If you have not set up an authenticator app, you must set one up first.',
      daBatBuoc: 'Two-factor authentication is now required for {{ten}}.',
      daBoBatBuoc: 'Two-factor authentication is no longer required for {{ten}}.',
    },
    layLai: {
      tamKhoa: 'Sign-in locked',
      guiDatLaiMatKhau: 'Send password reset email',
      moKhoaDangNhap: 'Unlock sign-in',
      moKhoaDangNhapMo: 'Locked after too many wrong passwords',
      datLaiMfa: 'Reset two-factor authentication',
      xnDatLaiTieuDe: 'Send a password reset email to {{ten}}?',
      xnDatLaiThan:
        'The email goes to {{email}} with a link for this person to set a new password. The link is valid for 1 hour. Their current password keeps working until they set a new one.',
      loiXungDot:
        'This person has not accepted the invitation or was just locked, so nothing was sent. The list has been reloaded: for someone who has not accepted, use "Resend invitation"; for a locked account, unlock it first.',
      loiQuaNhieu:
        'This person has already been sent 5 password reset emails today, the daily maximum. Ask them to check their inbox (and spam folder), or try again tomorrow.',
      loiMayChuEmail:
        'The email server is failing, so the email was not sent. Try again in a few minutes; if it keeps failing, contact the platform operations team.',
      xnMfaTieuDe: 'Reset two-factor authentication for {{ten}}?',
      xnMfaThan:
        "This person's authenticator apps and recovery codes are deleted, and they are signed out of every session now. Their next sign-in asks only for a password until they set up an authenticator app again. This cannot be undone.",
      xnMfaThanBatBuoc:
        "This person's authenticator apps and recovery codes are deleted, and they are signed out of every session now. Because two-factor authentication is required for them, they must set up an authenticator app again at their next sign-in. This cannot be undone.",
      daGuiDatLai: 'Password reset email sent to {{email}}.',
      daMoKhoaDangNhap: 'Sign-in unlocked for {{ten}}.',
      daDatLaiMfa: 'Two-factor authentication reset for {{ten}}.',
    },
  },
  coCauToChuc: {
    bangCaption: "Department tree of the organisation",
    timNhan: 'Search departments',
    timPlaceholder: 'Search by department name',
    moRongHet: 'Expand all',
    thuGonHet: 'Collapse all',
    cotPhongBan: 'Department',
    cotNguoiDung: 'Users',
    cotPhongCon: 'Sub-departments',
    cotTaoLuc: 'Created',
    cap: 'Level {{so}}',
    chuaCoAi: 'No one yet',
    loiDanhSach: 'Could not load the department tree: {{loi}}',
    rongThat: 'The organisation has no departments yet',
    rongThatMo:
      'Start from the top-level departments, such as Finance division, then add sub-departments below them.',
    rongLoc: 'No departments match “{{tuKhoa}}”',
    themPhongBan: 'Add department',
    themPhongBanDauTien: 'Add the first department',
    themPhongBanCon: 'Add sub-department',
    suaPhongBan: 'Edit department',
    xoaPhongBan: 'Delete department',
    chuaXoaMoTa: 'Still has users or sub-departments',
    dangHien: 'Departments: {{so}}',
    dangHienLoc: 'Showing {{so}} of {{tong}} departments',
    taoTieuDe: 'Add department',
    taoConTieuDe: 'Add a sub-department to "{{ten}}"',
    suaTieuDe: 'Edit department "{{ten}}"',
    dangTaiChiTiet: 'Loading department...',
    loiChiTiet: 'Could not load the department: {{loi}}',
    xungDot: 'Someone else just edited this department; its name is now "{{ten}}". Check your changes and save again.',
    fTen: 'Department name',
    fTenPlaceholder: 'For example: Accounting',
    fTenTrong: 'Enter a department name.',
    fTenDai: 'Longer than {{so}} characters. Shorten it.',
    fCha: 'Parent department',
    fChaTrong: 'None, this is a top-level department',
    fChaGoiY: 'Leave empty for a top-level department. The tree has at most {{so}} levels.',
    fChaQuaSau: 'Choosing this department would make the tree deeper than {{so}} levels',
    fChaQuaSauNgan: 'Over {{so}} levels',
    fChaKhongCo: 'No matching departments',
    luuTao: 'Add department',
    luuSua: 'Save changes',
    daTao: 'Added department "{{ten}}".',
    daLuu: 'Saved department "{{ten}}".',
    daXoa: 'Deleted department "{{ten}}".',
    xnXoaTieuDe: 'Delete department "{{ten}}"?',
    xnXoaThan: 'The department is removed from the organisation structure. This cannot be undone.',
    chuaXoaTieuDe: 'Department "{{ten}}" cannot be deleted yet',
    chuaXoaNguoi: '{{so}} users still belong to this department. Move them to another department in User settings.',
    chuaXoaCon: 'It still has {{so}} sub-departments. Move or delete them first.',
  },

  vaiTroLop2: {
    bangCaption: "Organisation's roles",
    timNhan: 'Search roles',
    timPlaceholder: 'Search by role code or name',
    cotVaiTro: 'Role',
    cotQuyen: 'Permissions',
    cotNguoiDung: 'Users',
    cotTaoLuc: 'Created',
    soQuyen: 'Permissions: {{so}}',
    themModule: '+{{so}} more',
    chuaCoQuyen: 'No permissions',
    chuaAiDung: 'Unassigned',
    loiDanhSach: 'Could not load roles: {{loi}}',
    rongThat: 'Your organisation has no roles yet',
    rongThatMo:
      'A role groups the permissions for a job, such as Accountant or Profile reviewer. Create a role, then assign it to users under User settings.',
    rongLoc: 'No roles match “{{tuKhoa}}”',
    taoVaiTroDauTien: 'Create the first role',
    dangHien: 'Roles: {{so}}',
    dangHienLoc: 'Showing {{so}} of {{tong}} roles',
    phanQuyen: 'Edit permissions',
    doiTen: 'Rename',
    xoaVaiTro: 'Delete role',
    taoVaiTro: 'Create role',
    taoTieuDe: 'Create role',
    dangTao: 'Creating...',
    fTen: 'Role name',
    fTenGoiY: 'The name users see, such as Chief accountant.',
    fTenTrong: 'Enter a role name.',
    fMa: 'Role code',
    fMaTuSinh: 'Generated from the name. Cannot be changed after the role is created.',
    fMaChoTen: 'Type a name to generate the code.',
    fTenKhongSinhMa: 'The name needs at least one letter or digit to generate a code.',
    fTrungMa: 'A role with code {{ma}} already exists. Choose a different name.',
    fMaKhongSua: 'The role code cannot be changed after the role is created.',
    fQuaDai: 'Longer than {{so}} characters. Shorten it.',
    fQuyen: 'Permissions (optional)',
    fQuyenGoiY: 'Leave empty to set permissions later with Edit permissions on the role row.',
    daChonSoQuyen: 'Selected: {{so}}',
    doiTenTieuDe: 'Rename role {{ten}}',
    luuTen: 'Save name',
    phanQuyenTieuDe: 'Permissions for {{ten}}',
    phanQuyenMo: 'Choose permissions module by module. Tick the box at the top of a module to select or clear all of it. Role:',
    luuPhanQuyen: 'Save permissions',
    quyenNgoaiDanhMuc:
      'This role holds permissions from modules removed from your organisation ({{so}}). Saving drops them:',
    dangTaiQuyen: 'Loading permission catalog',
    loiDanhMucQuyen: 'Could not load the permission catalog: {{loi}}',
    danhMucRong: 'No permissions to assign yet',
    danhMucRongMo:
      'Your organisation has no modules installed. Permissions appear here once the platform team installs a module for you.',
    quyenNenTang: {
      nhom: 'Module management',
      xem: 'View module list',
      nop: 'Submit module packages',
      cai: 'Install, upgrade and uninstall modules',
    },
    xnXoaTieuDe: 'Delete role {{ten}}?',
    xnXoaThanCoNguoi:
      'Users with this role: {{so}}. They immediately lose every permission it grants. This cannot be undone.',
    xnXoaThanChuaAi: 'Nobody has this role. Deleting it cannot be undone.',
    ganTieuDe: 'Assign roles to {{ten}}',
    ganMo: 'The user gets every permission of the selected roles.',
    ganLegend: "User's roles",
    ganXungDot:
      "Another administrator just changed {{ten}}'s roles. The latest version is loaded — review your selection and save again.",
    ganRong: 'Your organisation has no roles yet',
    ganRongMo: 'Create a role under Role settings first, then come back here to assign it.',
    denCauHinhVaiTro: 'Go to Role settings',
    dangTaiVaiTro: 'Loading roles',
    loiTaiVaiTroNguoi: "Could not load the user's roles: {{loi}}",
    daChonSoVaiTro: 'Selected: {{so}}',
    luuVaiTro: 'Save roles',
    daTao: 'Created role {{ten}}.',
    daDoiTen: 'Renamed the role to {{ten}}.',
    daLuuQuyen: 'Saved permissions for {{ten}}.',
    daXoa: 'Deleted role {{ten}}.',
    daGanVaiTro: 'Saved the roles of {{ten}}.',
  },
  // ─────────── Platform roles — ADR-0074, G2-D1-19 ───────────
  vaiTroNenTang: {
    bangCaption: 'Platform staff roles',
    timNhan: 'Search platform roles',
    timPlaceholder: 'Search by role code or name',

    cotVaiTro: 'Role',
    cotQuyen: 'Permissions',
    cotNhanSu: 'Staff',
    cotTaoLuc: 'Created',
    nhanHeThong: 'System',
    chuaCoQuyen: 'No permissions',
    chuaAiDung: 'Unassigned',
    soQuyen: '{{so}} permissions',
    themPhanHe: '+{{so}} areas',
    dangHien: 'Showing {{so}} roles',
    dangHienLoc: 'Showing {{so}} of {{tong}} roles',

    rongThat: 'No platform roles yet',
    rongThatMo: 'Create a role to group permissions by task, then assign it to platform staff.',
    rongLoc: 'No roles match “{{tuKhoa}}”',
    loiDanhSach: 'Could not load the role list: {{loi}}',

    taoVaiTro: 'Create role',
    taoVaiTroDauTien: 'Create the first role',
    taoTieuDe: 'Create platform role',
    dangTao: 'Creating...',
    doiTen: 'Rename',
    doiTenTieuDe: 'Rename role {{ten}}',
    luuTen: 'Save name',
    phanQuyen: 'Permissions',
    phanQuyenTieuDe: 'Permissions for {{ten}}',
    phanQuyenMo: 'Choose permissions for role',
    luuPhanQuyen: 'Save permissions',
    xoaVaiTro: 'Delete role',

    fTen: 'Role name',
    fTenGoiY: 'Display name, for example Infrastructure operations.',
    fTenTrong: 'Enter a role name.',
    fMa: 'Role code',
    fMaTuSinh: 'Generated from the name. Cannot change after creation: every permission check reads it.',
    fMaChoTen: 'Type a name to generate the code.',
    fTenKhongSinhMa: 'The name needs at least one letter or digit to generate a code.',
    fTrungMa: 'A role with code {{ma}} already exists. Choose a different name.',
    fMaKhongSua: 'The code cannot change after creation: every permission check reads it.',
    fQuaDai: 'At most {{so}} characters.',
    fQuyen: 'Permissions (optional)',
    fQuyenGoiY: 'Choose now, or leave empty and set permissions later from the row menu.',

    dangTaiQuyen: 'Loading the permission catalogue',
    loiDanhMucQuyen: 'Could not load the permission catalogue: {{loi}}',
    danhMucRong: 'The server returned an empty permission catalogue. This is a server error, not missing configuration.',
    daChonSoQuyen: 'Selected: {{so}}',
    heThongChiXem: 'System roles are the fallback when configuration goes wrong, so their permissions are read-only. Create a new role if you need a different set.',
    quyenNgoaiDanhMuc: '{{so}} permissions of this role are no longer in the catalogue and will be dropped on save:',

    nhomTenant: 'Customer organisations',
    nhomGoi: 'Service plans',
    nhomLicense: 'Licences',
    nhomViec: 'Job queue',
    nhomAudit: 'Audit log',
    nhomChinhSach: 'Platform policy',
    nhomBaoMat: 'Organisation security',
    nhomLienHe: 'Contacts',
    nhomNhanSu: 'Platform staff',
    nhomVaiTro: 'Platform roles',
    nhomKhoaApi: 'API keys',
    nhomModelAi: 'AI models',
    nhomModule: 'Modules',

    xnXoaTieuDe: 'Delete role {{ten}}?',
    xnXoaThanChuaAi: 'Nobody holds this role, so no one loses access. Deleting cannot be undone.',
    xnXoaThanConNguoiGiu: '{{so}} people still hold this role, so it cannot be deleted yet. Remove the role from them on the Users screen first, so you can see who loses access.',
    xnXoaThanHeThong: 'System roles are the fallback when configuration goes wrong, so they cannot be deleted. Create a new role if you need a different set.',

    ganTieuDe: 'Platform roles of {{ten}}',
    ganMo: 'Once roles are assigned, this person has exactly the permissions of the selected roles. Clearing every box returns them to the default permissions of their account group.',
    ganLegend: 'Choose platform roles',
    ganXungDot: 'Another administrator just changed the roles of {{ten}}. The list has been reloaded to the latest version; review it and save again.',
    ganRong: 'No platform roles to assign yet',
    ganRongMo: 'Create a role on the Platform roles screen, then come back and assign it.',
    denManVaiTro: 'Go to Platform roles',
    dangTaiVaiTro: 'Loading roles',
    loiTaiVaiTroNhanSu: 'Could not load roles: {{loi}}',
    daChonSoVaiTro: 'Selected: {{so}}',
    luuVaiTro: 'Save roles',

    daTao: 'Created role {{ten}}.',
    daDoiTen: 'Renamed the role to {{ten}}.',
    daLuuQuyen: 'Saved the permissions of {{ten}}.',
    daXoa: 'Deleted role {{ten}}.',
    daGanVaiTro: 'Saved the roles of {{ten}}.',
  },
  khoaApi: {
    bangCaption: 'API keys of the organisation',
    timNhan: 'Search API keys',
    timPlaceholder: 'Search by name or key prefix',
    locTrangThai: 'Filter by status',
    tatCaTrangThai: 'All statuses',
    cotKhoa: 'Key',
    cotQuyen: 'Permissions',
    cotTrangThai: 'Status',
    cotHetHan: 'Expires',
    cotDungLanCuoi: 'Last used',
    cotTaoLuc: 'Created',
    tienToHien: '{{tienTo}}…',
    soQuyen: 'Permissions: {{so}}',
    themModule: '+{{so}} more',
    khongHetHan: 'Never',
    chuaDung: 'Never used',
    ttHoatDong: 'Active',
    ttHetHan: 'Expired',
    ttDaThuHoi: 'Revoked',
    loiDanhSach: 'Could not load API keys: {{loi}}',
    rongThat: 'Your organisation has no API keys yet',
    rongThatMo: 'Create one key per external system that calls your organisation’s APIs, so each can be revoked on its own.',
    rongLoc: 'No API keys match the filters',
    taoKhoaDauTien: 'Create the first API key',
    dangHien: 'Keys: {{so}}',
    dangHienLoc: '{{so}} of {{tong}} keys',
    taoKhoa: 'Create API key',
    taoTieuDe: 'Create API key',
    dangTao: 'Creating...',
    fTen: 'System that will use the key',
    fTenGoiY: 'For example: Public service portal, Document management.',
    fTenTrong: 'Enter the name of the system that will use the key.',
    fTenDai: 'Longer than {{so}} characters. Shorten it.',
    fHan: 'Expiry',
    fHanGoiY: 'The key expires on {{ngay}}.',
    fHanKhongGoiY: 'The key works until it is revoked. An expiry limits the damage if the key leaks.',
    han30: '30 days',
    han90: '90 days',
    han180: '180 days',
    han365: '1 year',
    hanKhong: 'Never expires',
    fQuyen: 'Permissions',
    fQuyenGoiY: 'Pick only what the external system needs. Keys can call module APIs only, never admin APIs.',
    fQuyenTrong: 'Pick at least one permission.',
    daChonSoQuyen: 'Selected: {{so}}',
    loiTheoO: 'The key wasn’t created. Fix the highlighted fields and try again.',
    biMatTieuDe: 'API key for {{ten}}',
    biMatCanhBao:
      'This is the only time the key is shown. Copy it into the external system’s secret store — the server keeps only a hash and can’t show it again. If it’s lost, revoke it and create a new one.',
    biMatNhan: 'API key',
    biMatGoiY: 'Send the key in the X-Api-Key header when calling module APIs.',
    saoChep: 'Copy',
    daChep: 'Copied',
    daChepToast: 'Copied the API key.',
    khongChepDuoc: 'Your browser blocked copying. Select the key in the box and copy it manually.',
    daLuuKhoa: 'I’ve saved the key',
    thuHoiKhoa: 'Revoke key',
    dangThuHoi: 'Revoking...',
    xnTieuDe: 'Revoke the key "{{ten}}"?',
    xnThan:
      'Systems using the key {{tienTo}}… will be rejected within seconds. This can’t be undone — create a new key if you need one.',
    daThuHoi: 'Revoked the key "{{ten}}".',
    loiKhongCon: 'This key no longer exists in your organisation — it may have just been revoked in another session. The key list has been reloaded.',
  },
  mucLog: {
    locService: 'Filter by service',
    moiService: 'All services',
    gioiHan: 'Up to {{so}} levels per service, {{phut}} minutes at most.',
    datMuc: 'Set log level',
    suaMuc: 'Edit level',
    xoaMuc: 'Delete log level',
    bangCaption: 'Temporary log levels in effect',
    cotService: 'Service',
    cotLogger: 'Logger',
    cotMuc: 'Level',
    cotConLai: 'Remaining',
    cotLyDo: 'Reason',
    cotCapNhat: 'Updated',
    heThong: 'System',
    toiKhiXoa: 'Until deleted',
    daHetHan: 'Expired',
    gioPhut: '{{gio}} h {{phut}} min',
    hetHanLuc: 'Expires at {{luc}}',
    soPhut: '{{so}} min',
    rong: 'No temporary log levels',
    rongMo: 'Every service runs at the log levels in its base configuration. To investigate an incident, set DEBUG with a time limit on the right logger.',
    rongLoc: '{{service}} has no temporary log levels',
    loiTai: 'Could not load log levels: {{loi}}',
    loiDanhMuc: 'Could not load the service list: {{loi}}',
    dangTaiDanhMuc: 'Loading the service list...',
    dialogDat: 'Set a temporary log level',
    dialogSua: 'Edit the log level of {{logger}}',
    fService: 'Service',
    fLogger: 'Logger',
    fLoggerGoiY: 'Pick a suggestion or type a package or class name. ROOT means every logger.',
    fLoggerViDu: 'vn.foxai.platform.tenant',
    fMuc: 'Level',
    fMucGoiY: 'DEBUG and TRACE log heavily, so they always have a time limit.',
    fThoiHan: 'Duration',
    fThoiHanGoiY: 'Counted from when you save, {{phut}} minutes at most. When it ends, the logger returns to its base level.',
    fLyDo: 'Reason',
    fLyDoGoiY: 'Name the incident or ticket, so others know when it can be removed.',
    fLyDoViDu: 'Investigating notification emails for tenant Hoa Binh, ticket HT-2291',
    nutDatTrong: 'Set {{muc}} for {{phut}} min',
    nutDatToiKhiXoa: 'Set {{muc}} until deleted',
    daDat: 'Set {{logger}} to {{muc}}.',
    daXoa: 'Deleted the log level of {{logger}}.',
    xnXoaTieuDe: 'Delete the log level of {{logger}}?',
    xnXoaThan: 'Logger {{logger}} of {{service}} returns to its base level on every instance within seconds. This cannot be undone; to set it again, create a new level.',
    loi: {
      serviceTrong: 'Choose the service whose log level you want to change.',
      loggerTrong: 'Enter a logger name, for example vn.foxai.platform.tenant or ROOT.',
      loggerQuaDai: 'Logger names are at most {{so}} characters.',
      loggerSaiMau: 'A logger name is a Java package or class name with dot-separated parts (for example org.hibernate.SQL), or ROOT.',
      tatBaoVe: 'Logging cannot be turned off for {{logger}}: it covers an audit or security logger that must stay on. Choose ERROR to reduce logging instead.',
      thoiHanBatBuoc: '{{muc}} needs a time limit so detailed logging does not run forever. Choose a duration.',
      thoiHanQuaTran: 'The longest duration is {{phut}} minutes. Choose a shorter one.',
      lyDoTrong: 'Enter a reason for changing the log level.',
      lyDoQuaDai: 'Reasons are at most {{so}} characters.',
      xungDot: 'Someone else just changed this log level. The list has been reloaded — close this dialog and act on the latest row.',
    },
  },
  nhanSuNenTang: {
    batBuoc: 'Required',
    bangCaption: 'List of platform staff accounts',
    cotNhanSu: 'Staff',
    cotVaiTro: 'Account group',
    cotVaiTroNenTang: 'Roles',
    chuaGanVaiTro: 'None',
    themVaiTro: '+{{so}}',
    cotMfa: 'Two-factor',
    cotTao: 'Created',
    ban: '(you)',
    tatCaTrangThai: 'All statuses',
    trangThai: {
      CHO_DUYET: 'Awaiting approval',
      ACTIVE: 'Active',
      DISABLED: 'Locked',
    },
    vaiTro: {
      PLATFORM_ADMIN: 'Platform admin',
      AUDITOR: 'Auditor',
      VENDOR: 'Vendor',
    },
    tamKhoa: 'Sign-in temporarily locked',
    daCaiMfa: 'Enrolled',
    chuaCaiMfa: 'Not enrolled',
    khongRoMfa: 'Unknown',
    rongThat: 'No platform staff accounts yet',
    rongThatMo: 'The first account is created by the bootstrap script. From the second onwards, request it here and have another platform admin approve it.',
    rongLoc: 'No accounts with this status',
    loiDanhSach: 'Could not load platform staff: {{loi}}',
    loiTaiThem: 'Could not load more: {{loi}}',
    taiThem: 'Load more',
    dangHien: '{{so}} accounts',
    dangHienCoThem: 'First {{so}} accounts',
    deNghi: 'Request account',
    suaThongTin: 'Edit name',
    duyet: 'Approve account',
    doiVaiTro: 'Change account group',
    ganVaiTroNenTang: 'Assign platform roles',
    khoaTaiKhoan: 'Lock account',
    moKhoaTaiKhoan: 'Unlock account',
    khongDuyetDuoc: {
      'da-duyet': 'This account has already been approved.',
      'tu-de-nghi': 'You requested this account, so another platform admin has to approve it.',
    },
    guiLaiEmail: 'Resend password setup email',
    deNghiTieuDe: 'Request a platform staff account',
    deNghiMo: 'The account starts awaiting approval: no role yet and cannot sign in. Another platform admin must approve it before it works.',
    nhanEmail: 'Email',
    goiYEmail: 'name.surname@ops.foxai.com.vn',
    moTaEmail: 'Also the username. Cannot be changed later.',
    moTaEmailDanhRieng: 'Platform admins and auditors must use an email on the reserved platform domain.',
    nhanHoTen: 'Full name',
    goiYHoTen: 'Jane Smith',
    nhanVaiTro: 'Account group',
    moTaVaiTroThieu: 'Without an account group the request cannot be sent: the approval step needs to know which group to grant.',
    moTaNhomTaiKhoan: 'Decides which portal this person can enter. Specific roles (what they may do) are assigned after approval.',
    nhanDeNghi: 'Request account',
    duyetTieuDe: 'Approve this account?',
    duyetThan: 'Grants {{ten}} the {{vaiTro}} role and enables the account. They will be able to sign in, and must enrol in two-factor at first sign-in.',
    nhanDuyet: 'Approve account',
    khoaTieuDe: 'Lock this account?',
    khoaThan: '{{ten}} is signed out immediately and cannot sign in again. Unlocking later does not restore the sessions they had open.',
    nhanKhoa: 'Lock account',
    doiVaiTroTieuDe: "Change this person's account group?",
    doiVaiTroThan: "{{ten}}'s permissions change on their next request. They may be in the middle of something.",
    nhanDoiVaiTro: 'Change account group',
    suaTieuDe: 'Edit name',
    suaMoEmail: 'The email cannot be changed: it appears in every audit record.',
    daDeNghi: 'Requested the account {{email}}. Another platform admin needs to approve it.',
    daDuyet: "Approved {{ten}}'s account.",
    daGuiLaiEmail: 'Resent the password setup email to {{ten}}.',
    daLuu: "Saved {{ten}}'s name.",
    daDoiVaiTro: "Changed {{ten}}'s account group.",
    daKhoa: "Locked {{ten}}'s account.",
    daMoKhoa: "Unlocked {{ten}}'s account.",
    loi: {
      emailDaCo: 'This email already has a platform account.',
      domainCuaTenant: 'That email domain is mapped to a tenant. Platform accounts need their own domain.',
      domainKhongDanhRieng: 'This role only accepts an email on the reserved platform domain.',
      adminCuoiCung: 'This is the last active platform admin. Approve or unlock another platform admin first.',
      tuDuyet: 'The approver must differ from the requester. Ask another platform admin to approve this account.',
      khac: 'Could not complete: {{loi}}',
    },
  },
  triThuc: {
    chonBo: 'Select a knowledge base',
    dangTai: 'Loading...',
    loiTaiThem: 'Could not load more: {{loi}}',
    loiTaiXuong: 'Could not download “{{ten}}”: {{loi}}',
    loi: {
      danhSachBo: 'Could not load knowledge bases: {{loi}}',
      phongBan: 'Could not load departments: {{loi}}',
      nguoi: 'Could not load people in this department: {{loi}}',
      tepDoc: '.doc files cannot be read. Open it in Word, save as .docx, then upload again.',
      tepKhongNhan: 'This file type is not accepted. Use PDF, Word (.docx), Excel, PowerPoint, TXT or an image.',
      tepQuaLon: 'The file is larger than {{so}} MB. Split or compress it, then upload.',
      tepRong: 'The file is empty. Pick the right file again.',
    },
    nap: {
      CHUA_INDEX: 'Not indexed',
      CHO_INDEX: 'Queued',
      DANG_INDEX: 'Indexing',
      DA_INDEX: 'Indexed',
      LOI: 'Index failed',
      ket: 'Stuck',
      chiThongTin: 'Details only, no file attached',
      soLanThu: 'Tried {{so}} times',
    },
    loaiTep: {
      WORD: 'Word',
      EXCEL: 'Excel',
      PDF: 'PDF',
      IMAGE: 'Image',
      POWERPOINT: 'PowerPoint',
      TEXT: 'Text',
    },
    trangThaiTaiLieu: {
      DRAFT: 'Draft',
      REVIEW: 'In review',
      APPROVED: 'Approved',
      ARCHIVED: 'Archived',
    },
    trangThaiMuc: {
      cho: 'Waiting',
      dang: 'Sending...',
      xong: 'Sent',
      loi: 'Failed',
    },
    cot: {
      ten: 'Name',
      loai: 'Type',
      kichThuoc: 'Size',
      ngayTai: 'Uploaded',
      hieuLuc: 'Validity',
      trangThaiNap: 'Index status',
      phanQuyen: 'Access',
    },
    thongKe: {
      tongBo: 'Knowledge bases',
      tongBoSub: 'in this organization',
      tongTep: 'Knowledge files',
      tongTepSub: 'including {{so}} PDF files',
      phongBan: 'Departments using them',
      phongBanSub: 'allowed to use at least one base',
      capNhat: 'Last updated',
      capNhatSub: 'latest change to a base or file',
      loi: 'Could not load knowledge figures: {{loi}}',
    },
    kho: {
      moTa: 'A knowledge base groups the files the AI assistant answers from. Each base has its own chunking rule and allowed departments.',
      danhSach: 'Knowledge bases',
      timKiem: 'Search by base name or code',
      tatCaPhongBan: 'All departments',
      locPhongBan: 'Filter by department',
      loiDanhSach: 'Could not load knowledge bases: {{loi}}',
      rongThat: 'No knowledge bases yet',
      rongThatMo: 'Create the first base, then upload files for the AI assistant to use.',
      rongLoc: 'No knowledge bases match the current filters',
      taoBoDauTien: 'Create the first knowledge base',
      dangHien: 'Showing {{so}} bases',
      dangHienCoThem: 'Showing {{so}} bases, more not loaded yet',
      taoBo: 'Create knowledge base',
      moBo: 'Open knowledge base',
      suaBo: 'Edit knowledge base',
      xoaBo: 'Delete knowledge base',
      luuBo: 'Save knowledge base',
      chuaCoMoTa: 'No description.',
      quanLy: 'Managed by:',
      duocDung: 'Used by:',
      chuaPhongBanNao: 'No department yet',
      chuaNoiIndex: 'Not connected to index',
      loaiVaSo: '{{loai}} {{so}}',
      soTep: '{{so}} files',
      capNhatNgay: 'Updated {{ngay}}',
      taoTieuDe: 'Create knowledge base',
      suaTieuDe: 'Edit knowledge base “{{ten}}”',
      fMa: 'Base code',
      fMaGoiY: 'Uppercase letters, digits, - and _, up to 20 characters. Cannot be changed after creation.',
      fTen: 'Base name',
      fMoTa: 'Description',
      fQuanLy: 'Managing department',
      fDuocDung: 'Allowed departments',
      fDuocDungGoiY: 'The assistant answers from this base only for people in these departments. Leave empty and nobody can use it yet.',
      chonPhongBan: 'Select a department',
      moiPhongBan: 'Select one or more departments',
      fAnNgoaiHieuLuc: 'Hide files outside their validity period',
      fAnNgoaiHieuLucMo: 'The assistant skips files that have expired or are not yet valid.',
      chunkTieuDe: 'Chunking rule',
      chunkMo: 'Applies to every file indexed into this base. Can only be set when the base is created.',
      chunkChiLucTao: 'The chunking rule is set at creation and cannot be changed.',
      chunkTuDong: 'Automatic',
      chunkThuCong: 'Manual',
      chunkTuDongMo: 'The system picks how to split, which suits most documents.',
      chunkThuCongMo: 'Set the chunk size and overlap yourself.',
      chunkThuCongTomTat: 'Manual: {{kichThuoc}}-character chunks, {{chongLan}}-character overlap',
      fKichThuoc: 'Chunk size (characters)',
      fChongLan: 'Overlap (characters)',
      chunkGoiY:
        'Larger chunks keep ideas whole but return fewer passages; smaller chunks do the opposite. Long clauses suit 1,200–2,000; short Q&A suits 400–700.',
      fCauTruc: 'Let AI build headings before splitting',
      fCauTrucMo: 'Helps with .docx files without headings: splits by section instead of by length.',
      loiMaTrong: 'Enter a base code.',
      loiMaDai: 'The base code can be at most 20 characters.',
      loiMaSai: 'Use only uppercase letters, digits, - and _.',
      loiTenTrong: 'Enter a base name.',
      loiQuanLyTrong: 'Select the managing department.',
      loiKichThuocThieu: 'Enter a chunk size.',
      loiKichThuocNgoai: 'Chunk size must be between {{min}} and {{max}} characters.',
      loiChongLanThieu: 'Enter an overlap.',
      loiChongLanNgoai: 'Overlap must be between {{minCl}} and {{maxCl}} characters.',
      loiChongLanQuaLon: 'Overlap must be smaller than the chunk size.',
      daTao: 'Created knowledge base “{{ten}}”.',
      daLuu: 'Saved knowledge base “{{ten}}”.',
      daXoa: 'Deleted knowledge base “{{ten}}”.',
      xoaTieuDe: 'Delete this knowledge base?',
      xoaThan:
        '“{{ten}}” is deleted for good with its {{so}} files, all its folders and review documents. The assistant stops answering from these files. This cannot be undone.',
    },
    chiTiet: {
      phuDe: '{{ma}} · Managed by {{phongBan}}',
      khongDoc: 'Knowledge base',
      khongThay: 'Knowledge base not found',
      khongThayMo: 'It may have been deleted. Go back to the list to pick another base.',
      loi: 'Could not load the knowledge base: {{loi}}',
      chuaNoiIndex:
        'The search store for this base could not be created, so uploaded files cannot be indexed yet. Tell the operations team, with the last error: {{loi}}',
      noiDung: 'Folders and files in the base',
      timKiem: 'Search folders, files...',
      moiLoai: 'All file types',
      dangTimCaBo: 'Searching the whole base.',
      duongDan: 'Folder path',
      bangCaption: 'Folders and files in {{ten}}',
      rongLoc: 'No folders or files match the current filters',
      thuMucTrong: 'This folder is empty',
      boTrong: 'This knowledge base has no files yet',
      trongMo: 'Upload files for the AI assistant to use.',
    },
    thuMuc: {
      gocBo: 'Base root',
      loai: 'Folder',
      mo: 'Open folder',
      doiTen: 'Rename folder',
      xoa: 'Delete folder',
      tao: 'Create folder',
      taoTieuDe: 'Create folder',
      doiTenTieuDe: 'Rename folder “{{ten}}”',
      fTen: 'Folder name',
      fTenViDu: 'e.g. Contracts 2026',
      namTrong: 'The new folder goes in {{noi}}.',
      loiTenTrong: 'Enter a folder name.',
      loiDoc: 'Could not load the folder tree: {{loi}}',
      daTao: 'Created folder “{{ten}}”.',
      daDoiTen: 'Renamed the folder to “{{ten}}”.',
      daXoa: 'Deleted folder “{{ten}}”.',
      daLuuQuyen: 'Saved who can use folder “{{ten}}”.',
      xoaTieuDe: 'Delete this folder?',
      xoaThan:
        'Folder “{{ten}}” and all its subfolders are deleted. Files inside are kept and moved to the base root. This cannot be undone.',
    },
    tep: {
      taiLenTieuDe: 'Upload files',
      taiVao: 'Files go into {{noi}} and are indexed for the assistant right away, without review.',
      keoTha: 'Drag files here, or click to choose',
      keoThaMo: 'PDF, Word (.docx), Excel, PowerPoint, TXT or images; up to {{so}} MB per file.',
      khongNhan: 'These files were not added:',
      tepVaLyDo: '{{ten}}: {{lyDo}}',
      daChon: '{{so}} files selected',
      boTep: 'Remove file {{ten}}',
      loiChuaChon: 'Choose at least one file.',
      chuaVaoThuMuc:
        'Uploaded {{ten}} but could not move it into the folder; it is in the base root. Use “Move to folder” instead of uploading again, so you do not get two copies.',
      taiLen: 'Upload',
      taiLenSo: 'Upload {{so}} files',
      dangTaiLen: 'Uploading...',
      fHieuLucTu: 'Valid from',
      fHieuLucDen: 'Valid until',
      fHieuLucDenGoiY: 'Leave empty: valid indefinitely.',
      chonNgay: 'Select date',
      khongThoiHan: 'No end date',
      khoangHieuLuc: '{{tu}} → {{den}}',
      hetHieuLuc: 'Expired',
      chuaToiHieuLuc: 'Not yet valid',
      loiThieuTu: 'Select the start date.',
      loiDenKhongSauTu: 'The end date must be after the start date.',
      hieuLucTieuDe: 'Edit validity',
      hieuLucGoiY: 'Leave both dates empty: the file applies with no time limit.',
      luuHieuLuc: 'Save validity',
      suaHieuLuc: 'Edit validity',
      taiXuong: 'Download',
      dongBoLai: 'Re-index',
      dongBoLaiMo: 'Index the file again for the assistant',
      chuyenThuMuc: 'Move to folder',
      chuyenThuMucTieuDe: 'Move file to another folder',
      fThuMucDich: 'Destination folder',
      chuyenBo: 'Move to another base',
      chuyenBoTieuDe: 'Move file to another knowledge base',
      fBoDich: 'Destination knowledge base',
      loiChuaChonBo: 'Select the destination knowledge base.',
      khongCoBoKhac: 'There is no other knowledge base.',
      chuyenBoHauQua:
        'The file leaves its current folder, goes to the root of the new base and is indexed again with that base’s chunking rule. This base’s assistant stops using it.',
      xoa: 'Delete file',
      xoaTieuDe: 'Delete this file?',
      xoaThan:
        '“{{ten}}” is removed from the base, from storage and from the index; the assistant stops using it. This cannot be undone.',
      loiDanhSach: 'Could not load files: {{loi}}',
      dangHienCoThem: 'Showing {{so}} files, more not loaded yet',
      daTaiLen: 'Uploaded “{{ten}}”. It is queued for indexing.',
      daTaiLenNhieu: 'Uploaded {{so}} files. They are queued for indexing.',
      daLuuHieuLuc: 'Saved the validity of “{{ten}}”.',
      daChuyenThuMuc: 'Moved “{{ten}}” to the new folder.',
      daChuyenBo: 'Moved “{{ten}}” to “{{bo}}”.',
      daBoKhoiBo: 'Removed “{{ten}}” from its knowledge base.',
      daXepDongBo: 'Queued “{{ten}}” for re-indexing.',
      loiDongBo: 'Could not re-index “{{ten}}”: {{loi}}',
      daXoa: 'Deleted file “{{ten}}”.',
    },
    quyen: {
      nguoiDuocDung: 'Who can use',
      nguoiDuocDoc: 'Who can read',
      phongBanDuocDoc: 'Departments that can read',
      thuMucTieuDe: 'Who can use folder “{{ten}}”',
      thuMucMo:
        'Files in this folder can only be granted to the people chosen here. Removing someone also removes their access to subfolders and files inside.',
      nguoiTepTieuDe: 'Who can read “{{ten}}”',
      nguoiTepMo:
        'The assistant answers from this file only for the people chosen here, and they must also be in a department allowed to use the file.',
      phongBanTepTieuDe: 'Departments that can read “{{ten}}”',
      phongBanTepMo:
        'Only people in the selected departments get answers from this file. Clear all and no department can read it.',
      phongBanTepMoLanDau:
        'The file currently follows the base’s departments. Once saved, it uses this list and cannot go back to following the base.',
      daChon: '{{so}} people selected',
      chuaChonAi: 'Nobody selected.',
      boNguoi: 'Remove {{ten}}',
      fPhongBan: 'Pick people by department',
      phongTrong: 'This department has nobody yet.',
      chonCaPhong: 'Select the whole department ({{so}} people)',
      chuaCoPhongBan: 'The organization has no departments yet.',
      luuNguoi: 'Save {{so}} people',
      luuPhongBan: 'Save {{so}} departments',
      loiDoc: 'Could not load who currently has access: {{loi}}',
      nguoiDaChon: 'Readers selected',
      chuaChonNguoi: 'No readers selected',
      phongBanTheoBo: 'Departments from base',
      khongPhongBanNao: 'No department',
      soPhongBan: '{{so}} departments',
      thuMucDaDat: 'People selected',
      thuMucChuaDat: 'From base',
      daLuuNguoi: 'Saved who can read “{{ten}}”.',
      daLuuPhongBan: 'Saved the departments that can read “{{ten}}”.',
    },
    taiLieu: {
      bo: 'Base:',
      phienBan: 'Version:',
      tep: 'File:',
      ngayTai: 'Uploaded:',
      capNhat: 'Updated:',
      xetLuc: 'Last reviewed:',
      khongKemTep: 'No file attached',
      tienTrinh: 'Review progress',
      ghiChuXet: 'Note from the latest review',
      ghiChuXetLuc: 'Note from the review on {{ngay}}',
      tomTat: 'Content summary',
      chuaCoTomTat: 'No summary yet.',
      lichSu: 'Version history',
      chuaCoLichSu: 'No version history yet.',
      khongGhiChu: 'No note',
      khongThay: 'Document not found. It may have been deleted with its knowledge base.',
      loiChiTiet: 'Could not load the document: {{loi}}',
      taiXuong: 'Download',
      taoPhienBan: 'Create new version',
      phienBanTieuDe: 'Create a new version of “{{ten}}”',
      phienBanMo:
        'The current version is {{phienBan}}. A new version bumps the number and moves the document back to draft; it must be submitted for review again.',
      fThayDoi: 'What changed in this version',
      fTomTat: 'Content summary',
      loiThayDoiTrong: 'Describe what changed.',
      fGhiChu: 'Note for the author',
      fGhiChuTuyChon: 'Optional.',
      fGhiChuViDu: 'Describe exactly what needs to change...',
      loiGhiChuTrong: 'Say what needs fixing so the author knows.',
      guiDuyet: 'Submit for review',
      duyet: 'Approve document',
      traNhap: 'Return to draft',
      yeuCauSua: 'Request changes',
      luuTru: 'Archive document',
      quayLai: 'Roll back one step',
      duyetTieuDe: 'Approve this document?',
      duyetThan: '“{{ten}}” becomes a file in “{{bo}}” and is queued for indexing; the assistant can use it once indexing finishes.',
      traNhapTieuDe: 'Return this document to draft?',
      traNhapThan: '“{{ten}}” goes back to draft. The author revises it and submits again.',
      yeuCauSuaTieuDe: 'Request changes to this document?',
      yeuCauSuaThan: '“{{ten}}” goes back to draft with the note below for the author.',
      luuTruTieuDe: 'Archive this document?',
      luuTruThan:
        '“{{ten}}” is archived. The file already in “{{bo}}” stays as it is; delete it on the knowledge base screen if the assistant should stop using it.',
      quayLaiTieuDe: 'Move this document back one step?',
      quayLaiThan: '“{{ten}}” goes back to {{trangThai}}. Any file already in the knowledge base stays as it is.',
      daGuiDuyet: 'Submitted “{{ten}}” for review.',
      daDuyet: 'Approved “{{ten}}”. The file is queued for indexing.',
      daTraNhap: 'Returned “{{ten}}” to draft.',
      daYeuCauSua: 'Requested changes to “{{ten}}”.',
      daLuuTru: 'Archived “{{ten}}”.',
      daQuayLai: 'Moved “{{ten}}” back one step.',
      daTaoPhienBan: 'Created version {{phienBan}} of “{{ten}}”.',
    },
    duyet: {
      tieuDe: 'Knowledge review & approval',
      moTa: 'Review documents before they become files in a knowledge base, and track each document’s versions.',
      taiTaiLieu: 'Upload documents',
      danhSach: 'Documents',
      chiTiet: 'Document details',
      timKiem: 'Search by title',
      moiTrangThai: 'All statuses',
      loiDanhSach: 'Could not load documents: {{loi}}',
      rongThat: 'No documents yet',
      rongThatMo: 'Documents uploaded on the Upload documents screen appear here for review.',
      rongLoc: 'No documents match the current filters',
      dangHien: 'Showing {{so}} documents',
      dangHienCoThem: 'Showing {{so}} documents, more available',
      chuaChon: 'Select a document to view',
      chuaChonMo: 'Details, version history and review actions appear here.',
    },
    upload: {
      moTa: 'Documents uploaded here enter the review flow as drafts. Once approved, a document becomes a file in its knowledge base.',
      moKiemDuyet: 'Open review screen',
      khongCoQuyen: 'This account cannot upload documents yet. Ask an organization admin for permission to create knowledge documents.',
      anThongBao: 'Dismiss',
      hangDoi: 'Upload queue',
      boDaXong: 'Remove {{so}} sent items',
      datBoTatCa: 'Set one base for all items',
      guiTatCa: 'Send {{so}} documents',
      guiMuc: 'Send',
      boKhoiHang: 'Remove {{ten}} from the queue',
      rong: 'The queue is empty',
      rongMo: 'Drag files onto the area above to add them to the queue.',
      chuaCoBo: 'The organization has no knowledge base yet. Create one on the Knowledge management screen before sending.',
      fBo: 'Knowledge base',
      fTieuDe: 'Title',
      fTomTat: 'Content summary',
      fTomTatGoiY: 'Optional. Reviewers read this before opening the file.',
      loiChuaChonBo: 'Select a knowledge base.',
      loiTieuDeTrong: 'Enter a document title.',
      loiTieuDeDai: 'The title can be at most {{so}} characters.',
      daTaiLen: 'Sent {{so}} documents to review.',
    },
  },

  // ─────────── Cấu hình xử lý văn bản (module van-ban, GN-D2-3 → GN-D2-5) ───────────
  vanBanChung: {
    dong: 'Close',
    luuThayDoi: 'Save changes',
    themMoi: 'Add',
  },
  vanBanDonVi: {
    tieuDe: 'Units and roles by level',
    moTa: 'Manage agencies, departments and individuals with their functions, duties and rating, used by AI to assign documents',
    timTheo: 'Search by name, duty...',
    tatCaLoai: 'All types',
    locLoai: 'Filter by type',
    thongKe: {
      tong: 'Total',
      coQuan: 'Agencies',
      phongBan: 'Departments',
      caNhan: 'Individuals',
    },
    cot: {
      ten: 'Name',
      loai: 'Type',
      nhiemVu: 'Main functions and duties',
      phuTrach: 'Person in charge',
      trucThuoc: 'Reports to',
      diem: 'Rating',
    },
    loai: {
      CO_QUAN: 'Agency',
      PHONG_BAN: 'Department',
      CA_NHAN: 'Individual',
    },
    bangCaption: 'Agencies, departments and individuals',
    rongThat: 'No agencies, departments or individuals yet',
    rongThatMo: 'Add levels so AI can match their functions and duties when assigning documents.',
    rongLoc: 'No units match the filter',
    loiDanhSach: 'Could not load the list: {{loi}}',
    dialogThem: 'Add agency / department / individual',
    dialogSua: 'Edit level',
    fTen: 'Name',
    fTenGoiY: 'Agency / department / individual name',
    fLoai: 'Type',
    fDiem: 'Rating (0 - 10)',
    fPhuTrach: 'Person in charge',
    fPhuTrachGoiY: 'Full name of the head of the agency / department',
    fTrucThuoc: 'Reports to',
    khongTrucThuoc: '— None —',
    fNhiemVu: 'Main functions and duties',
    fNhiemVuGoiY: 'Describe the main functions and duties...',
    loiTenTrong: 'Enter the name of the agency, department or individual.',
    loiNhiemVuTrong: 'Enter the main functions and duties.',
    loiDiemSai: 'Rating must be between 0 and 10.',
    sua: 'Edit',
    xoa: 'Delete',
    xacNhanXoa: 'Delete {{loai}} "{{ten}}"?',
    xacNhanXoaMo: 'Units under it will no longer have a parent. This cannot be undone.',
    nutXoa: 'Delete {{loai}}',
    daThem: 'Added.',
    daLuu: 'Changes saved.',
    daXoa: 'Deleted.',
  },
  vanBanMau: {
    tieuDe: 'Document templates',
    moTa: 'Manage administrative document templates following Decree 30/2020/NĐ-CP, used by Document drafting',
    them: 'Add template',
    timTheo: 'Search templates...',
    tatCaNguon: 'All sources',
    locNguon: 'Filter by source',
    nguonNd30: 'Decree 30/2020/NĐ-CP',
    nguonTuyChinh: 'Custom',
    thongKe: {
      tong: 'Total templates',
      nd30: 'Decree 30 templates',
      tuyChinh: 'Custom templates',
      dangDung: 'In use',
    },
    cot: {
      ten: 'Template name',
      kyHieu: 'Symbol',
      nguon: 'Source',
    },
    trangThai: {
      DANG_DUNG: 'In use',
      NGUNG_DUNG: 'Not in use',
    },
    kyHieuMau: 'No.: …/{{ma}}-…',
    bangCaption: 'Document templates',
    rongThat: 'No document templates yet',
    rongThatMo: 'Add a custom template to use it in Document drafting.',
    rongLoc: 'No templates match the filter',
    loiDanhSach: 'Could not load templates: {{loi}}',
    xem: 'View template',
    sua: 'Edit template',
    xoa: 'Delete template',
    xacNhanXoa: 'Delete template "{{ten}}"?',
    xacNhanXoaNd30: 'This is a base template under Decree 30/2020/NĐ-CP. It is removed from your organization and cannot be restored.',
    xacNhanXoaMo: 'This cannot be undone.',
    xemTieuDe: 'View template: {{ten}}',
    dungDeSoan: 'Draft with this template',
    tTenMau: 'Template name',
    tKyHieu: 'Document symbol',
    tNguon: 'Source',
    tMoTa: 'Description',
    cacTruong: 'Fields ({{so}})',
    aiDien: 'AI fills',
    boCucTieuDe: 'Layout',
    boCucNguon: '(Article 8, Decree 30/2020/NĐ-CP)',
    boCuc: {
      '1': 'National title and motto',
      '2': 'Name of the issuing agency or organization',
      '3': 'Document number and symbol',
      '4': 'Place and date of issue',
      '5': 'Document type and summary',
      '6': 'Document content',
      '7': 'Position, full name and signature of the authorized person',
      '8': 'Seal and digital signature of the agency or organization',
      '9': 'Recipients',
    },
    xemTruocNoiDung: 'Template preview',
    xemTruocAi: 'AI-generated preview',
    xemTruocTep: 'Template preview (from file: {{tep}})',
    taoXemTruoc: 'Generate AI preview',
    dangTaoXemTruoc: 'Generating...',
    taoLai: 'Regenerate',
    chuaCoNoiDung: 'This template has no static content. Click "Generate AI preview" to create a sample document following Decree 30, or build the content in Document drafting.',
    chuaCoNoiDungKhongAi: 'This template has no static content. AI preview is not enabled on this installation; build the content in Document drafting.',
    daTaoXemTruoc: 'AI generated a preview for the template.',
    daThem: 'New template added.',
    daLuu: 'Template changes saved.',
    daXoa: 'Template deleted.',
    kieu: {
      TEXT: 'Text',
      TEXTAREA: 'Paragraph',
      DATE: 'Date',
      NUMBER: 'Number',
      SELECT: 'Choice list',
    },
  },
  vanBanMauForm: {
    dialogThem: 'Add document template',
    dialogSua: 'Edit document template',
    nutThem: 'Add template',
    thongTinChung: 'General information',
    fTen: 'Template name',
    fTenGoiY: 'E.g. Telegram, Transfer slip, Memorandum...',
    fKyHieu: 'Symbol',
    fKyHieuGoiY: 'E.g. CĐ, PC...',
    fTrangThai: 'Status',
    fMoTa: 'What the template is for',
    fMoTaGoiY: 'Describe the purpose and scope of the template...',
    loiTenTrong: 'Enter the template name.',
    theThuc: 'Document format (Article 8, Decree 30/2020/NĐ-CP)',
    theThucMo: 'Builds the header (issuing agency, number, symbol, place) and the signature block. Changes show right away in the preview on the right.',
    canGiua: 'Center the national title and motto',
    canGiuaMo: '(for applications and forms, without an agency)',
    fCqch: 'Governing agency (parent)',
    fCqchGoiY: 'E.g. BỘ Y TẾ',
    fCq: 'Issuing agency or organization',
    fCqGoiY: 'E.g. SỞ Y TẾ HÀ NỘI',
    fSo: 'Document number',
    fSoGoiY: 'E.g. 1234',
    fKyHieuCq: 'Agency abbreviation',
    fKyHieuCqGoiY: 'E.g. SYT',
    fDd: 'Place',
    fDdGoiY: 'E.g. Hà Nội',
    fCv: 'Signer position',
    fCvGoiY: 'E.g. GIÁM ĐỐC',
    fKy: 'Signer full name',
    fKyGoiY: 'E.g. Nguyễn Văn A',
    fNn: 'Recipients',
    fNnGoiY: 'One recipient per line, e.g.:\nNhư trên;\nUBND các quận, huyện;\nLưu: VT.',
    truong: 'Fields',
    truongMo: 'Declare fields, then click or drag a chip into the document on the right.',
    themTruong: 'Add field',
    chuaCoTruong: 'No fields yet.',
    chuaCoTruongMo: 'Click "Add field" to start.',
    fTenTruong: 'Field name {{so}}',
    fTenTruongGoiY: 'Field name (e.g. Kính gửi)',
    fKieuTruong: 'Field type {{so}}',
    fGoiYAi: 'Description or hint for AI (optional)',
    fLuaChon: 'Choices, separated by commas (e.g. Phép năm, Nghỉ ốm)',
    batBuoc: 'Required',
    aiTuDien: 'AI fills from the source document',
    aiTuDienMo: 'On: AI fills it from the source document when drafting. Off: the user types it (e.g. a signature).',
    xoaTruong: 'Delete field {{so}}',
    luuYNd30: 'Base template under Decree 30/2020/NĐ-CP: edit only the description and format.',
    xemTruoc: 'Preview, edit like Word',
    daChinhTay: 'Edited by hand',
    dungLai: 'Rebuild',
    dungLaiMo: 'Rebuild the document from the settings',
    chenTruong: 'Insert field:',
    chenTruongRong: 'declare fields in the left column to click or drag them into the document.',
    chenTruongMo: 'Click to insert at the cursor, or drag into the preview',
    vungDau: 'Document header',
    vungThan: 'Document body',
    vungKy: 'Signature block',
    goiYThan: 'Write the template content here. Click or drag fields to insert them into the document.',
  },
  vanBanCc: {
    thanhCongCu: 'Text formatting',
    dam: 'Bold',
    nghieng: 'Italic',
    gachChan: 'Underline',
    canTrai: 'Align left',
    canGiua: 'Align center',
    canPhai: 'Align right',
    canDeu: 'Justify',
    dsCham: 'Bulleted list',
    dsSo: 'Numbered list',
    coChu: 'Font size',
    co: {
      '2': 'Small',
      '3': 'Normal',
      '4': 'Large',
      '5': 'Extra large',
      '6': 'Heading',
    },
    hoanTac: 'Undo',
    lamLai: 'Redo',
    xoaDinhDang: 'Clear formatting',
  },
  vanBanTichHop: {
    tieuDe: 'Integrations & webhooks',
    moTa: 'Issue API keys so external systems (document management, public service portal, official email…) can push documents into FoxAI for automatic summaries, then receive results by webhook',
    them: 'Add integration',
    timTheo: 'Search by integration name...',
    tatCaNguon: 'All sources',
    locNguon: 'Filter by source',
    thongKe: {
      tong: 'Total integrations',
      hoatDong: 'Active',
      coWebhook: 'With webhook',
    },
    cot: {
      ten: 'Integration name',
      nguon: 'Source',
      webhook: 'Result webhook',
    },
    chuaCauHinh: 'Not configured',
    dangHoatDong: 'Active',
    daTat: 'Off',
    bangCaption: 'Integrations',
    rongThat: 'No integrations yet',
    rongThatMo: 'Add an integration to issue an API key for an external system to push documents into FoxAI.',
    rongLoc: 'No integrations match the filter',
    loiDanhSach: 'Could not load integrations: {{loi}}',
    quanLy: 'Manage (API key, webhook, log)',
    sua: 'Edit integration',
    xoa: 'Delete integration',
    xacNhanXoa: 'Delete integration "{{ten}}"?',
    xacNhanXoaMo: 'The API key stops working right away and all related webhook history is deleted. This cannot be undone.',
    daLuu: 'Integration changes saved.',
    daXoa: 'Integration deleted.',
    daSaoChep: 'Copied.',
    daSaoChepKhoa: 'API key copied.',
    daSaoChepLenh: 'Sample command copied.',
    khongSaoChep: 'Could not copy. Select the text and copy it manually.',
    nguon: {
      QLVB: 'Document management system',
      CONG_DVC: 'Public service portal',
      EMAIL: 'Official email',
      UPLOAD: 'Manual upload',
      OTHER: 'Other source',
    },
    suKien: {
      AFTER_SUMMARIZE: 'After a document is summarized',
      AFTER_COMPOSE: 'After a document is drafted',
      AFTER_OCR: 'After a voucher is OCR-ed',
      ON_ERROR: 'When processing fails',
    },
    xacThuc: {
      NONE: 'No authentication',
      API_KEY: 'API Key (X-Api-Key)',
      BEARER: 'Bearer Token',
    },
  },
  vanBanTichHopForm: {
    dialogThem: 'Add integration',
    dialogSua: 'Edit integration',
    nutTao: 'Create and issue API key',
    fTen: 'Integration name',
    fTenGoiY: 'E.g. Hệ thống QLVB&ĐH',
    fNguon: 'Source label',
    fLoaiVb: 'Default document type',
    fTuDong: 'Automatic summary',
    fTuDongMo: 'Summarize as soon as received',
    phanTiepNhan: 'Receiving documents (partner calls in)',
    fXacThucDauVao: 'Partner authentication',
    xacThucVao: {
      API_KEY: 'API Key (X-Api-Key)',
      BEARER: 'Bearer Token',
    },
    fDinhDang: 'Data format',
    dinhDang: {
      MULTIPART: 'Multipart (with file)',
      JSON: 'JSON (text only)',
    },
    tiepNhanMo: 'The receiving endpoint is always POST /inbound; the server accepts both authentication types and both formats, this setting is for display.',
    phanWebhook: 'Result webhook',
    fUrl: 'Webhook URL',
    fPhuongThuc: 'Method',
    fXacThuc: 'Authentication',
    xacThucNgan: {
      NONE: 'None',
      API_KEY: 'API Key',
      BEARER: 'Bearer',
    },
    fThuLai: 'Retries',
    fToken: 'Token / API key to send',
    fTokenSua: 'Token / API key to send (leave blank to keep)',
    fSuKien: 'Events that trigger the webhook',
    fKichHoat: 'Enable this integration',
    loiTenTrong: 'Enter the integration name.',
    loiThuLaiSai: 'Webhook retries must be between 0 and 10.',
    loiUrlSai: 'The webhook URL must start with http:// or https://',
    loiTokenTrong: 'Enter the token or API key for webhook authentication.',
  },
  vanBanKhoa: {
    tieuDe: 'API key for "{{ten}}"',
    canhBao: 'The API key is shown only once. Copy and store it now: after closing this dialog it cannot be shown again, only replaced with a new key.',
    saoChep: 'Copy',
    daLuuKhoa: 'I have saved the key',
  },
  vanBanQl: {
    tieuDe: 'Manage integration: {{ten}}',
    iNguon: 'Source label',
    iLoaiVb: 'Default document type',
    iTuDong: 'Automatic summary',
    co: 'Yes',
    khong: 'No',
    iTrangThai: 'Status',
    phanKhoa: 'Partner API key',
    capLai: 'Issue new key',
    khoaMo: 'The key is stored as a SHA-256 hash and cannot be recovered. Issuing a new key disables the old one right away.',
    xacNhanCapLai: 'Issue a new API key for "{{ten}}"?',
    xacNhanCapLaiMo: 'The old key stops working right away; the partner must switch to the new key.',
    nutCapLai: 'Issue new API key',
    phanKetNoi: 'How to connect and send documents',
    ketNoiMo: 'The external system calls the endpoint below to push documents into FoxAI, authenticated with this integration\'s API key (replace <API_KEY> with the saved key). Received documents land in Document summary with the source label "{{nguon}}".',
    chuaMoInbound: 'The partner receiving endpoint is not open on the platform Gateway yet. The details below are for preparing the integration.',
    iEndpoint: 'Endpoint',
    iDinhDang: 'Data format',
    dinhDangDai: {
      JSON: 'JSON (text content only)',
      MULTIPART: 'Multipart (with attachment)',
    },
    iHeader: 'Authentication header',
    viDu: 'Sample call',
    truongMo: 'Fields: title (required) · content or file (one of them required) · doc_type, doc_id, auto_summarize (optional). Responds 202 with summarization_id.',
    iUrl: 'URL:',
    iXacThuc: 'Authentication:',
    thuLaiToiDa: '{{xacThuc}} · up to {{so}} retries',
    kichHoatKhi: 'Triggered on:',
    guiThu: 'Send test webhook',
    xemNhatKy: 'View delivery log',
    guiThanhCong: 'Delivered, HTTP {{ma}}',
    guiThatBai: 'Delivery failed: {{loi}}',
    chuaCoWebhook: 'This integration has no webhook. Click "Edit integration" to add a result URL.',
    nhatKyRong: 'No webhook deliveries yet.',
    nkCaption: 'Webhook delivery log',
    nkSuKien: 'Event',
    nkLan: 'Attempt',
    nkKetQua: 'Result',
    nkThoiGian: 'Time',
  },

  baoCaoDoanhNghiep: {
    phanHe: {
      overview: 'Overview',
      finance: 'Finance',
      projects: 'Projects',
      sales: 'Sales',
      people: 'People',
    },
    phanHeMoTa: 'The figures the FOXAI mobile app shows for this module. Saving overwrites all of this module’s figures in the selected source.',
    khongCoQuyen: {
      tieuDe: 'You can’t view this page yet',
      moTa: 'It needs the {{quyen}} permission. Ask your organization admin to grant it, then reopen the page.',
    },
    loiTaiPhanHe: 'Couldn’t load the module figures: {{loi}}',
    chuaNhap: '{{ten}} has no figures in this source yet. Fill in the blocks below, then click Save figures to create them.',
    chuaNhapChiXem: '{{ten}} has no figures in this source yet.',
    chiXem: 'You can view this module but not edit its figures.',
    nguonDaNgungBanner: 'The source “{{ten}}” is inactive: you can view it but not write to it. Reactivate it on the Data sources page.',
    conLoi: '{{so}} fields need fixing before you can save. Each error is shown under its field.',
    khongLuuDuoc: 'Couldn’t save: {{loi}}',
    daLuuPhanHe: 'Saved the {{ten}} figures.',
    luuSoLieu: 'Save figures',
    boThayDoi: 'Discard changes',
    thongTinChung: 'General',
    tieuDePhanHe: 'Module title',
    tieuDeKhoiTaiLieu: 'Documents block title',
    tieuDeKhoiTaiLieuGoiY: 'Leave blank to use the app’s default title.',
    soTaiLieuPhanHe: 'Documents in this module:',
    capNhatLuc: 'Updated:',
    ghiChuGiaTri: 'Enter the value already formatted for Vietnamese readers (“8,42 tỷ”, “+12,4%”). The app shows it as is and does not convert it.',
    ghiChuXuHuong: 'Set it separately, not from the sign of the change: “-4,1%” on Receivables is good news.',
    ghiChuSpark: 'At least 2 points, in the same unit as the value, with the last point matching the value above. Separate with commas (7.1, 8.42) or semicolons (7,1; 8,42).',
    hero: {
      tieuDe: 'Headline card',
      nhan: 'Label',
      giaTri: 'Value',
      chuThich: 'Caption',
      delta: 'Change',
      xuHuong: 'Trend',
      spark: 'Sparkline',
      sparkGoiY: '7.1, 7.9, 8.42',
      them: 'Add headline card',
      go: 'Remove headline card',
      trong: 'No headline card. The app hides this block.',
    },
    chiSo: {
      tieuDe: 'Stat tiles',
      them: 'Add stat',
      ma: 'Code',
      nhan: 'Label',
      giaTri: 'Value',
      mauNhan: 'Accent color',
      bieuTuong: 'Icon',
      chuaDatTen: 'Untitled stat',
      trong: 'No stat tiles. The app hides this block.',
      go: 'Remove stat {{ten}}',
    },
    xepHang: {
      tieuDe: 'Ranking table',
      tenBang: 'Table title',
      them: 'Add ranking table',
      themDong: 'Add row',
      trong: 'No ranking table. The app hides this block.',
      trongDong: 'The table has no rows.',
      go: 'Remove ranking table',
      goDong: 'Remove row {{ten}}',
    },
    xuHuongGiaTri: {
      up: 'Up',
      down: 'Down',
      flat: 'Flat',
    },
    mauNhan: {
      primary: 'Primary',
      success: 'Green',
      warning: 'Amber',
      danger: 'Red',
      violet: 'Violet',
      teal: 'Teal',
      sky: 'Sky blue',
      orange: 'Orange',
    },
    bieuTuong: {
      banknote: 'Banknote',
      receipt: 'Receipt',
      droplet: 'Droplet',
      'trending-up': 'Upward arrow',
      wallet: 'Wallet',
      'chart-column': 'Column chart',
      folder: 'Folder',
      'clipboard-list': 'Checklist',
      calendar: 'Calendar',
      users: 'People',
      'user-check': 'Approved person',
      briefcase: 'Briefcase',
      target: 'Target',
      handshake: 'Handshake',
      package: 'Package',
      'file-text': 'Text page',
    },
    danhMuc: {
      finance: 'Finance',
      projects: 'Projects',
      sales: 'Sales',
      people: 'People',
      other: 'Other',
    },
    loaiTep: {
      pdf: 'PDF',
      web: 'Web page',
      document: 'Office document',
      unknown: 'Unknown',
    },
    loi: {
      batBuoc: 'Fill in this field.',
      maTrung: 'Another stat already uses this code. Choose a different one.',
      sparkSai: 'Some points aren’t numbers. Enter numbers only, separated by commas or semicolons.',
      sparkThieu: 'Enter at least 2 points.',
      urlSai: 'Enter a full link starting with http:// or https://.',
      chuaChonTep: 'Choose a file to upload.',
      tepKhongNhan: 'Only PDF, Word (.docx), Excel (.xlsx) and PowerPoint (.pptx) files are accepted.',
      tepQuaLon: 'The file is larger than 50 MB. Split or compress it, then choose it again.',
      tepRong: 'The file is empty. Choose another file.',
      nguonDaNgung: 'This source is inactive, so nothing can be written to it. Reactivate it on the Data sources page, then try again.',
      nguonMacDinhKhoa: 'The default source can’t be deactivated, and an inactive source can’t become the default. Change the default or reactivate the source first.',
      thuMucConNoiDung: 'The folder still has subfolders or documents. Move them out, then delete it again.',
      thuMucChuTrinh: 'A folder can’t be moved into itself or one of its subfolders. Choose another place.',
    },
    taiLieu: {
      moTa: 'Report documents the mobile app shows in each module, by category. Folders are only for organizing.',
      them: 'Add document',
      themDauTien: 'Add the first document',
      themTieuDe: 'Add document',
      suaTieuDe: 'Edit document',
      sua: 'Edit document',
      luuThayDoi: 'Save changes',
      daThem: 'Added the document “{{ten}}”.',
      daLuu: 'Saved the document “{{ten}}”.',
      tim: 'Search by title or summary',
      tatCaDanhMuc: 'All categories',
      locDanhMuc: 'Filter by category',
      dangHien: 'Showing {{hien}} of {{tong}} documents',
      caption: 'Report documents in {{noi}}',
      trong: 'This source has no documents yet',
      trongMoTa: 'Upload report files or add links for the mobile app to show.',
      rongLoc: 'No documents match the filters',
      loiTai: 'Couldn’t load the documents: {{loi}}',
      loiTaiThem: 'Couldn’t load more: {{loi}}',
      cotTieuDe: 'Title',
      cotDanhMuc: 'Category',
      cotLoai: 'Type',
      cotCapNhat: 'Updated',
      daChon: '{{so}} documents selected',
      boChon: 'Clear selection',
      chuyenSo: 'Move {{so}} documents',
      chuyenThuMuc: 'Move to folder',
      chuyenTieuDe: 'Move {{so}} documents to another folder?',
      chuyenToi: 'Move to',
      daChuyen: 'Moved {{so}} documents.',
      chuyenDoDang: 'Moved {{xong}}/{{tong}} documents, then stopped: {{loi}}',
      moTep: 'Open file',
      moLienKet: 'Open link',
      go: 'Remove from app',
      goTieuDe: 'Remove “{{ten}}” from the mobile app?',
      goThan: 'App users will no longer see this document, and it disappears from this list. Uploaded files are kept on the server. This page can’t show removed documents again yet.',
      daGo: 'Removed “{{ten}}” from the app.',
      noiDung: 'Document content',
      tepTaiLen: 'Upload a file',
      lienKetNgoai: 'External link',
      tep: 'File',
      tepGoiY: 'PDF or Office (.docx, .xlsx, .pptx), up to 50 MB. The app opens it through a temporary link valid for 5 minutes.',
      chonTep: 'Choose file',
      chonTepKhac: 'Choose another file',
      seThayTep: 'Replaces the current file on save',
      url: 'Link',
      urlGoiY: 'https://files.company.com/q3-report.pdf',
      ghiChuUrl: 'A full link starting with http:// or https://. http links only open on the internal network.',
      ghiChuUrlBoTep: 'Saving this link removes the attached file from the document.',
      danhMucGoiY: 'Decides which module of the app shows the document.',
      tomTat: 'Summary',
      meta: 'Description line',
      ghiChuMeta: 'Human-readable text, for example “PDF · 2.4 MB”.',
    },
    thuMuc: {
      tieuDe: 'Folders',
      tatCa: 'All documents',
      chuaPhanLoai: 'Unfiled',
      ngoaiCung: 'Top level (no parent folder)',
      chonThuMuc: 'Choose a folder',
      chuaCoThuMuc: 'No folders yet. Create folders to organize documents.',
      loiTai: 'Couldn’t load the folders: {{loi}}',
      moRong: 'Expand {{ten}}',
      thuGon: 'Collapse {{ten}}',
      them: 'Add folder',
      themCon: 'Add subfolder',
      themTieuDe: 'Add folder',
      themConTieuDe: 'Add subfolder',
      taoTrong: 'Create in: {{ten}}',
      taoThuMuc: 'Create folder',
      daTao: 'Created the folder “{{ten}}”.',
      ten: 'Folder name',
      tenGoiY: 'October reports',
      doiTen: 'Rename',
      doiTenTieuDe: 'Rename folder',
      daDoiTen: 'Renamed the folder to “{{ten}}”.',
      chuyen: 'Move to another folder',
      chuyenTieuDe: 'Move the folder “{{ten}}”',
      chuyenVao: 'New parent folder',
      chuyenNut: 'Move folder',
      daChuyen: 'Moved the folder “{{ten}}”.',
      xoa: 'Delete folder',
      xoaTieuDe: 'Delete the folder “{{ten}}”?',
      xoaThan: 'Only empty folders can be deleted. This can’t be undone.',
      daXoa: 'Deleted the folder “{{ten}}”.',
      trong: 'This folder has no documents yet',
      trongMoTa: 'Add documents here, or select documents in another folder and move them over.',
      chuaPhanLoaiTrong: 'Every document is in a folder',
      chuaPhanLoaiTrongMoTa: 'New documents added without a folder end up here.',
      keCaThuMucCon: 'Include subfolders',
    },
    nguon: {
      moTa: 'Each source keeps its own figures and documents. The mobile app reads the default source.',
      chon: 'Source shown',
      tenMacDinh: '{{ten}} (default)',
      tenDaNgung: '{{ten}} — inactive',
      loiTai: 'Couldn’t load the sources: {{loi}}',
      rong: 'No data sources yet',
      rongMoTa: 'Create a source to enter figures, or for an external system to push figures in.',
      them: 'Add source',
      themDauTien: 'Add the first source',
      themTieuDe: 'Add data source',
      suaTieuDe: 'Edit data source',
      sua: 'Edit source',
      luu: 'Save changes',
      daThem: 'Added the source “{{ten}}”.',
      daLuu: 'Saved the source “{{ten}}”.',
      cotTen: 'Source',
      cotTaiLieu: 'Documents',
      cotCachNhap: 'Input',
      cotCapNhat: 'Updated',
      nhapTay: 'Manual',
      heThongNgoai: 'External system',
      hoatDong: 'Active',
      dungHoatDong: 'Inactive',
      macDinh: 'Default',
      ten: 'Display name',
      tenGoiY: 'Misa accounting software',
      tenKhongSinhDuocMa: 'The name needs at least one letter or digit.',
      maSeLa: 'The source code will be',
      maLa: 'Source code',
      maKhongDoiDuoc: 'The code comes from the name and goes into the app’s links and external systems’ settings. It can’t be changed after creation.',
      moTaTruong: 'Description',
      laNhapTay: 'Figures are entered by hand on these pages (clear this if an external system pushes them)',
      datMacDinh: 'Make default',
      batLaiTruoc: 'Reactivate the source first',
      macDinhTieuDe: 'Make “{{ten}}” the default source?',
      macDinhThan: 'The mobile app will show this source’s figures and documents to every user, instead of the current default source.',
      daDatMacDinh: 'Made “{{ten}}” the default source.',
      ngung: 'Deactivate',
      ngungTieuDe: 'Deactivate the source “{{ten}}”?',
      ngungThan: 'Its figures and documents stay viewable, but nobody can write to it, external systems included. You can reactivate it at any time.',
      daNgung: 'Deactivated the source “{{ten}}”.',
      batLai: 'Reactivate',
      daBatLai: 'Reactivated the source “{{ten}}”.',
    },
    tichHop: {
      tieuDe: 'External system integration',
      moTa: 'Accounting software, ERPs and other systems push figures in with an organization API key. Each call picks a source with the source parameter.',
      diaChiGoc: 'Base URL',
      xacThuc: 'Authentication',
      chonNguon: 'Source',
      chonNguonMo: 'Add ?source=<source code> to each call; leave it out for the default source.',
      dayPhanHe: 'Overwrite one module’s figures (overview, finance, projects, sales, people).',
      dayTaiLieu: 'Push a batch of 1–200 documents; pushing the same externalId again updates instead of duplicating.',
      doiSoat: 'Read back to reconcile, paginated with nextCursor.',
      goTaiLieu: 'Remove one document from the app.',
      phamVi: 'A key that syncs from accounting software usually needs these scopes:',
      moKhoaApi: 'Create an API key on the API keys page',
      nhoQuanTri: 'Ask your organization admin to create a key under System settings › API keys.',
    },
  },
  ekyc: {
    loiSoLieu: 'Could not load the overview figures: {{loi}}',
    loiTrienKhai: 'Could not read the deployment configuration: {{loi}}',
    loiDanhSach: 'Could not load the case list: {{loi}}',
    loiChiTiet: 'Could not load the case: {{loi}}',
    loiNhatKy: 'Could not load the audit log: {{loi}}',
    loiTaiThem: 'Could not load more: {{loi}}',
    tongQuanTieuDe: 'Identity overview',
    tongQuanMo: 'Figures for the last {{so}} days.',
    hoSoTieuDe: 'Identity cases',
    hoSoMo: 'Cases from every partner. Every time one is opened, it is written to the audit log.',
    chiTietTieuDe: 'Case {{ma}}',
    chiTietMo: 'Result of each check and the grounds for the decision.',

    dinhDanh: {
      ungDung: 'Application',
      quyTrinh: 'Identity workflow',
      quyTrinhMacDinh: '{{ten}} (default)',
      quyTrinhMacDinhHeThong: 'This application has no workflows; sessions run on the system defaults.',
      tieuDe: 'Verify a person',
      moTa: 'Run the whole identity flow on this screen.',
      loaiGiayTo: 'Document type',
      maHoSo: "Your organisation's case reference",
      maHoSoGoiY: 'For example: contract number, customer code',
      maHoSoMo: "Used to find this case later. Do not enter the customer's name or ID number.",
      luong: 'Identity flow that will run',
      banThucHien: 'You do',
      heThongTuChay: 'The system does',
      buocDongY: 'Consent to data processing',
      buocDongYMo: 'The customer reads and accepts the terms before any image is sent.',
      buocMatTruoc: 'Photograph the front',
      buocMatTruocMo: 'Without the front there is nothing to verify.',
      buocMatSau: 'Photograph the back',
      buocMatSauMo: 'A chip CCCD carries data on the back. Not needed for a passport.',
      buocChanDung: 'Take a selfie',
      buocChanDungMo: 'To compare against the photo on the document.',
      chuaChay: 'Not running in this release',
      chuaChayMo: 'Automated checks are not enabled yet, so a submitted case waits for a person to review it.',
      dongYTieuDe: 'Consent to processing of personal data',
      dongYMo: 'To verify your identity, we need to process images of your document and your face.',
      dongYY1: 'Document and face images are kept for as long as your identity record remains valid.',
      dongYY2: 'The data is used only for identity verification and fraud prevention, not for any other purpose, and is not shared outside.',
      dongYY3: 'You may ask to review or erase your data at any time — contact the place you are dealing with.',
      dongYNut: 'Agree and start',
      khongDongYNut: 'I do not agree',
      dangGhiNhan: 'Recording...',
      khachTuBam: 'Ask the customer to tap: the consent record must be their own choice.',
      tuChoiTieuDe: 'Recorded that the customer did not agree',
      tuChoiMo: 'No image was collected. The refusal is still kept as evidence.',
      anhTieuDe: "Customer's images",
      anhMo: 'JPEG or PNG, short edge at least 480 px, up to 10 MB.',
      dangTaiAnh: 'Uploading and checking the photo...',
      daNhanAnh: 'Received · {{rong}} × {{cao}} px',
      batBuoc: 'Required',
      nopNut: 'Submit case',
      dangNop: 'Submitting...',
      conThieu: '{{so}} required images missing.',
      daNop: 'Case submitted, awaiting review.',
      huyNut: 'Cancel session',
      huyTieuDe: 'Cancel this identity session?',
      huyThan: 'Uploaded images will not be used for any case. This cannot be undone.',
      huyXacNhan: 'Cancel identity session',
      daHuy: 'Identity session cancelled.',
      dinhDanhKhac: 'Verify another person',
      khongQuyenTao: 'Your account can view cases but cannot open a new identity session. Contact your organisation admin if you need to verify customers.',
    },

    tongHoSo: 'Total cases',
    tongHoSoSub: 'In the last {{so}} days',
    dangChoNguoi: 'Awaiting a person',
    dangChoNguoiSub: 'Open the review queue',
    tiLeTuDong: 'Automated rate',
    tiLeTuDongSub: 'Of cases that finished',
    thoiGianXuLy: 'Processing time',
    thoiGianXuLySub: 'Average per case',
    lyDoHayGap: 'Common reasons',
    lyDoHayGapRong: 'No case carries a reason code yet.',
    theoTrangThai: 'By status',
    theoTrangThaiRong: 'No cases yet.',

    toanVen: 'Audit log integrity',
    toanVenOk: 'Intact',
    toanVenOkMo: 'Checked {{so}} entries, the hash chain is unbroken.',
    toanVenGay: 'Hash chain broken',
    toanVenGayMo: 'Broken from entry {{so}}. Tell your system administrator to compare against a backup.',
    toanVenChua: 'Could not check',
    toanVenChuaMo: 'The check result could not be read. This is not a sign the log was tampered with — most likely the check endpoint has no permission declared at the gateway.',

    trienKhai: 'Identity service connections',
    trienKhaiNguoiDung: 'Users',
    trienKhaiDuLieu: 'Data',
    trienKhaiPhanQuyen: 'Admin authorization',
    trienKhaiBocTach: 'Document extraction',
    trienKhaiGateway: 'Enforced by API Gateway',
    trienKhaiTuKiem: 'Enforced by the service',
    trienKhaiCanhBao: 'No gateway in front — every account that can sign in holds full access to identity data.',
    cheDo: {
      NATIVE: 'Runs with Native',
      STANDALONE: 'Runs standalone',
      MIXED: 'Mixed configuration',
    },

    timHoSo: "Search by the partner's case reference",
    cotHoSo: 'Case',
    cotQuyetDinh: 'Decision',
    cotRuiRo: 'Risk',
    cotTaoLuc: 'Created',
    dangTaiThem: 'Loading...',
    bangHoSo: 'List of identity cases',
    taiThem: 'Load more',
    rong: 'No identity cases yet',
    rongMo: 'Cases appear here once a partner opens a session through the API, or once a member of staff verifies a customer at the counter.',
    rongLoc: 'No case matches',
    xemChiTiet: 'Open case',
    huyHoSo: 'Cancel case',
    xoaDuLieu: 'Delete personal data',
    xoaPhaiHuyTruoc: 'The case is still running; cancel it before deleting',
    huyHoiTieuDe: 'Cancel case {{ma}}?',
    huyHoiThan: 'The customer can no longer continue this case; verifying them again needs a new case. This cannot be undone.',
    daHuy: 'Case {{ma}} cancelled.',
    xoaHoiTieuDe: 'Delete the personal data of case {{ma}}?',
    xoaHoiThan: "Photos, data read from the document, the partner's case reference, the device and the IPs in the log are permanently deleted, and the case leaves the list. Only the consent record and timestamps are kept as proof of deletion (Decree 13/2023/ND-CP). This cannot be undone.",
    daXoa: 'Personal data of case {{ma}} deleted.',

    tab: {
      tatCa: 'All',
      choNguoi: 'Awaiting a person',
      dangChay: 'Running',
      dat: 'Passed',
      tuChoi: 'Rejected',
      roi: 'Dropped',
    },

    khongTimThay: 'Case not found',
    khongTimThayMo: 'The case may have been erased at the data subject’s request, or the case reference is wrong.',
    hangMuc: 'The five checks',
    canCu: 'Grounds for the decision',
    yeuToRuiRo: 'Risk factors',
    maLyDo: 'Reason codes',
    luatPhuQuyet: 'Hard rules triggered',
    luatPhuQuyetRong: 'No hard rule was triggered.',
    diemRuiRo: 'Risk score',
    chinhSach: 'Policy applied',
    thoiGian: 'Processing time',
    soLanChup: 'Selfie attempts',
    soLanChupCon: '{{da}} so far, {{con}} left',
    soLanChupHet: '{{da}} so far, none left',

    chuaCoQuyetDinh: 'No decision',
    chuaCoQuyetDinhMo: 'The session stopped before any document could be read, so the system reached no conclusion about this person.',

    loiMayChu: 'Case stopped by a server error',
    loiMayChuMo: 'No check finished. This says nothing about the person who submitted the case — once the incident is resolved, invite them to verify again.',

    chuaVaoSo: 'Case passed but was not added to the identity registry',
    chuaVaoSoMo: 'This person sits outside the duplicate check and can enrol again with the same document. Check the document extraction source before trusting the result.',

    duLieuGiayTo: 'Document data',
    moDuLieu: 'Open document data',
    moDuLieuMo: 'Opening personal data writes an audit entry with your name on it.',
    duLieuDaXoa: 'The personal data of this case has been erased under the retention policy.',
    chuaBocTach: 'No data extracted from the document: the reading step does not run in this release. Check the images directly.',
    anhHoSo: 'Submitted images',
    moAnh: 'Open {{ten}}',
    moAnhMo: 'Each time you open an image, an audit entry is written with your name on it.',
    anhLoi: 'The image could not be opened. Try again, or check whether your account may view images.',
    mayBocRa: 'Extracted by the machine',
    khachSua: 'Edited by the customer',
    doTinCay: 'Extraction confidence: {{so}}',
    doTinCayToanTaiLieu: 'Measured for the whole document, not per field.',

    truong: {
      idNumber: 'ID number',
      oldIdNumber: 'Previous ID number',
      fullName: 'Full name',
      dob: 'Date of birth',
      gender: 'Gender',
      issueDate: 'Issue date',
      expiryDate: 'Expiry date',
      issuePlace: 'Place of issue',
      residence: 'Permanent residence',
      nationality: 'Nationality',
      mrz: 'MRZ line',
    },

    gioiTinh: {
      NAM: 'Male',
      NU: 'Female',
      KHAC: 'Other',
    },

    nhatKy: 'Case audit log',
    nhatKyRong: 'No audit entries yet.',
    nhatKyCotLuc: 'Time',
    nhatKyCotHanhDong: 'Action',
    nhatKyCotNguoi: 'Performed by',
    nhatKyChuaNoi: 'Not yet chained',
    nhatKyChuaNoiMo: 'This entry has not been linked into the hash chain yet (the job runs every 5 minutes). The entry is readable; it just cannot yet prove it was not altered.',
    bangNhatKy: 'Audit log of actions on this identity case',

    khongCoTinHieu: 'No signal',
    khongCoTinHieuMo: 'This check did not run, so there is nothing to score — which is different from scoring zero.',
    chuaCoPhanHe: 'Not available yet',
    nguongLa: 'Threshold {{so}}',
    vuotNguong: '{{so}} above the threshold',
    duoiNguong: '{{so}} below the threshold',
    vanDe: 'Issues recorded',
    phienBanModel: 'Model {{ten}}',

    trangThai: {
      CREATED: 'Just created',
      CONSENT_REQUIRED: 'Awaiting consent',
      DOCUMENT_REQUIRED: 'Awaiting document photos',
      OCR_PROCESSING: 'Extracting',
      FRAUD_CHECKING: 'Checking for forgery',
      INFO_CONFIRMATION: 'Awaiting customer confirmation',
      SELFIE_REQUIRED: 'Awaiting selfie',
      LIVENESS_PROCESSING: 'Checking liveness',
      VERIFICATION_PROCESSING: 'Matching',
      VERIFIED: 'Passed',
      REJECTED: 'Rejected',
      MANUAL_REVIEW: 'Awaiting review',
      ADDITIONAL_REQUIRED: 'Awaiting more documents',
      EXPIRED: 'Expired',
      CANCELLED: 'Cancelled by customer',
      ERROR: 'System error',
      DELETED: 'Erased',
    },

    quyetDinh: {
      PASS: 'Pass',
      REVIEW: 'Review',
      REJECT: 'Reject',
    },

    ruiRo: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
    },

    ketLuan: {
      PASS: 'Passed',
      REVIEW: 'Review',
      FAIL: 'Failed',
      SKIPPED: 'Turned off',
      ERROR: 'Could not run',
    },

    mucVanDe: {
      LOW: 'Minor',
      MEDIUM: 'Moderate',
      HIGH: 'Serious',
    },

    tenHangMuc: {
      sanity: 'Image quality',
      logic: 'Data consistency',
      fraud: 'Forgery traces',
      liveness: 'Liveness',
      faceMatch: 'Face match',
    },

    hangMucMo: {
      sanity: 'Whether the document photo is clear enough to read.',
      logic: 'Whether the extracted data is valid and internally consistent.',
      fraud: 'Whether there are traces of editing or a screen recapture.',
      liveness: 'Whether a live person is in front of the camera.',
      faceMatch: 'Whether the face on the document and the face on camera are the same person.',
    },

    yeuTo: {
      fraud: 'Forgery traces',
      liveness: 'Liveness',
      pad: 'Dedicated presentation-attack detection',
      face: 'Face match',
      document: 'Document quality',
      ocr: 'Extraction confidence',
      device: 'Device',
      authority: 'Authority cross-check',
      behavior: 'Behaviour',
    },

    loaiGiayTo: {
      VIETNAM_CCCD: 'Citizen ID / chip CCCD',
      VIETNAM_CCCD_BARCODE: 'Barcode CCCD',
      VIETNAM_CMND: '9-digit CMND',
      VIETNAM_PASSPORT: 'Passport',
      VIETNAM_DRIVER_LICENSE: 'Driving licence',
      LAOS_ID: 'Lao ID card',
    },

    anh: {
      DOCUMENT_FRONT: 'the front image',
      DOCUMENT_BACK: 'the back image',
      SELFIE: 'the selfie',
    },

    // ───── Identity workflows (ADR-0085 §3) ─────
    // ───── Image capture on the counter screen (in-page camera + existing photo) ─────
    chupAnh: {
      moCamera: 'Open camera',
      chupLaiCamera: 'Retake with camera',
      chonCoSan: 'Choose existing photo',
      chonCoSanCua: 'Choose an existing photo for {{ten}}',
      chup: 'Capture',
      dong: 'Close camera',
      khungCamera: 'Camera frame to capture {{ten}}',
      anhVuaChup: '{{ten}} just captured',
      chuaNhan: 'This photo has not been accepted.',
      meoTieuDeGiayTo: 'Photographing the document',
      meoGiayTo1: 'Lay the document on a flat, dark, plain surface.',
      meoGiayTo2: 'Keep all four corners in frame; do not crop the edges or cover them with a finger.',
      meoGiayTo3: 'Avoid glare: do not shoot under a direct lamp or through a plastic sleeve.',
      meoGiayTo4: 'Photograph the real document — photos of a screen are detected.',
      meoTieuDeChanDung: 'Taking the portrait',
      meoChanDung1: 'Centre your face in the oval so it fills most of its height.',
      meoChanDung2: 'Look straight at the camera and keep your head level.',
      meoChanDung3: 'Remove masks, hats and sunglasses. Light should come from the front.',
      cameraYeu: 'This camera produces images smaller than {{min}} px — photos may be rejected. Consider choosing an existing photo from another camera.',
      quaNho: 'The {{rong}}×{{cao}} px image is too small (the short edge needs at least {{min}} px). Move closer, or choose the original, unresized photo.',
      loiKhongBaoMat: 'Browsers only allow the camera over a secure connection (HTTPS or localhost). This page is on HTTP so the camera cannot open — choose an existing photo, or open the page over HTTPS.',
      loiTuChoi: 'Camera access is blocked. Click the lock or camera icon in the address bar to allow it, then try again.',
      loiKhongCoThietBi: 'No camera was found on this device. Choose an existing photo instead.',
      loiDangBan: 'Another app is using the camera. Close that app and try again.',
      loiKhac: 'Could not open the camera. Choose an existing photo instead.',
    },
    mucNopLai: {
      DOCUMENT_FRONT: 'Front side',
      DOCUMENT_BACK: 'Back side',
      SELFIE: 'Portrait',
      SELFIE_VIDEO: 'Portrait video',
    },

    thamDinh: {
      raQuyetDinh: 'Make a decision',
      ketQuaGanNhat: 'Latest review result',
      lyDo: 'Reason',
      lyDoGoiY: 'Anyone who later disputes this decision will read this line. Required when rejecting.',
      lyDoMau: 'For example: the portrait does not match the photo on the document',
      canLyDo: 'Write the reason before rejecting.',
      canNopLai: 'Customer must resubmit',
      canNopLaiGoiY: 'Only used when requesting more information. Pick exactly what the customer has to retake.',
      canMuc: 'Pick at least one item the customer must resubmit.',
      duyet: 'Approve case',
      tuChoi: 'Reject case',
      yeuCauBoSung: 'Request more information',
      duyetHoi: 'Approve this identity case?',
      duyetThan: 'The case becomes Approved and the partner receives the result. This is final and cannot be undone.',
      tuChoiHoi: 'Reject this identity case?',
      tuChoiThan: 'The case becomes Rejected with your reason, and the partner receives the result. This is final and cannot be undone.',
      boSungHoi: 'Ask the customer to resubmit?',
      boSungThan: 'The customer is asked to resubmit: {{muc}}. The case reopens for new images and returns to the review queue once they submit.',
      daDuyet: 'Case approved.',
      daTuChoi: 'Case rejected.',
      daYeuCauBoSung: 'Resubmission requested.',
      khongGhiLyDo: 'The reviewer did not write a reason.',
      lyDoDaXoa: 'The reason was erased together with the case\'s personal data.',
    },
    buoc: {
      dong_y: 'Data processing consent',
      mat_truoc: 'Capture front side',
      mat_sau: 'Capture back side',
      chan_dung: 'Capture portrait',
      nfc: 'Read NFC chip',
      chat_luong_anh: 'Image quality check',
      boc_tach: 'Extract information',
      mrz_logic: 'QR/MRZ and logic check',
      khach_xac_nhan: 'Customer confirms details',
      gia_mao: 'Tamper detection',
      thuc_the_song: 'Liveness check',
      doi_chieu_mat: 'Face match',
      pad: 'PAD mask detection',
      kiem_trung: 'Duplicate identity check',
      doi_soat_co_quan: 'Issuing authority check',
      kiem_thiet_bi: 'Device check',
      cham_diem: 'Risk scoring',
      duyet_tay: 'Manual review',
    },

    nhomBuoc: {
      thuThap: 'Data collection',
      giayTo: 'Document processing',
      khuonMat: 'Face verification',
      doiSoat: 'Cross-checks',
      quyetDinh: 'Decision',
    },

    quyTrinh: {
      tieuDe: 'Identity workflows',
      moTaMan: 'Turn each eKYC step on or off and set the policy, save it as a named workflow, then apply it when opening a session.',
      chonUngDung: 'Choose application',
      danhSach: 'Workflow list',
      taoQuyTrinh: 'Create workflow',
      dangTao: 'Creating...',
      luuQuyTrinh: 'Save workflow',
      boThayDoi: 'Discard changes',
      quyTrinhMoi: 'New workflow (unsaved)',
      macDinh: 'Default',
      dangTat: 'Disabled',
      khongHopLe: 'No longer valid',
      phienBan: 'Version {{so}}',
      ten: 'Workflow name',
      ma: 'Code',
      maTuSinh: 'Generated from the name; cannot change after creation.',
      maKhongDoi: 'Cannot change after creation.',
      maChoTen: 'Type a name to generate the code',
      tenKhongSinhMa: 'The name needs at least one letter or digit to generate a code.',
      moTa: 'Description',
      cacBuoc: 'Steps',
      thuTuMayChu: 'Steps run in the order shown, enforced by the server. Unavailable steps start running by default once their subsystem ships.',
      luonBat: 'Always on',
      tuBat: 'On because another step needs it',
      canBuoc: 'Requires: {{ten}}',
      chuaKhaDung: 'Not available yet',
      chuaKhaDungSeChay: 'Not available yet · runs once the subsystem ships',
      thieuPhuThuoc: 'Missing a required step',
      nguoiDungTat: 'Turned off',
      chinhSach: 'Policy',
      luuY: 'Heads up',
      conLoi: 'There are errors. Fix the fields marked in red before saving.',
      biCu: 'Someone else just saved this workflow. Load the latest version and redo your edits — unsaved changes here will be lost.',
      taiBanMoi: 'Load latest version',
      daTao: 'Workflow created.',
      daLuu: 'Changes saved.',
      daBat: 'Workflow enabled.',
      daTat: 'Workflow disabled.',
      daDatMacDinh: '{{ten}} is now the default workflow.',
      daXoa: 'Workflow deleted.',
      datMacDinh: 'Set as default',
      tatQuyTrinh: 'Disable workflow',
      tatQuyTrinhMo: 'Stop opening new sessions with this workflow.',
      batQuyTrinh: 'Enable workflow',
      xoaQuyTrinh: 'Delete workflow',
      xoaHoi: 'Delete workflow {{ten}}?',
      xoaThan: 'Sessions that already ran on this workflow keep their own policy snapshot. This cannot be undone.',
      loiTai: 'Could not load workflow settings: {{loi}}',
      chuaCoUngDung: 'No applications yet',
      chuaCoUngDungMo: 'Workflows belong to an application. Create an application first, then come back here.',
      moUngDung: 'Open Applications & API keys',
      rong: 'This application has no workflows',
      rongMo: 'Sessions for this application run on the system defaults. Create a workflow to customise steps and policy.',
    },

    chinhSachQt: {
      chuaTacDung: 'Some settings only take effect once the matching step is available (liveness, extraction, authority checks). The server still saves them and snapshots them into each session.',
      nguongTieuDe: 'Decision thresholds',
      nguongMo: 'Applied to the risk score from 0 to 1: below the pass threshold is approved, at or above the reject threshold is rejected, in between waits for a reviewer.',
      nguongDat: 'Pass threshold',
      nguongDatGoiY: 'For example 0.30',
      nguongTuChoi: 'Reject threshold',
      nguongTuChoiGoiY: 'Higher than the pass threshold, for example 0.70',
      nguongMat: 'Face match threshold',
      nguongMatGoiY: 'From 0.50 to 0.95',
      trongSoTieuDe: 'Risk weights',
      trongSoMo: 'Nine factors that must add up to 1. A factor that could not be measured is not counted as 0.',
      tongTrongSo: 'Total: {{tong}}',
      livenessTieuDe: 'Liveness',
      cheDoLiveness: 'Mode',
      soDongTac: 'Actions per attempt',
      soLanLiveness: 'Maximum attempts',
      boDongTac: 'Action set',
      thuTuNgauNhien: 'Shuffle action order on each attempt',
      khiHetLuotLiveness: 'When liveness attempts run out',
      giayToTieuDe: 'Documents',
      nguonOcr: 'Extraction source',
      nguonOcrGoiY: 'Provider API keys live on the server, not here.',
      soLanChupLai: 'Maximum retakes',
      tuoiToiThieu: 'Minimum age',
      tuoiToiThieuGoiY: 'Leave empty for no limit.',
      truongTraVe: 'Document fields returned to the partner',
      khiThieuCoQuan: 'When the issuing authority cannot be checked',
      webhookTieuDe: 'Webhook',
      guiWebhook: 'Send a webhook when a case has a result',
      guiWebhookMo: "Sent to the session's callback URL, or the application's webhook URL if there is none. When off, no event is recorded.",
    },

    cheDoLiveness: {
      PASSIVE: 'Passive (single image)',
      ACTIVE: 'Active (perform actions)',
      HYBRID: 'Hybrid',
    },

    dongTac: {
      TURN_LEFT: 'Turn left',
      TURN_RIGHT: 'Turn right',
      LOOK_UP: 'Look up',
      LOOK_DOWN: 'Look down',
      MOVE_CLOSER: 'Move closer',
    },

    nguonOcr: {
      macDinh: 'Use the server-wide setting',
      service: 'Platform OCR service',
      gemini: 'Google Gemini',
      openai: 'OpenAI',
      local: 'Local model',
    },

    khiThieu: {
      MANUAL_REVIEW: 'Send to a reviewer',
      REJECT: 'Reject',
    },

    truongGiayTo: {
      idNumber: 'ID number',
      oldIdNumber: 'Old ID card number',
      fullName: 'Full name',
      dob: 'Date of birth',
      gender: 'Gender',
      issueDate: 'Issue date',
      expiryDate: 'Expiry date',
      issuePlace: 'Place of issue',
      residence: 'Permanent residence',
      nationality: 'Nationality',
      mrz: 'MRZ line',
    },

    // ───── Applications & API keys (ADR-0085 §1–2) ─────
    trangThaiGiao: {
      PENDING: 'Retrying',
      SENT: 'Delivered',
      FAILED: 'Out of retries',
    },

    ungDung: {
      tieuDe: 'Applications & API keys',
      moTaMan: "Each application is one partner's identity channel, with its own webhook, webhook signing secret and workflows.",
      bang: 'Application list',
      taoUngDung: 'Create application',
      dangTao: 'Creating...',
      luuUngDung: 'Save application',
      taoTieuDe: 'Create application',
      suaTieuDe: 'Edit application {{ten}}',
      ten: 'Application name',
      canTen: 'Enter an application name.',
      ma: 'Code',
      maKhongDoi: 'Cannot change: the code is part of the webhook signing secret.',
      trungMa: 'An application with code {{ma}} already exists. Change the name to generate a different code.',
      webhookUrl: 'Webhook URL',
      webhookUrlGoiY: "The partner's https URL that receives results. Leave empty if you don't use webhooks yet.",
      cotUngDung: 'Application',
      cotWebhook: 'Webhook',
      cotQuyTrinh: 'Default workflow',
      cotPhien30: 'Sessions, 30 days',
      dangDung: 'In use',
      daTatNhan: 'Disabled',
      chuaDatWebhook: 'No webhook set',
      biMatChuaCap: 'No signing secret issued',
      biMatBan: 'Signing secret · issue #{{so}}',
      soLuotLoi: 'Failed deliveries: {{so}}',
      soQuyTrinh: 'Workflows: {{so}}',
      phienCuoi: 'Latest {{luc}}',
      chuaCoPhien: 'No sessions yet',
      xemSoLieu: 'View stats and webhooks',
      xemSoLieuCua: 'View stats and webhooks of {{ten}}',
      moQuyTrinh: 'Open identity workflows',
      sua: 'Edit application',
      capBiMat: 'Issue webhook signing secret',
      xoayBiMat: 'Rotate webhook signing secret',
      bat: 'Enable application',
      tat: 'Disable application',
      tatUngDung: 'Disable application',
      daLuu: 'Changes saved.',
      daBat: '{{ten}} enabled.',
      daTat: '{{ten}} disabled.',
      xoayHoi: 'Rotate the webhook signing secret of {{ten}}?',
      xoayThan: 'The old secret stops working immediately, including for webhooks waiting to be retried. The partner must switch to the new secret before signatures verify again. This cannot be undone.',
      tatHoi: 'Disable application {{ten}}?',
      tatThan: 'The application stops accepting new sessions; partner calls get a 403 error. Sessions in progress continue and still send webhooks. You can re-enable it at any time.',
      chuaCoKhoaGoc: 'The server has no master key configured, so webhook signing secrets cannot be issued and webhooks are not sent. Ask your operator to set EKYC_KHOA_GOC_FILE.',
      chuaCoKhoaGocNgan: 'The server has no master key yet.',
      chuaCoKhoaGocKhiTao: "Application created. The server has no master key yet, so no webhook signing secret was issued; issue one later from the row's menu once your operator has configured it.",
      biMatTieuDe: 'Webhook signing secret of {{ten}}',
      biMatCanhBao: 'This secret is shown only once. The server neither stores nor returns it — after you close this dialog the only way forward is to rotate a new one.',
      biMatNhan: 'Webhook signing secret',
      biMatGoiY: 'Issue #{{so}}. The partner uses it to verify the X-Ekyc-Signature header.',
      saoChep: 'Copy',
      daChep: 'Copied',
      daChepBiMat: 'Secret copied to the clipboard.',
      khongChepDuoc: 'Could not copy automatically. Select the text in the box and copy it by hand.',
      daLuuBiMat: "I've saved the secret",
      rong: 'No applications yet',
      rongMo: 'Create an application per partner or identity channel to keep their webhooks and workflows apart.',
      loiTai: 'Could not load applications: {{loi}}',
      soLieuTieuDe: '30-day stats for {{ten}}',
      loiSoLieu: 'Could not load stats: {{loi}}',
      tongPhien: 'Total sessions',
      daDuyet: 'Approved',
      tuChoi: 'Rejected',
      choDuyet: 'Awaiting reviewer',
      roi: 'Dropped',
      dangLam: 'In progress',
      webhookDaGui: 'Webhooks sent',
      webhookChoVaLoi: '{{cho}} pending · {{loi}} failed',
      luotGiaoTieuDe: 'Recent webhook deliveries',
      bangLuotGiao: 'Webhook deliveries',
      loiLuotGiao: 'Could not load webhook deliveries: {{loi}}',
      chuaCoLuotGiao: 'No webhook deliveries yet',
      chuaCoLuotGiaoMo: 'Webhooks are sent when a case has a result and the workflow has "Send a webhook" on.',
      chuaDatWebhookMo: 'This application has no webhook URL. Edit the application to set one.',
      cotSuKien: 'Event',
      cotLanCuoi: 'Latest attempt',
      cotTaoLuc: 'Created',
      khongToiDich: 'Could not reach the endpoint',
      lanThu: 'Attempts: {{so}}',
      thuLaiLuc: 'Retrying at {{luc}}',
      guiLai: 'Resend',
      guiLaiMo: 'Try exactly one more delivery.',
      daXepGuiLai: 'Resend queued.',
      tichHopTieuDe: 'How partners integrate',
      tichHopKhoaApi: "Partners call the API with your organisation's API key in the X-Api-Key header, and choose the application with ungDungId when opening a session. Grant the key these permissions:",
      quyenMoPhien: 'open identity sessions',
      quyenGuiAnh: 'record consent, upload images, submit cases',
      quyenDocKetQua: 'read status and results',
      quyenDocPii: 'read full document numbers and images — grant only when truly needed',
      moKhoaApi: 'Open API keys',
      tichHopWebhook: 'Webhooks are signed with HMAC-SHA256 over the timestamp and the raw body. Verify them like this:',
      tichHopThuLai: 'Any non-2xx response (3xx included) counts as a failure. The server tries up to 9 times, backing off to 24 hours; use the X-Ekyc-Delivery header to drop duplicates.',
    },
  },
  thamDinh: {
    tongQuanTieuDe: 'Review overview',
    tongQuanMo: 'Review activity over the last 30 days.',
    hoSoTieuDe: 'Review cases',
    hoSoMo: 'Cases submitted for scoring against a criteria set.',
    pheDuyetTieuDe: 'Review approvals',
    pheDuyetMo: 'Cases waiting for your sign-off.',
    tieuChiTieuDe: 'Criteria library',
    tieuChiMo: 'Domains, case types and the criteria sets used for scoring.',
    tichHopTieuDe: 'API integration',
    tichHopMo: 'API keys that let other systems submit cases for review.',

    tabBo: 'Criteria sets',
    tabLinhVuc: 'Domains',
    tabLoaiHoSo: 'Case types',

    chuaMoTieuDe: 'This section is not open yet',
    xemCheoTieuDe: 'You are viewing a tenant interface',
    xemCheoMo: 'Platform staff accounts do not belong to any tenant, so this screen has no data. To see real data, sign in with an account from a tenant that has the AI Review service enabled.',
    chuaMoMo: 'The screen is built; the API for this area ships in a later increment. It will start working then with no further changes.',

    cay: {
      nhan: 'Criteria library tree',
      tim: 'Search domains, case types, criteria sets',
      nhap: 'Draft',
      rong: 'The library is empty',
      rongMo: 'Start with a domain, then add case types and criteria sets inside it.',
      rongLoc: 'Nothing matches',
      themLinhVuc: 'Add domain',
      conLoaiHoSo: 'Still has {{so}} case types inside; delete them first',
      conBo: 'Still has {{so}} criteria sets inside; delete or archive them first',
      daPhatHanh: 'Published sets cannot be deleted; archive it instead',
    },

    soan: {
      chuaChonBo: 'No criteria set selected',
      chuaChonBoMo: 'Pick a criteria set in the tree on the left to start editing.',
      dangApDung: 'Version {{so}} in use',
      chuaPhatHanh: 'Not published',
      suaGanNhat: 'Last edited: {{luc}}',
      phatHanh: 'Publish v{{so}}',
      canTrongSo: 'AI weight balancing',
      canKetQua: 'AI suggests changing {so} weights',
      canModel: 'Suggested by {model}',
      canApDung: 'Apply to draft',
      canKhongDoi: 'AI suggested no weight changes.',
      chayThu: 'Test run',
      chayThuDangCham: 'Test run in progress...',
      chayThuTieuDe: 'Test the criteria set on a sample case',
      chayThuMo: 'Scores against the draft you are editing, not the released version. Test runs do not appear in the case list.',
      chayThuChonTep: 'Choose a sample case',
      chayThuBatDau: 'Run test',
      chayThuXemKetQua: 'View test result',
      goiY: 'AI criteria suggestions',
      goiYTieuDe: 'AI suggests criteria from legal sources',
      goiYMoTa: 'Type of case to review',
      goiYMoTaMo: 'This description is sent to the web search tool. Do not enter details of a specific case.',
      goiYMoTaVd: 'SME loan application',
      goiYChonTep: 'Choose a sample case',
      goiYTepMo: 'Optional. The sample file only helps the AI see what a real case contains — it is not sent to the web search tool.',
      goiYBatDau: 'Start suggesting',
      goiYDangChay: 'Suggesting criteria...',
      goiYKetQua: 'AI suggested {so} criteria, built by {model}',
      goiYTuLieu: 'Sources searched',
      goiYThayCanh: 'Applying will REPLACE the whole criteria tree you are editing. The draft changes on screen only — you still need to save.',
      goiYApDung: 'Apply {so} criteria to draft',
      loaiTru: 'exclusionary',
      chuaPhatHanhDuoc: 'Not ready to publish',
      nguong: 'Conclusion thresholds',
      nguongDat: 'Pass threshold',
      nguongDatDieuKien: 'Conditional pass threshold',
      nguongMo: 'Below that is Fail. Failing a required criterion is also Fail, whatever the score.',
      thanhTyTrong: 'Group weights after normalisation (100% in total).',
      tenNhomMoi: 'New group name, press Enter to add',
      themNhom: 'Add group',
      tenTieuChiMoi: 'New criterion name, press Enter to add',
      themTieuChi: 'Add criterion',
      batBuoc: 'Required criterion',
      trongSoNhom: 'Weight of group {{ten}}',
      trongSoTieuChi: 'Weight of criterion {{ten}}',
      chuyenLen: 'Move {{ten}} up',
      chuyenXuong: 'Move {{ten}} down',
      xoaMuc: 'Delete {{ten}}',
      daLuu: 'All changes saved',
      chuaLuu: 'Unsaved changes',
      hoanTac: 'Undo',
    },

    chiTiet: {
      tieuDe: 'Criterion details',
      chuaChon: 'No criterion selected',
      chuaChonMo: 'Click a criterion in the middle panel to edit its scoring type, AI guidance and legal basis.',
      ten: 'Criterion name',
      kieu: 'Scoring type',
      kieuMo: 'Decides how the AI rules on it and how that converts to a score.',
      thangToiDa: 'Maximum points',
      mucNghiemTrong: 'Severity',
      loaiTru: 'Required criterion',
      loaiTruMo: 'Failing this one makes the whole case Fail, whatever the total score.',
      huongDan: 'Scoring guidance for the AI',
      huongDanMo: 'This text goes straight into the scoring prompt. Spell out what counts as a pass and what does not.',
      viDuDat: 'Passing example',
      viDuMo: 'Examples anchor the AI ruling; take them from real cases.',
      viDuKhongDat: 'Failing example',
      canCu: 'Legal basis',
      canCuMo: 'The AI may only cite these sources when looking things up.',
      canCuTen: 'Document number and title',
      canCuDieu: 'Article, clause',
      canCuUrl: 'Link',
      themCanCu: 'Add basis',
      xoaCanCu: 'Remove basis',
    },

    phatHanh: {
      tieuDe: 'Publish version {{so}}?',
      than: 'Version {{so}} is captured and can no longer be edited. Cases scored against it will always be traceable to exactly this version. The draft stays editable.',
      ghiChu: 'Version note',
      ghiChuMo: 'What changed since the previous version — whoever reads older scoring results will need this.',
      xacNhan: 'Publish v{{so}}',
    },

    kieu: {
      co_khong: 'Yes / no',
      thang_diem: 'Point scale',
      doi_chieu_gia_tri: 'Value check',
      thanh_phan_ho_so: 'Required documents',
      doi_chieu_cheo: 'Cross-check',
      tu_do: 'Free form',
    },

    bo: {
      caption: 'Criteria sets',
      tim: 'Search by set name',
      themMoi: 'Add criteria set',
      rong: 'No criteria sets yet',
      rongMo: 'A criteria set decides how a case is scored. Create the first one to start reviewing.',
      rongLoc: 'No criteria sets match the filter',
      cotTen: 'Set name',
      cotLoaiHoSo: 'Case type',
      cotTrangThai: 'Status',
      cotPhienBan: 'Version',
      cotCapDuyet: 'Approval levels',
      cotCapNhat: 'Updated at',
      chuaPhatHanh: 'Not published',
      banSo: 'Version {{so}}',
      khongCapDuyet: 'No approval',
      soCapDuyet: '{{so}} levels',
      sua: 'Edit criteria set',
      xoa: 'Delete criteria set',
      luuTru: 'Move to archive',
      dungLai: 'Reuse this set',
      phatHanh: 'Publish version',
      daTao: 'Created criteria set {{ten}}.',
      daLuu: 'Criteria set saved.',
      daLuuNhap: 'Draft saved.',
      daXoa: 'Deleted criteria set {{ten}}.',
      daPhatHanh: 'New version published.',
      formTaoTieuDe: 'Add criteria set',
      formSuaTieuDe: 'Edit criteria set',
      nhanTen: 'Set name',
      nhanMoTa: 'Description',
      nhanLoaiHoSo: 'Case type',
      moLoaiHoSo: 'A criteria set belongs to one case type and cannot be moved to another after creation.',
      chonLoaiHoSo: 'Select a case type',
      xoaHoiTieuDe: 'Delete this criteria set?',
      xoaHoiThan: 'Set "{{ten}}" will be removed from the library. This cannot be undone. Published sets cannot be deleted — move them to the archive instead.',
      xoaXacNhan: 'Delete criteria set',
    },

    linhVuc: {
      caption: 'Domains',
      tim: 'Search by name or code',
      themMoi: 'Add domain',
      rong: 'No domains yet',
      rongMo: 'A domain groups case types in the same field and declares which legal sources may be cited.',
      rongLoc: 'No domains match the filter',
      cotMa: 'Code',
      cotTen: 'Domain name',
      cotNguon: 'Preferred sources',
      cotSoLoai: 'Case types',
      khongNguon: 'No sources declared',
      themNguon: '{{so}} more',
      sua: 'Edit domain',
      xoa: 'Delete domain',
      daTao: 'Created domain {{ten}}.',
      daLuu: 'Domain saved.',
      daXoa: 'Deleted domain {{ten}}.',
      formTaoTieuDe: 'Add domain',
      formSuaTieuDe: 'Edit domain',
      nhanMa: 'Domain code',
      moMa: 'Lowercase letters, digits, hyphens and underscores. Cannot be changed after creation.',
      nhanTen: 'Domain name',
      nhanMoTa: 'Description',
      nhanNguon: 'Preferred sources',
      moNguon: 'Domains allowed as legal sources, one per line. For example: vbpl.vn. Leave empty to let the AI cite any source.',
      xoaHoiTieuDe: 'Delete this domain?',
      xoaHoiThan: 'Domain "{{ten}}" will be deleted. This cannot be undone. A domain that still has case types cannot be deleted.',
      xoaXacNhan: 'Delete domain',
    },

    loaiHoSo: {
      caption: 'Case types',
      tim: 'Search by case type name',
      themMoi: 'Add case type',
      rong: 'No case types yet',
      rongMo: 'A case type lists the documents a case must contain. You need a domain first.',
      rongLoc: 'No case types match the filter',
      rongChuaCoLinhVuc: 'Create a domain first',
      rongChuaCoLinhVucMo: 'Every case type belongs to a domain, so a domain has to exist first.',
      cotTen: 'Case type name',
      cotLinhVuc: 'Domain',
      cotThanhPhan: 'Default documents',
      cotSoBo: 'Criteria sets',
      khongThanhPhan: 'No documents declared',
      soGiayTo: '{{so}} documents',
      locTheoLinhVuc: 'Filter by domain',
      moiLinhVuc: 'All domains',
      sua: 'Edit case type',
      xoa: 'Delete case type',
      daTao: 'Created case type {{ten}}.',
      daLuu: 'Case type saved.',
      daXoa: 'Deleted case type {{ten}}.',
      formTaoTieuDe: 'Add case type',
      formSuaTieuDe: 'Edit case type',
      nhanLinhVuc: 'Domain',
      moLinhVuc: 'Cannot be moved to another domain after creation, because legal citations are looked up against the domain sources.',
      chonLinhVuc: 'Select a domain',
      nhanTen: 'Case type name',
      nhanMoTa: 'Description',
      nhanThanhPhan: 'Default documents',
      moThanhPhan: 'Names of the required documents, one per line.',
      xoaHoiTieuDe: 'Delete this case type?',
      xoaHoiThan: 'Case type "{{ten}}" will be deleted. This cannot be undone. A case type that still has criteria sets cannot be deleted.',
      xoaXacNhan: 'Delete case type',
    },

    phien: {
      caption: 'Review cases',
      tim: 'Search by case title',
      rong: 'No cases reviewed yet',
      rongMo: 'Cases submitted here are scored against a published criteria set.',
      rongLoc: 'No cases match the filter',
      cotTieuDe: 'Case',
      cotBo: 'Criteria set',
      cotTrangThai: 'Status',
      cotKetLuan: 'Conclusion',
      cotDiem: 'Score',
      cotNguoiTao: 'Submitted by',
      cotCapNhat: 'Updated at',
      locTrangThai: 'Filter by status',
      moiTrangThai: 'All statuses',
      dangXuLy: 'In progress',
      tabTatCa: 'All',
      xem: 'View details',
      xoa: 'Delete case',
      daNop: 'Submitted case {{ten}}.',
      daXoa: 'Deleted case {{ten}}.',
      xoaHoiTieuDe: 'Delete this case?',
      xoaHoiThan: 'Case "{{ten}}" along with all its files and scoring results will be deleted. This cannot be undone.',
      xoaXacNhan: 'Delete case',
      daXem: '{{daXem}}/{{canXem}} reviewed',
    },

    hangCho: {
      caption: 'Cases awaiting approval',
      rong: 'No cases are waiting for your approval',
      rongMo: 'Cases submitted for sign-off will appear here.',
      cotTieuDe: 'Case',
      cotBo: 'Criteria set',
      cotKetLuan: 'Conclusion',
      cotDiem: 'Score',
      cotCap: 'Approval level',
      cotNguoiTrinh: 'Submitted by',
      cotTrinhLuc: 'Submitted at',
      capSo: 'Level {{so}}',
    },

    raSoat: {
      dangTai: 'Loading case...',
      veDanhSach: 'Back to cases',
      ketQua: 'Scoring results',
      ketLuan: 'Conclusion',
      tep: 'Case files',
      soKyTu: '{{so}} characters',
      chuaCham: 'No scoring results yet',
      chuaChamMo: 'The case is waiting to be scored. Results appear here once it finishes.',
      chamLoi: 'The case could not be scored',
      chamLai: 'Re-score',
      daDayChamLai: 'Submitted for re-scoring.',
      daNopLai: 'Case resubmitted.',
      nopLai: 'Resubmit case',
      nopLaiMo: 'Upload the corrected files. The previous case stays in history for comparison.',
      soSanh: 'Compare with previous submission',
      soSanhLan: 'Submission {sau} compared with {truoc}',
      soSanhDiem: 'Score: {truoc} → {sau}',
      soSanhThayDoi: 'Criteria with changed verdicts ({so})',
      soSanhKhongDoi: 'No criteria changed verdict.',
      soSanhVanBan: 'Differences in the case files',
      soSanhGap: '{so} identical lines',
      soSanhBiCat: 'The diff is too long and was truncated — open both cases to compare in full.',
      lanNop: 'Submission {so}',
      taiBienBan: 'Download report',
      daTaiBienBan: 'Report downloaded.',
      danhGia: 'AI review note',
      danhGiaMo: 'The AI assistant summarises the scoring results into a note you can read quickly.',
      danhGiaBoi: 'Written by {model}, run {lan}',
      danhGiaChuaCo: 'No review note for this case yet.',
      danhGiaViet: 'Write review note',
      danhGiaVietLai: 'Rewrite',
      danhGiaDangViet: 'Writing the review note...',
      danhGiaLoi: 'The review note could not be written',
      danhGiaBoSot: 'The note omits {so} criteria that needed mentioning',
      danhGiaRong: 'Empty review note',
      danhGiaRongMo: 'The model replied with no content. Press Rewrite.',
      canXem: 'Needs review',
      daGhiDe: 'Overridden',
      bangChung: 'Quotes from the case',
      khongBangChung: 'The AI gave no quote for this criterion.',
      goiYSua: 'What to fix',
      apSua: 'Apply fix suggestions',
      apSuaMo: 'The system rebuilds the case from the suggestions you approved, then scores it as a new submission.',
      apSuaDangChay: 'Applying fix suggestions...',
      apSuaSoDongY: '{so} suggestions approved',
      apSuaChuaDongY: 'No suggestions approved yet. Mark them on each criterion first.',
      apSuaXong: 'New submission created',
      apSuaMoHoSoMoi: 'Open the new case',
      apSuaBoQua: '{so} operations could not be applied',
      apSuaLoi: 'The fix suggestions could not be applied',
      troLy: 'Ask the assistant about this case',
      troLyMo: 'The assistant reads the case and the scoring results. Your question history is private to you.',
      troLyOHoi: 'Ask about this case',
      troLyVd: 'Does the case state a warranty period?',
      troLyGui: 'Ask',
      troLyDung: 'Stop',
      troLyKhongMoDuoc: 'The assistant response stream could not be opened, please try again.',
      troLyTrichDan: 'Citations',
      troLyTrangSo: 'page {so}',
      troLyLichSu: 'Previous questions',
      troLyChuaHoi: 'You have not asked anything about this case yet.',
      troLyDangChay: 'This question was interrupted.',
      goiYSuaDongY: 'Approve fix',
      goiYSuaTuChoi: 'Reject',
      goiYSuaBoQuyet: 'Clear decision',
      ghiDe: 'Ruling',
      theoAi: 'As AI ruled: {{pq}}',
      ghiChu: 'Your note',
      danhDauDaXem: 'Mark as reviewed',
      trang: 'page {{so}}',
      conPhaiXem: '{{con}} of {{tong}} criteria still need review',
      chuaXemHet: '{{so}} criteria still need review before you can finish. Open each highlighted row to review it.',
      hoanTat: 'Finish review',
      daHoanTat: 'Case completed.',
      daTrinh: 'Case submitted for approval.',
      pheDuyet: 'Approve',
      pheDuyetTieuDe: 'Approve this case?',
      pheDuyetThan: 'The case moves to the next approval level, or completes if this is the last one.',
      daPheDuyet: 'Case approved.',
      traLai: 'Send back',
      traLaiTieuDe: 'Send this case back?',
      traLaiThan: 'The case returns to review so the reviewer can fix it. Every level approved in this round must approve again.',
      daTraLai: 'Case sent back.',
      yKien: 'Comment',
      yKienBatBuoc: 'Say what needs fixing — the reviewer reads this to know what to do next.',
      yKienTuyChon: 'Optional.',
    },

    phanQuyet: {
      dat: 'Pass',
      mot_phan: 'Partial',
      khong_dat: 'Fail',
      khong_ap_dung: 'Not applicable',
      khong_du_can_cu: 'Not enough evidence',
    },
    nop: {
      nutMoi: 'New review',
      tieuDe: 'New review',
      bo: 'Criteria set',
      boMo: 'Only published sets can be chosen — a case is pinned to a fixed version so it stays reproducible later.',
      chonBo: 'Select a criteria set',
      chuaCoBo: 'No criteria set has been published yet. Go to the Criteria library, build one and publish it first.',
      tieuDeHoSo: 'Case name',
      maThamChieu: 'Reference code',
      maThamChieuMo: 'Your own system\u2019s code, for reconciliation later. Optional.',
      tep: 'Case files',
      tepMo: 'Accepts {{duoi}}. PDFs must be digital originals with a text layer; scans need OCR first.',
      chonTep: 'Choose files',
      boTep: 'Remove {{ten}}',
      tepQuaLon: 'File "{{ten}}" is over 50 MB. Split or compress it and upload again.',
      xacNhan: 'Submit case',
      dangNop: 'Reading files...',
    },
    tichHopTaiLieu: {
      goiApi: 'Calling the API',
      goiApiMo: 'Every call sends the key in the X-Api-Key header. An external system can only read the cases it submitted itself.',
      webhook: 'Webhooks',
      webhookMo: 'The request body is JSON {suKien, phienId, maThamChieu, trangThai, diem, ketLuan, luc}. Verify the signature in the X-FoxAI-Signature header before trusting it. Failed deliveries are retried up to 5 times with increasing delays.',
      suKienChamXong: 'The AI finished scoring; the case awaits a reviewer.',
      suKienHoanTat: 'Case completed, after all approval levels.',
      suKienLoi: 'The case could not be scored.',
    },
    khoa: {
      caption: 'API keys',
      cotWebhookKetQua: 'Last webhook delivery',
      chuaGuiWebhook: 'Not sent yet',
      thuWebhook: 'Test webhook',
      themMoi: 'Create API key',
      rong: 'No API keys yet',
      rongMo: 'An API key lets another system submit cases for review without using this interface.',
      cotTen: 'Key name',
      cotTienTo: 'Prefix',
      cotTrangThai: 'Status',
      cotWebhook: 'Webhook',
      cotDungCuoi: 'Last used',
      chuaDung: 'Never used',
      khongWebhook: 'Not set',
      sao: 'Copy',
      daChep: 'Copied',
      taoTieuDe: 'Create API key',
      ten: 'Key name',
      tenMo: 'Name it after the system that uses it, e.g. "Public service portal" — one key per system so you can revoke them separately.',
      webhookUrl: 'Webhook URL',
      webhookUrlMo: 'Where review results are delivered. Leave empty and the external system polls the API instead.',
      taoXacNhan: 'Create key',
      daTaoTieuDe: 'API key created',
      chiHienMotLan: 'The key and webhook secret are shown this once. Only a hash is stored, so they cannot be retrieved — copy and store them now. If lost, revoke the key and create a new one.',
      khoaApi: 'API key',
      biMatWebhook: 'Webhook signing secret',
      biMatWebhookMo: 'Used to verify the signature in the X-FoxAI-Signature header.',
      daChepXong: 'I have copied them',
      thuHoiHoiTieuDe: 'Revoke this key?',
      thuHoiHoiThan: 'Key "{{ten}}" stops working immediately. Cases already submitted with it are kept and stay traceable. This cannot be undone.',
      thuHoiXacNhan: 'Revoke key',
      daThuHoi: 'Revoked key {{ten}}.',
      thuHoi: 'Revoke key',
    },

    soLieu: {
      dangCham: 'Scoring',
      choDuyet: 'Awaiting reviewer',
      choPheDuyet: 'Awaiting approval',
      loi: 'Failed',
      hoanTat30Ngay: 'Completed in 30 days',
      tyLeDat: 'Pass rate',
      tieuChiHayTruot: 'Most-failed criteria',
      tieuChiHayTruotMo: 'Over the last 90 days.',
      ganDay: 'Recent cases',
      soLanTruot: '{{so}} times',
      cotSoLan: 'Times failed',
      cotTyLeTruot: 'Failure rate',
      dangChamMo: 'AI is reading and scoring',
      choDuyetMo: 'Needs a reviewer',
      hoanTat: 'Completed',
      hoanTatMo: 'in 30 days',
      tyLeDatMo: '30 days · {{dieuKien}} conditional · {{khongDat}} failed',
      choDuyetKhoiMo: 'Longest-waiting cases first.',
      khongChoDuyet: 'No cases are waiting for review.',
      khongTieuChiTruot: 'No criterion has been marked as failed yet.',
      cotTieuChi: 'Criterion code',
      phienGanDay: 'Recent cases',
      xemTatCa: 'View all',
      chuaCoLinhVuc: 'No domains declared yet',
      chuaCoLinhVucMo: 'Reviewing needs at least one domain and one published criteria set.',
      chuaCoBoPhatHanh: 'No criteria set has been published',
      chuaCoBoPhatHanhMo: 'A criteria set must be published before it can be used for scoring.',
      sangThuVien: 'Open criteria library',
    },

    trangThaiBo: {
      hoat_dong: 'In use',
      luu_tru: 'Archived',
    },
    trangThaiPhien: {
      cho_xu_ly: 'Queued',
      dang_doc: 'Extracting',
      dang_cham: 'Scoring',
      cho_duyet: 'Awaiting reviewer',
      cho_phe_duyet: 'Awaiting approval',
      hoan_tat: 'Completed',
      loi: 'Failed',
    },
    ketLuan: {
      dat: 'Pass',
      dat_co_dieu_kien: 'Conditional pass',
      khong_dat: 'Fail',
      chua_du_du_lieu: 'Not enough data',
    },
    mucNghiemTrong: {
      thap: 'Low',
      trung_binh: 'Medium',
      cao: 'High',
      nghiem_trong: 'Critical',
    },
    trangThaiKhoa: {
      hoat_dong: 'Active',
      thu_hoi: 'Revoked',
    },
  },

  // Xử lý văn bản (ADR-0084): mỗi màn một nhánh
  vanBanTomTat: {
    tieuDe: 'Document summaries',
    moTa: 'Summarize document content automatically with AI. Click a document to compare the original with its summary.',
    them: 'Add document',
    daThem: 'Document added.',
    daLuuTomTat: 'Summary saved.',
    daXoa: 'Document deleted.',
    thatBai: 'Summarization failed. Click "AI summarize" to try again.',
    thongKe: {
      tong: 'Total documents',
      daTomTat: 'Summarized',
      chuaTomTat: 'Not summarized',
      tyLe: 'Avg. reduction',
    },
    timKiem: 'Search documents...',
    tatCaLoai: 'All document types',
    locLoai: 'Filter by document type',
    tatCaTrangThai: 'All statuses',
    locTrangThai: 'Filter by status',
    daTomTat: 'Summarized',
    chuaTomTat: 'Not summarized',
    loiDanhSach: 'Could not load documents: {{loi}}',
    bangCaption: 'Documents to summarize',
    rongLoc: 'No documents match',
    rongThat: 'No documents yet',
    rongThatMo: 'Upload a document or paste its content for AI to summarize.',
    cot: {
      ten: 'Document name',
      loai: 'Type',
      ngayTao: 'Created',
      nguoiTao: 'Created by',
    },
    nguon: {
      QLVB: 'Records & workflow',
      CONG_DVC: 'Public service portal',
      EMAIL: 'Official email',
      OTHER: 'Other source',
    },
    maDoiTac: 'Partner ID: {{ma}}',
    kyTuGoc: '{{so}} original characters',
    daRutGon: 'Reduced by {{so}}%',
    xemSoSanh: 'Compare original / summary',
    suaBanTomTat: 'Edit summary',
    xuatDoc: 'Export summary (.doc)',
    soanTuBanNay: 'Draft a document from this one',
    aiPhanCong: 'AI task assignment',
    xoa: 'Delete document',
    aiTomTat: 'AI summarize',
    dangTomTatNgan: 'Summarizing...',
    xacNhanXoa: 'Delete document "{{ten}}"?',
    xacNhanXoaMo: 'The original, its summary and the attached file are deleted permanently. This cannot be undone.',
    soSanhTieuDe: 'Compare original & summary',
    loiChiTiet: 'Could not load the document: {{loi}}',
    phanCongXong: 'Tasks assigned.',
    phanCongXongMo: 'AI created {{so}} tasks from the summary and assigned them to the matching departments and people.',
    rutGonSoVoiGoc: 'The summary is {{so}}% shorter than the original',
    vanBanGoc: 'Original document',
    banTomTatAi: 'AI summary',
    soKyTu: '{{so}} characters',
    dangChinhSua: 'Editing',
    dangTomTat: 'AI is summarizing this document...',
    chuaDuocTomTat: 'This document has not been summarized',
    aiTomTatNgay: 'AI summarize now',
    tomTatTrong: 'The summary cannot be empty.',
    luuTomTat: 'Save summary',
    suaTomTat: 'Edit summary',
    xuatTomTat: 'Export summary',
    themTieuDe: 'Add a new document',
    oTen: 'Document name',
    oTenViDu: 'E.g. Official letter on...',
    oLoai: 'Document type',
    oTep: 'Upload a file (PDF, DOCX, TXT, MD — text is extracted automatically)',
    oNoiDung: 'Or paste the document content (leave empty if you uploaded a PDF/DOCX)',
    oNoiDungViDu: 'Paste the original document here...',
    tuDongTomTat: 'Summarize with AI right after adding',
    loiThieuTen: 'Enter a document name.',
    loiThieuNoiDung: 'Upload a file or paste the document content.',
  },
  vanBanSoanThao: {
    tieuDe: 'Document drafting',
    moTa: 'Draft administrative documents with AI using the format of Decree 30/2020/ND-CP',
    thietLap: 'Drafting setup',
    oNguon: 'Source document (content source)',
    oNguonTuyChon: 'Source document (content source, optional)',
    tuTomTat: 'Pick from Document summaries',
    taiMoi: 'Upload a new document',
    chonVanBanGoc: '— Select a source document —',
    chuaCoVanBanGoc: 'No summarized documents yet',
    dangDocNoiDung: 'Reading file content...',
    goTep: 'Remove uploaded document',
    chuaTaiTep: 'No document uploaded yet',
    trichDoan: 'Excerpt:',
    oMau: 'Document template — Decree 30/2020/ND-CP',
    chonMau: '— Select a template —',
    mauKhongCo: 'This template is no longer in use or does not exist. Choose another template.',
    cheDoDien: 'Fill in the template',
    cheDoAi: 'AI drafts in Decree 30 format',
    theThuc: 'Format details',
    theThucTuyChon: '(optional — leave empty if no frame is needed)',
    theThucTuMau: 'from the template',
    tt: {
      cqch: 'Governing agency',
      cq: 'Issuing agency',
      so: 'Document number',
      kyhieu: 'Agency code',
      dd: 'Place',
      ky: 'Signer',
      cv: 'Position',
      noinhan: 'Recipients',
    },
    noiNhanViDu: 'One recipient per line, e.g.:\n- As above;\n- Archive: Clerical.',
    noiDungDien: 'Template fields to fill',
    aiDien: 'AI fill',
    nhanAiDienDuoc: '{{nhan}} (AI can fill)',
    nhanTuNhap: '{{nhan}} (manual)',
    chon: '— Select —',
    dangXuLy: 'Processing...',
    dangDocTepNgan: 'Reading file...',
    taoHoanChinh: 'Create the complete document',
    aiSoan: 'AI process — Draft document',
    vanBanSauSoan: 'Drafted document',
    xongSua: 'Finish editing',
    suaVanBan: 'Edit document',
    saoChep: 'Copy',
    xuatTep: 'Export file',
    rongHuongDan:
      'Pick a document template on the left. Fill-in templates preview as you type; AI-drafted templates need a source document before you click the process button.',
    loiDinhDang: 'Unsupported format. Only PDF, DOCX, TXT and MD are accepted.',
    loiQuaLon: 'The file is larger than 20MB. Choose a smaller file.',
    loiKhongDocDuoc: 'Could not read any text from the file. Scanned PDFs need a version with recognizable text.',
    dangDocTep: 'Still reading the file, please wait a moment.',
    canNguonDeAiDien: 'Pick a source document so AI can suggest field values.',
    daAiDien: 'AI suggested the field values; you can adjust them.',
    chuaChonMau: 'Pick a document template before processing.',
    canNguon: 'Pick a source document with enough content to draft from.',
    thieuTheThuc: 'Fill in all format details: {{ds}}',
    thieuTruong: 'Fill in the required fields: {{ds}}',
    conTruongChuaDien: 'The document still has unfilled fields: {{ds}}',
    daSaoChep: 'Document content copied.',
  },
  vanBanPhanCong: {
    tieuDe: 'Task assignment',
    moTa: 'AI assigns tasks to departments and individuals from summarized documents or uploaded files, based on the Organization roles list',
    nutThemTay: 'Add task manually',
    nutTaiTep: 'Upload file & assign with AI',
    nutTuTomTat: 'From Document summary',
    thongKe: {
      tong: 'Total tasks',
      phongBan: 'Assigned to departments',
      caNhan: 'Assigned to individuals',
      hoanThanh: 'Completed',
    },
    timTheo: 'Search by task, document, assignee...',
    tatCaDoiTuong: 'All assignees',
    locDoiTuong: 'Filter by assignee type',
    tatCaTrangThai: 'All statuses',
    locTrangThai: 'Filter by status',
    loiDanhSach: 'Could not load tasks: {{loi}}',
    bangCaption: 'Assigned tasks',
    rongLoc: 'No tasks match the filters',
    rongThat: 'No tasks yet',
    rongThatMo: 'Add a task manually, upload a file for AI to assign, or open a document in Document summary',
    cot: {
      nhiemVu: 'Assigned task',
      doiTuong: 'Assignee',
      vanBanNguon: 'Source document',
      dinhKem: 'Attachment',
      han: 'Due date',
    },
    xemTep: 'View {{ten}}',
    taiTep: 'Download file',
    taiTepTen: 'Download {{ten}}',
    sua: 'Edit task',
    xoa: 'Delete task',
    xacNhanXoa: 'Delete the task assigned to {{ten}}?',
    xacNhanXoaMo: 'The task and its own attachment are permanently deleted. This cannot be undone.',
    nutXoa: 'Delete task',
    daThem: 'Task added',
    daCapNhat: 'Task updated',
    daXoa: 'Task deleted',
    trangThai: {
      MOI_PHAN_CONG: 'Newly assigned',
      DANG_THUC_HIEN: 'In progress',
      HOAN_THANH: 'Completed',
    },
    uuTien: {
      AUTO: 'Automatic (AI decides)',
      PHONG_BAN: 'Prefer departments',
      CA_NHAN: 'Prefer individuals',
    },
    loi: {
      tepQuaLon: 'The file exceeds {{mb}} MB. Choose a smaller file.',
      thieuNoiDung: 'Enter the task content.',
      thieuDoiTuong: 'Choose who receives the task.',
      dinhDang: 'Unsupported format. Choose a PDF, DOCX, TXT or MD file.',
    },
    form: {
      tieuDeThem: 'Add task manually',
      tieuDeSua: 'Edit task',
      nutThem: 'Add task',
      noiDung: 'Task content',
      noiDungGoiY: 'Describe the task in detail...',
      vanBanLienQuan: 'Related document',
      vanBanLienQuanGoiY: 'Document title or number (optional)',
      dinhKem: 'Attachment',
      chonTep: 'Choose attachment',
      doiTep: 'Change attachment',
      boTep: 'Remove file {{ten}}',
      dangDinhKem: 'This file is attached. Choosing a new file replaces it.',
      loaiDoiTuong: 'Assignee type',
      hanXuLy: 'Due date',
      coQuanNhan: 'Receiving agency',
      chonCoQuan: 'Choose an agency...',
      phongBanNhan: 'Receiving department',
      chonPhongBan: 'Choose a department...',
      caNhanNhan: 'Receiving individual',
      chonCaNhan: 'Choose an individual...',
      chuaCoDonVi: 'The Organization roles list is empty',
    },
    tep: {
      tieuDe: 'Upload file & assign tasks with AI',
      tep: 'Document file',
      keoTha: 'Drag and drop or click to choose a document',
      dinhDang: 'PDF · DOCX · TXT · MD, up to 20 MB',
      dangTrich: 'Extracting content...',
      trichDoan: 'Content excerpt:',
      khongTrichDuoc: 'Selected "{{ten}}". The content is extracted when assigning.',
      tuyChon: 'Assignment options',
      uuTien: 'Prefer assigning to',
      hanMacDinh: 'Default due in',
      soNgay: '{{so}} days',
      ghiChu: 'Description / extra notes',
      ghiChuGoiY: 'E.g. urgent document, prefer the IT department...',
      nutChay: 'Assign from file with AI',
      dangPhanCong: 'Assigning...',
      dangTaiLen: 'Uploading file...',
      aiDangXuLy: 'AI is analyzing and assigning...',
      daTao: 'AI created {{so}} tasks from "{{ten}}".',
      khongTaoDuoc: 'AI could not create any task from this file.',
      thatBai: 'AI assignment failed. Try again in a few minutes.',
      hetGio: 'Assignment is taking too long. Reload the list in a few minutes.',
    },
  },
  vanBanNhiemVu: {
    breadcrumbNhom: 'Document processing',
    tieuDe: 'Task management',
    moTa: 'Assign tasks, break them into multi-level subtasks and track progress on administrative documents',
    giaoMoi: 'Assign new task',
    phanCongAi: 'Assign with AI',
    trangThai: {
      MOI_PHAN_CONG: 'Not started',
      DANG_THUC_HIEN: 'In progress',
      HOAN_THANH: 'Completed',
      QUA_HAN: 'Overdue',
      SAP_DEN_HAN: 'Due soon',
    },
    vaiTro: {
      CHU_TRI: 'Lead',
      PHOI_HOP: 'Support',
      XEM_DE_BIET: 'For information',
    },
    phamVi: {
      nhan: 'Scope',
      mine: 'My tasks',
      assigned: 'Assigned by me',
      all: 'Whole organization',
      moiVaiTro: 'All my roles',
    },
    khoang: {
      nhan: 'Assigned between',
      thangNay: 'This month',
      quyNay: 'This quarter',
      namNay: 'This year',
      tuNgay: 'From',
      denNgay: 'To',
    },
    kpi: {
      tong: 'Total tasks',
      tongMo: 'Including subtasks',
      tyLe: '{{phanTram}}% of total',
      sapDenHanMo: 'Due within {{so}} days',
      locMo: 'Click to filter the list, click again to clear',
      loi: 'Could not load the statistics.',
    },
    thongKe: {
      nut: 'Progress statistics',
      nutMo: 'Status breakdown and distribution by lead unit',
      tyLe: 'Status breakdown',
      tyLeAria: 'Task status breakdown bar',
      theoDonVi: 'By lead unit',
      theoDonViRong: 'No tasks in this scope yet',
    },
    ds: {
      tieuDe: 'Task list',
      soChinh: 'Main tasks: {{so}}',
      sapXepMo: 'Sorted by urgency: overdue and due soon first',
      tim: 'Search by content, document, assignee...',
      moiTrangThai: 'All statuses',
      moiDonVi: 'All lead units',
      canLam: 'To do',
      daXong: 'Completed',
      cotNhiemVu: 'Task',
      cotHan: 'Deadline',
      cotDonVi: 'Lead unit · assignees',
      cotTrangThai: 'Status',
      tienDoCon: '{{xong}}/{{tong}} subtasks',
      tienDoPhanTram: '{{phanTram}}%',
      chuaCoDonVi: 'No unit yet',
      themNguoi: '+{{so}}',
      moCon: 'Expand subtasks',
      thuCon: 'Collapse subtasks',
      xemChiTiet: 'View details: {{ten}}',
      giaoCon: 'Assign subtask',
      themNguoiThucHien: 'Add assignee',
      sua: 'Edit task',
      xoa: 'Delete task',
      doiTrangThai: 'Change status: {{trangThai}}',
      xongBiChan: 'Completed ({{so}} subtasks left)',
      aiPhanRa: 'AI breakdown',
      rongTieuDe: 'No tasks yet',
      rongMo: 'Click "Assign new task" to assign the first one.',
      rongCuaToiTieuDe: 'You have no tasks yet',
      rongCuaToiMo: 'Tasks you lead, support or follow for information will appear here.',
      rongLoc: 'No tasks match the filters',
      loi: 'Could not load the task list: {{loi}}',
    },
    han: {
      none: 'No deadline',
      overdue: '{{so}} days overdue',
      today: 'Due today',
      soon: '{{so}} days left',
      later: '{{so}} days left',
      done: 'Due {{ngay}}',
    },
    chiTiet: {
      tieuDe: 'Task details',
      thuoc: 'Part of task',
      moTa: 'Description / instructions',
      chuaMoTa: 'No description',
      donViChuTri: 'Lead unit',
      nguoiGiao: 'Assigned by',
      ngayGiao: 'Assigned on',
      xongLuc: 'Completed at',
      tepDinhKem: 'Attachment',
      taiTep: 'Download {{ten}}',
      vanBanLienQuan: 'Related document',
      nguoiThucHien: 'Assignees',
      chuaCoNguoi: 'No assignees yet',
      viecCon: 'Subtasks ({{xong}}/{{tong}})',
      chuaCoViecCon: 'Not broken down into subtasks yet',
      khongRo: 'Unknown',
      khongTai: 'Could not load the task: {{loi}}',
    },
    themNhanh: {
      tieuDe: 'Add assignee',
      vaiTro: 'Role',
    },
    form: {
      tieuDeTao: 'Assign new task',
      tieuDeCon: 'Assign subtask',
      tieuDeSua: 'Edit task',
      thuoc: 'Part of task: {{ten}}',
      chaTuCapNhat: 'The parent task status updates automatically from its subtasks.',
      noiDung: 'Task content',
      noiDungGoiY: 'E.g. Review and fix system security vulnerabilities',
      noiDungThieu: 'Enter the task content',
      moTa: 'Description / instructions',
      moTaGoiY: 'Specific requirements, expected results...',
      han: 'Deadline',
      hanGoiY: 'dd/mm/yyyy',
      donVi: 'Lead unit',
      donViGoiY: 'Choose a unit...',
      nguoi: 'Assignees',
      nguoiMo: 'At least one lead is required. Support and for-information assignees are optional.',
      nguoiThieu: 'Choose at least one lead',
      timNguoi: 'Search people by name...',
      khongThayNguoi: 'No users found',
      boNguoi: 'Remove {{ten}}',
      chuaChonNguoi: 'No assignees selected',
      tep: 'Attachment',
      chonTep: 'Choose file',
      tepHienTai: 'Current file: {{ten}}',
      tepMo: 'Up to {{so}}MB. A new file replaces the old one.',
      tepQuaLon: 'Attachments are limited to {{so}}MB',
      nutTao: 'Assign task',
      nutSua: 'Save changes',
    },
    xoa: {
      tieuDe: 'Delete task "{{ten}}"?',
      than: 'The task is deleted permanently. This cannot be undone.',
      thanKemCon: 'The task and its {{so}} subtasks are deleted permanently. This cannot be undone.',
      nut: 'Delete task',
    },
    toast: {
      daGiao: 'Task assigned.',
      daLuu: 'Task saved.',
      daThemNguoi: 'Assignee added.',
      daDoiTrangThai: 'Status updated.',
      daXoa: 'Task deleted.',
      daLuuTienDo: 'Progress updated.',
      daGuiYKien: 'Comment sent.',
      daTaoCon: 'Created {{so}} subtasks.',
    },
    hoatDong: {
      tieuDe: 'Progress & discussion',
      tabTienDo: 'Update progress',
      tabYKien: 'Comment',
      tienDoHienTai: 'Current progress',
      ghiChuTienDo: 'Work done, blockers (optional)',
      tienDoMo: 'Reporting progress on a task not yet started moves it to In progress.',
      yKienGoiY: 'Instructions, discussion, proposals...',
      dinhKem: 'Attach a result file',
      boTep: 'Remove file {{ten}}',
      nutTienDo: 'Save progress',
      nutYKien: 'Send comment',
      dangGui: 'Sending...',
      yKienThieu: 'Enter a comment or attach a file',
      rong: 'No activity yet',
      loi: 'Could not load the timeline',
      heThong: 'System',
      themNguoi: 'Added {{ten}} ({{vaiTro}})',
      boNguoi: 'Removed {{ten}} ({{vaiTro}})',
      doiVaiTro: '{{ten}} → {{vaiTro}}',
    },
    loaiHoatDong: {
      CREATED: 'assigned the task',
      CHILD_ADDED: 'assigned subtask "{{ten}}"',
      UPDATED: 'edited {{truong}}',
      PARTICIPANTS_CHANGED: 'changed the assignees',
      STATUS_CHANGED: 'changed status {{tu}} → {{sang}}',
      STATUS_CHANGED_AUTO: 'changed status {{tu}} → {{sang}} following its subtasks',
      PROGRESS: 'updated progress to {{phanTram}}%',
      COMMENT: 'commented',
    },
    truongDoi: {
      title: 'content',
      description: 'description',
      deadline: 'deadline',
      related_document: 'related document',
      lead_org: 'lead unit',
      attachment: 'attachment',
    },
    thongBao: {
      tieuDe: 'Task notifications',
      moChuong: 'Task notifications ({{so}} unread)',
      rong: 'No notifications yet',
      rongMo: 'Updates on tasks you take part in or assigned will appear here.',
      xemTatCa: 'View all',
      danhDauDaDoc: 'Mark as read',
      tieuDeTrang: 'Notifications',
      moTaTrang: 'New activity on tasks you take part in or assigned',
      loi: 'Could not load notifications',
      chuaDoc: 'Unread',
    },
    phanRa: {
      tieuDe: 'AI subtask breakdown',
      cha: 'Parent task: {{ten}}',
      dangGoiY: 'AI is reading the task, instructions and attachment to suggest subtasks...',
      dangGoiYMo: 'This usually takes 10–40 seconds.',
      daDocTep: 'AI read the task attachment.',
      khongCoTep: 'The task has no readable attachment — AI used only its content and instructions.',
      rong: 'AI considers this task specific enough and suggests no breakdown. You can still add subtasks manually.',
      loi: 'Could not get suggestions from AI: {{loi}}',
      goiYLai: 'Suggest again',
      chonViec: 'Create this task',
      noiDungGoiY: 'Subtask content',
      donVi: 'Lead unit',
      han: 'Deadline',
      aiChon: 'AI picked: {{ten}}',
      aiKhongKhop: 'AI picked "{{ten}}" — not in Levels and roles, choose manually',
      nguoiChuaKhop: 'No account matches "{{ten}}" — choose a person',
      dangKhop: 'Matching accounts...',
      boDong: 'Remove row {{so}}',
      themDong: 'Add subtask',
      hanVuot: 'Deadline is after the parent task deadline ({{ngay}})',
      noiDungThieu: 'Enter content for every selected task',
      chuaChon: 'Select at least one task to create',
      nut: 'Create {{so}} subtasks',
      aiDonVi: '{{ten}} · Levels and roles',
      aiDonViMo: 'Not in the organization structure — the task is still assigned to this unit. Choose one to link a unit in the structure.',
    },
  },
  // Trợ lý AI (ADR-0086)
  ...enChung,
  ...enHoi,
  ...enKetNoi,
  ...enKho,
  ...enKhuon,
  ...enBot,
  ...enVanHanh,
  // Nhật ký & Truy vết (ADR-0095)
  ...enNktvChung,
  ...enNktvSuDungAi,
  ...enGiaAi,
  ...enCanhBao,
  ...enBanToken,
  ...enManTokenAi,
  ...enBanGiao,
  ...enRpaChung,
  ...enRpaLoi,
  ...enRpaKetNoi,
  ...enRpaWorkflow,
  ...enRpaLichSu,
  ...enRpaTongQuan,
  ...enRpaLicense,
  ...enRpaDanhBa,
  ...enRpaThongBao,
  ...enRpaAgent,
};
